import React, { useEffect, useMemo, useState } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { authService } from '../../../authService';
import { supabase } from '../../../supabaseClient';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  useTheme,
  useMediaQuery,
  CssBaseline,
  Menu,
  MenuItem,
  Grid,
  Card,
  CardHeader,
  CardContent,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  FormControl,
  InputLabel,
  Select,
  Stack,
  Alert,
  Snackbar,
  InputAdornment,
  CircularProgress,
  Tabs,
  Tab,
  FormControlLabel,
  Checkbox,
  TablePagination,
  Collapse
} from '@mui/material';
import { SystemThemeProvider } from '../../../hooks/useSystemTheme'
import AccessibleSelect from '../../common/AccessibleSelect'
import MobileBottomBar from '../../common/MobileBottomBar';
import UnifiedSidebar from '../../common/UnifiedSidebar';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import {
  Menu as MenuIcon,
  Dashboard,
  Store,
  Badge,
  Payment,
  Assessment,
  Settings as SettingsIcon,
  ArrowBack,
  BusinessCenter,
  Logout,
  Person,
  Search,
  Add,
  Edit,
  Delete,
  Lightbulb,
  Warning,
  Phone,
  Email,
  Save,
  Info,
  LocationOn,
  Receipt,
  CreditCard,
  Tag,
  Visibility,
  KeyboardArrowDown,
  KeyboardArrowUp,
  DirectionsBike,
  Sailing,
  MedicalServices,
  Description,
  Close,
  Undo,
  Print,
  Download
} from '@mui/icons-material';
import { bploService } from '../../../services/bploService';
import OccupationSelector from './OccupationSelector';

const exportToExcel = async (title, columns, data) => {
  const safeTitle = String(title || 'export')
    .replace(/[\\/:*?"<>|]/g, '-')
    .replace(/\s+/g, ' ')
    .trim();

  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet('List');

  sheet.columns = columns.map((c, idx) => ({
    header: c.header,
    key: `c${idx}`,
    width: Math.max(12, Math.min(40, String(c.header || '').length + 8))
  }));

  sheet.getRow(1).font = { bold: true };

  (data || []).forEach((row) => {
    const values = columns.map((c) => {
      const v = c.accessor ? c.accessor(row) : (c.field ? row[c.field] : '');
      if (v === null || v === undefined) return '';
      return String(v);
    });
    sheet.addRow(values);
  });

  sheet.views = [{ state: 'frozen', ySplit: 1 }];
  sheet.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: 1, column: Math.max(1, columns.length) }
  };

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
  saveAs(blob, `${safeTitle}.xlsx`);
};
const drawerWidth = 280;

const BPLODashboard = ({ onLogout }) => {
  const [user, setUser] = useState(null);
  const [accessLoading, setAccessLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    let isActive = true;

    const checkAccess = async () => {
      const currentUser = authService.getUserSession();
      if (!currentUser) {
        navigate('/login');
        return;
      }

      if (isActive) {
        setUser(currentUser);
        setAccessLoading(true);
      }

      if (currentUser.role === 'superadmin') {
        if (isActive) {
          setHasAccess(true);
          setAccessLoading(false);
        }
        return;
      }

      try {
        if (!currentUser.department) {
          console.warn('User has no department assigned');
          if (isActive) {
            setHasAccess(false);
            setAccessLoading(false);
          }
          navigate('/portal');
          return;
        }

        // Explicitly block Accounting department as a safety measure
        if (currentUser.department.includes('Accounting')) {
          console.warn('Explicitly blocking Accounting department from BPLO');
          if (isActive) {
            setHasAccess(false);
            setAccessLoading(false);
          }
          navigate('/portal');
          return;
        }

        const { data: deptData, error: deptError } = await supabase
          .from('lgu_departments')
          .select('id')
          .eq('department_name', currentUser.department)
          .eq('is_active', true)
          .single();

        if (deptError || !deptData) {
          console.error('Department not found:', deptError);
          if (isActive) {
            setHasAccess(false);
            setAccessLoading(false);
          }
          navigate('/portal');
          return;
        }

        const { data: systemData, error: systemError } = await supabase
          .from('department_systems')
          .select('system_id')
          .eq('department_id', deptData.id)
          .eq('system_id', 'business-permit')
          .single();

        if (systemError || !systemData) {
          console.warn('Access denied to BPLO system');
          if (isActive) {
            setHasAccess(false);
            setAccessLoading(false);
          }
          navigate('/portal');
          return;
        }

        if (isActive) {
          setHasAccess(true);
          setAccessLoading(false);
        }
      } catch (error) {
        console.error('Error checking access:', error);
        if (isActive) {
          setHasAccess(false);
          setAccessLoading(false);
        }
        navigate('/portal');
      }
    };

    checkAccess();

  return () => {
      isActive = false;
    };
  }, [navigate]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    authService.logout();
    if (onLogout) onLogout();
    navigate('/login', { replace: true });
  };

  const menuItems = [
    { name: 'Dashboard', icon: <Dashboard />, path: '/portal/business-permit/dashboard' },
    { name: 'Applicants', icon: <Store />, path: '/portal/business-permit/applicants' },
    { name: 'Application', icon: <Badge />, path: '/portal/business-permit/applications' },
    { name: 'Reports', icon: <Assessment />, path: '/portal/business-permit/reports' },
    { name: 'Permit', icon: <Description />, path: '/portal/business-permit/permit' },
    { name: 'Charges', icon: <Payment />, path: '/portal/business-permit/charges' },
    { name: 'Settings', icon: <SettingsIcon />, path: '/portal/business-permit/settings' }
  ];

  const drawer = (
    <UnifiedSidebar
      user={user}
      onLogout={handleLogout}
      onCloseMobile={() => setMobileOpen(false)}
    />
  );

  if (accessLoading) {
    return (
      <Box sx={{ width: '100%', mt: 8, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress sx={{ color: '#059669' }} />
      </Box>
    );
  }

  if (!hasAccess) return null;

  if (!user) return null;

  return (
    <SystemThemeProvider systemId="business-permit">
      <Box sx={{ display: 'flex' }}>
        <CssBaseline />
        
        {/* Mobile Top AppBar */}
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            width: { md: `calc(100% - ${drawerWidth}px)` },
            ml: { md: `${drawerWidth}px` },
            display: { md: 'none' },
            bgcolor: '#ffffff',
            color: '#0f172a',
            borderBottom: '1px solid #e2e8f0'
          }}
        >
          <Toolbar sx={{ minHeight: 56 }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 1.5, color: '#0f172a' }}
            >
              <MenuIcon />
            </IconButton>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BusinessCenter sx={{ color: '#059669', fontSize: 22 }} />
              <Typography variant="subtitle1" fontWeight={800} color="#0f172a" noWrap>
                BPLO System
              </Typography>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Side Navigation Drawers */}
        <Box
          component="nav"
          sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
        >
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{ keepMounted: true }}
            sx={{
              display: { xs: 'block', md: 'none' },
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, borderRight: '1px solid #e2e8f0' },
            }}
          >
            {drawer}
          </Drawer>
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: 'none', md: 'block' },
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, borderRight: '1px solid #e2e8f0' },
            }}
            open
          >
            {drawer}
          </Drawer>
        </Box>

        {/* Main Content Area */}
        <Box
          component="main"
          className="bplo-forms"
          sx={{
            flexGrow: 1,
            p: { xs: 2, sm: 3 },
            pb: { xs: 10, md: 4 },
            width: { md: `calc(100% - ${drawerWidth}px)` },
            bgcolor: '#f8faf9',
            minHeight: '100vh',
            mt: { xs: 7, md: 0 }
          }}
        >
          <Routes>
            <Route index element={<Home />} />
            <Route path="dashboard" element={<Home />} />
            <Route path="applicants" element={<Businesses />} />
            <Route path="applications" element={<Permits />} />
            <Route path="permit" element={<Permit />} />
            <Route path="charges" element={<Payments />} />
            <Route path="reports" element={<Reports />} />
            <Route path="settings" element={<Settings />} />
          </Routes>
        </Box>

        {/* Sticky Mobile Bottom Navigation */}
        <MobileBottomBar
          items={menuItems}
          currentPath={location.pathname}
          onNavigate={(path) => navigate(path)}
          onToggleMenu={handleDrawerToggle}
          isMenuOpen={mobileOpen}
        />
      </Box>
    </SystemThemeProvider>
  );
};

const StatCard = ({ title, value, icon, color, bgcolor }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="body2" color="text.secondary">
            {title}
          </Typography>
          <Typography variant="h4" fontWeight="bold">
            {value}
          </Typography>
        </Box>
        <Avatar sx={{ bgcolor: bgcolor, color: color, width: 56, height: 56 }}>
          {icon}
        </Avatar>
      </Box>
    </CardContent>
  </Card>
);

const Home = () => {
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({
    applicants: 0,
    activeApplications: 0,
    collections: 0
  });
  const [recentApplications, setRecentApplications] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [applicantsRes, applicationsRes, paymentsRes] = await Promise.all([
        bploService.getApplicants(),
        bploService.getApplications(),
        bploService.getPayments()
      ]);

      const applicantCount = applicantsRes.success ? applicantsRes.data.length : 0;
      
      const apps = applicationsRes.success ? applicationsRes.data : [];
      // Filter for active applications (not completed/rejected)
      const activeAppsCount = apps.filter(a => 
        ['Pending', 'For Review', 'For Inspection', 'For Payment', 'In Progress'].includes(a.status)
      ).length;

      const payments = paymentsRes.success ? paymentsRes.data : [];
      const totalCollection = payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);

      setCounts({
        applicants: applicantCount,
        activeApplications: activeAppsCount,
        collections: totalCollection
      });

      setRecentApplications(apps.slice(0, 5));

    } catch (error) {
      console.error("Failed to load dashboard data", error);
    } finally {
      setLoading(false);
    }
  };

  const stats = [
    { label: 'Total Applicants', value: counts.applicants, icon: <Store />, color: '#b45309', bgcolor: '#fef3c7' },
    { label: 'Active Applications', value: counts.activeApplications, icon: <Badge />, color: '#16a34a', bgcolor: '#dcfce7' },
    { label: 'Total Collections', value: `₱${counts.collections.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, icon: <Payment />, color: '#2563eb', bgcolor: '#dbeafe' }
  ];

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>Dashboard Overview</Typography>
      
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {stats.map((s) => (
          <Grid item xs={12} sm={6} md={4} key={s.label}>
            <StatCard title={s.label} value={loading ? '...' : s.value} icon={s.icon} color={s.color} bgcolor={s.bgcolor} />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* Recent Applications */}
        <Grid item xs={12} md={8}>
          <Card sx={{ height: '100%', boxShadow: 2 }}>
            <CardHeader 
              title="Recent Applications" 
              action={
                <Button size="small" onClick={() => navigate('applications')}>View All</Button>
              }
            />
            <Divider />
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>App No.</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={4} align="center" sx={{ py: 3 }}><CircularProgress size={24} /></TableCell></TableRow>
                  ) : recentApplications.length === 0 ? (
                    <TableRow><TableCell colSpan={4} align="center" sx={{ py: 3 }}>No recent applications found</TableCell></TableRow>
                  ) : (
                    recentApplications.map((app) => (
                      <TableRow key={app.id} hover>
                        <TableCell sx={{ fontWeight: 'medium' }}>{app.application_no || 'N/A'}</TableCell>
                        <TableCell>{app.application_type}</TableCell>
                        <TableCell>
                           <Chip 
                             label={app.status || 'Unknown'} 
                             size="small" 
                             color={
                               ['Approved', 'Completed', 'Active'].includes(app.status) ? 'success' : 
                               ['Pending', 'For Review'].includes(app.status) ? 'warning' : 
                               ['Rejected', 'Cancelled'].includes(app.status) ? 'error' : 'default'
                             } 
                             variant="outlined"
                           />
                        </TableCell>
                        <TableCell>{new Date(app.created_at).toLocaleDateString()}</TableCell>
                      </TableRow>
                    ))
                  )}


                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Grid>

        {/* Quick Actions & Tips */}
        <Grid item xs={12} md={4}>
          <Stack spacing={3}>
             <Card sx={{ boxShadow: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Lightbulb color="primary" /> Quick Actions
                </Typography>
                <Stack spacing={2} sx={{ mt: 2 }}>
                  <Button variant="contained" fullWidth startIcon={<Add />} onClick={() => navigate('applications')}>
                    New Application
                  </Button>
                  <Button variant="outlined" fullWidth startIcon={<Person />} onClick={() => navigate('applicants')}>
                    Manage Applicants
                  </Button>
                  <Button variant="outlined" fullWidth startIcon={<Assessment />} onClick={() => navigate('reports')}>
                    View Reports
                  </Button>
                </Stack>
              </CardContent>
             </Card>

             <Card sx={{ bgcolor: 'primary.main', color: 'primary.contrastText' }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  System Status
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  All systems are running smoothly. Remember to check the "Settings" tab for configuring charges and requirements.
                </Typography>
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

const useLocalStorage = (key, initial) => {
  const [value, setValue] = useState(() => {
    try {
      const v = localStorage.getItem(key);
      return v ? JSON.parse(v) : initial;
    } catch {
      return initial;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {}
  }, [key, value]);
  return [value, setValue];
};

const parseName = (fullName) => {
  if (!fullName) return { firstName: '', middleName: '', lastName: '', suffix: '' };

  // Handle "Last, First Middle Suffix" format (comma separated)
  if (fullName.includes(',')) {
    const [lastNameStr, restStr] = fullName.split(',').map(s => s.trim());
    const parts = restStr.split(' ');
    const lastPart = parts[parts.length - 1];
    const isSuffix = ['Jr', 'Sr', 'II', 'III', 'IV', 'Jr.', 'Sr.'].includes((lastPart || '').replace('.', ''));
    const suffix = isSuffix ? lastPart : '';
    const effectiveParts = isSuffix ? parts.slice(0, -1) : parts;
    
    // For comma format "Last, First Middle", everything before the last word (if > 1 word) is First Name
    // But usually Middle Name is just one word/initial at the end.
    // However, the issue is about "two words in first name".
    // If effectiveParts has > 1 element, we need to decide where to split.
    // Heuristic: If there are multiple parts, assume the last part is Middle Name if it looks like one,
    // otherwise treat most as First Name.
    // Actually, the previous logic: firstName = effectiveParts[0], middleName = rest
    // This fails for "Mary Jane" (First) "D." (Middle). 
    // It makes firstName="Mary", middleName="Jane D."
    
    // Improved logic:
    // If 1 part: First Name only.
    // If >= 2 parts: Last part is Middle Name IF it is a single letter or looks like initial? 
    // OR we just assume Middle Name is the last part? 
    // The user says "The second word became the middle initial." implying "First Second Middle".
    // So "Mary Jane D." was parsed as F:Mary M:Jane D.
    // We want F:Mary Jane M:D.
    
    // Let's assume Middle Name is usually the LAST word of the remaining parts.
    if (effectiveParts.length === 0) return { firstName: '', middleName: '', lastName: lastNameStr, suffix };
    if (effectiveParts.length === 1) return { firstName: effectiveParts[0], middleName: '', lastName: lastNameStr, suffix };
    
    const middleName = effectiveParts[effectiveParts.length - 1];
    const firstName = effectiveParts.slice(0, -1).join(' ');
    
    return { firstName, middleName, lastName: lastNameStr, suffix };
  }

  // Handle "First Middle Last" format
  // This is much harder without a comma. "Mary Jane D. Doe"
  // Previous: last is lastName. Rest is First + Middle.
  // F: effectiveParts[0], M: rest. -> F:Mary M:Jane D.
  // We want F:Mary Jane M:D.
  const parts = fullName.trim().split(' ');
  if (parts.length === 1) return { firstName: parts[0], middleName: '', lastName: '', suffix: '' };
  if (parts.length === 2) return { firstName: parts[0], middleName: '', lastName: parts[1], suffix: '' };
  
  const last = parts[parts.length - 1];
  const isSuffix = ['Jr', 'Sr', 'II', 'III', 'IV', 'Jr.', 'Sr.'].includes(last.replace('.', ''));
  const suffix = isSuffix ? last : '';
  const effectiveParts = isSuffix ? parts.slice(0, -1) : parts;
  
  const lastName = effectiveParts.pop();
  
  // Remaining effectiveParts is [First... Middle]
  if (effectiveParts.length === 0) return { firstName: '', middleName: '', lastName, suffix };
  if (effectiveParts.length === 1) return { firstName: effectiveParts[0], middleName: '', lastName, suffix };

  // Assume last part of remaining is Middle Name
  const middleName = effectiveParts[effectiveParts.length - 1];
  const firstName = effectiveParts.slice(0, -1).join(' ');
  
  return { firstName, middleName, lastName, suffix };
};

const formatName = (fullName) => {
  const { firstName, middleName, lastName, suffix } = parseName(fullName);
  if (!lastName && !firstName) return fullName;
  return `${lastName}, ${firstName}${middleName ? ` ${middleName[0]}.` : ''}${suffix ? ` ${suffix}` : ''}`;
};

const Businesses = () => {
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', gender: '', barangay: '', birthdate: '', age: '', mobile: '', email: '', status: 'Active', date_registered: new Date().toISOString().slice(0, 10) });
  const [permits, setPermits] = useLocalStorage('bplo_permits', []);
  const [showAppTypeModal, setShowAppTypeModal] = useState(false);
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [selectedAppTypes, setSelectedAppTypes] = useState([]);
  const [filterBarangay, setFilterBarangay] = useState('All');
  const [filterGender, setFilterGender] = useState('All');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const barangays = ['Poblacion','San Pedro','Calabasahan','Sampong','Bakhawan','Masudsud','Masadya','Dalajican','San Vicente'];
  const [appForms, setAppForms] = useState({});
  const [modalGrossItem, setModalGrossItem] = useState({ label: '', amount: '' });
  const [editGrossItem, setEditGrossItem] = useState({ label: '', amount: '' });
  const [natureOptions, setNatureOptions] = useState([]);
  const [natureRecords, setNatureRecords] = useState([]);
  const [appTypeRecords, setAppTypeRecords] = useState([]);
  
  // Fishing Vessel States for Applicants Page Modal
  const [modalTabValue, setModalTabValue] = useState(0);
  const [modalFishingVessels, setModalFishingVessels] = useState([]);
  const [modalFishingGears, setModalFishingGears] = useState([]);
  const [modalShowVesselForm, setModalShowVesselForm] = useState(false);
  const [modalNewVessel, setModalNewVessel] = useState({
    registrationType: '',
    type: '',
    name: '',
    homeport: '',
    placeBuilt: '',
    yearBuilt: '',
    material: '',
    engines: []
  });
  const [modalNewEngine, setModalNewEngine] = useState({ name: '', horsepower: '', serial: '' });
  const [modalShowEngineForm, setModalShowEngineForm] = useState(false);
  const [modalNewGear, setModalNewGear] = useState({ name: '', category: '', quantity: '' });
  const [modalShowGearForm, setModalShowGearForm] = useState(false);
  const [fishingGearsRef, setFishingGearsRef] = useState([]);
  const [fishingGearCategories, setFishingGearCategories] = useState([]);
  
  // Motorcycle/Tricycle Vehicles State
  const [vehicles, setVehicles] = useState([]);
  const [newVehicle, setNewVehicle] = useState({ permitType: 'Renewal', vehicleType: 'Motorcycle', serviceType: 'Private', brandModel: '', plateNumber: '', stickerNumber: '', licenseNumber: '', licenseExpiry: '' });
  const [showVehicleForm, setShowVehicleForm] = useState(false);

  // Billing Charges State for MC/Fishing
  const [modalBillingCharges, setModalBillingCharges] = useState([]);
  const [modalChargesSearch, setModalChargesSearch] = useState('');
  const [modalAvailableCharges, setModalAvailableCharges] = useState([]);
  const [modalCustomCharge, setModalCustomCharge] = useState({ item: '', amount: '' });

  // Occupation Permit State
  const [modalOccupations, setModalOccupations] = useState([]);
  const [modalOccupationsError, setModalOccupationsError] = useState(null);

  const groupedGears = useMemo(() => {
    return fishingGearsRef.reduce((acc, gear) => {
      const cat = gear.category || 'Uncategorized';
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(gear);
      return acc;
    }, {});
  }, [fishingGearsRef]);

  const handleModalGearToggle = (gear, checked) => {
    if (checked) {
      setModalFishingGears([...modalFishingGears, { gearId: gear.id, name: gear.name, category: gear.category, quantity: 1 }]);
    } else {
      setModalFishingGears(modalFishingGears.filter(g => g.gearId !== gear.id));
    }
  };

  const handleModalGearQuantityChange = (gearId, qty) => {
    setModalFishingGears(modalFishingGears.map(g => 
      g.gearId === gearId ? { ...g, quantity: qty } : g
    ));
  };

  const [selectedApplicantView, setSelectedApplicantView] = useState(null);
  const [appList, setAppList] = useState([]);
  const [applicantErrors, setApplicantErrors] = useState({});
  const [applicantAlert, setApplicantAlert] = useState(null);
  const [appFormErrors, setAppFormErrors] = useState({});
  const [appSaveAlert, setAppSaveAlert] = useState(null);
  const [editAppErrors, setEditAppErrors] = useState({});
  const [editAppAlert, setEditAppAlert] = useState(null);
  const [appSaveLoading, setAppSaveLoading] = useState(false);
  const [modalVesselErrors, setModalVesselErrors] = useState({});
  const [modalVesselAlert, setModalVesselAlert] = useState(null);
  const [modalEngineErrors, setModalEngineErrors] = useState({});
  const [loadingApps, setLoadingApps] = useState(false);
  const [showEditAppModal, setShowEditAppModal] = useState(false);
  const [editingApp, setEditingApp] = useState(null);
  const [editAppForm, setEditAppForm] = useState({ status: 'Active', expiry_date: '' });
  const [showApplicantAppsModal, setShowApplicantAppsModal] = useState(false);

  // Alert & Confirm states
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const showConfirm = (title, message, onConfirm) => {
    setConfirmDialog({ open: true, title, message, onConfirm });
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [applicantsRes, applicationsRes, natureRes, appTypesRes, gearsRes, gearCatsRes] = await Promise.all([
          bploService.getApplicants(),
          bploService.getApplications(),
          bploService.getNatureOfBusiness(),
          bploService.getApplicationTypes(),
          bploService.getFishingGears(),
          bploService.getFishingGearCategories()
        ]);

        if (applicantsRes.success) setBusinesses(applicantsRes.data);
        if (gearsRes.success) setFishingGearsRef(gearsRes.data || []);
        if (gearCatsRes.success) setFishingGearCategories(gearCatsRes.data || []);

        if (applicationsRes.success) {
          const mapped = (applicationsRes.data || []).map(a => ({
            id: a.id,
            businessId: a.applicant_id,
            permitNo: a.application_no,
            permitType: a.application_type,
            issueDate: a.issue_date,
            expiryDate: a.expiry_date,
            status: a.status,
            typeOfBusiness: a.type_of_business,
            businessName: a.business_name,
            businessAddress: a.business_address,
            natureOfBusiness: a.nature_of_business,
            capital: a.capital,
            gross: a.gross,
            grossBreakdown: a.gross_breakdown,
            vehicleType: a.vehicle_type,
            serviceType: a.service_type,
            brandModel: a.brand_model,
            plateNumber: a.plate_number,
            stickerNumber: a.sticker_number,
            licenseNumber: a.license_number,
            licenseExpiry: a.license_expiry,
            fishingVessels: a.fishing_vessels,
            fishingGears: a.fishing_gears,
            billingCharges: a.billing_charges,
            vehicles: a.vehicles,
            occupations: a.occupations
          }));
          setPermits(mapped);
        }

        if (natureRes.success) {
          setNatureOptions((natureRes.data || []).map(d => d.name));
          setNatureRecords(natureRes.data || []);
        }
        if (appTypesRes.success) {
          setApplicationTypes((appTypesRes.data || []).map(d => d.name));
          setAppTypeRecords(appTypesRes.data || []);
        }
      } catch (error) {
        console.error("Failed to load data", error);
        showSnackbar("Failed to load data", "error");
      } finally {
        setLoading(false);
      }
    })();
  }, []);







  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return (businesses || []).filter(b => {
      const matchesSearch = !s || String(b.name||'').toLowerCase().includes(s) || String(b.barangay||'').toLowerCase().includes(s) || String(b.mobile||'').toLowerCase().includes(s);
      const matchesBarangay = filterBarangay === 'All' || b.barangay === filterBarangay;
      const matchesGender = filterGender === 'All' || b.gender === filterGender;
      return matchesSearch && matchesBarangay && matchesGender;
    });
  }, [businesses, search, filterBarangay, filterGender]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleGearToggleMain = (gearRef) => {
    setFishingGears(prev => {
      const exists = prev.find(g => g.name === gearRef.name);
      if (exists) {
        return prev.filter(g => g.name !== gearRef.name);
      } else {
        return [...prev, { name: gearRef.name, category: gearRef.category, quantity: 1 }];
      }
    });
  };

  const handleGearQuantityChangeMain = (gearName, qty) => {
    setFishingGears(prev => prev.map(g => g.name === gearName ? { ...g, quantity: qty } : g));
  };

  const reset = () => { setForm({ firstName: '', middleName: '', lastName: '', suffix: '', name: '', gender: 'Male', barangay: barangays[0], birthdate: '', age: '', mobile: '', email: '', status: 'Active', date_registered: new Date().toISOString().slice(0, 10) }); setEditing(null); };

  useEffect(() => {
    if (!form.birthdate) {
      setForm(prev => ({ ...prev, age: '' }));
      return;
    }
    const d = new Date(form.birthdate);
    const now = new Date();
    let age = now.getFullYear() - d.getFullYear();
    const m = now.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
    setForm(prev => ({ ...prev, age: isNaN(age) || age < 0 ? '' : String(age) }));
  }, [form.birthdate]);
  
  const save = async () => {
    const errs = {};
    if (!String(form.firstName || '').trim()) errs.firstName = 'First Name is required';
    if (!String(form.lastName || '').trim()) errs.lastName = 'Last Name is required';
    if (!String(form.gender || '').trim()) errs.gender = 'Gender is required';
    if (!String(form.barangay || '').trim()) errs.barangay = 'Barangay is required';
    const missing = Object.values(errs);
    if (missing.length > 0) {
      setApplicantErrors(errs);
      setApplicantAlert(`Please complete required fields: ${missing.join(', ')}`);
      return;
    }
    setApplicantErrors({});
    setApplicantAlert(null);
    const fullName = `${form.lastName}, ${form.firstName} ${form.middleName} ${form.suffix}`.trim().replace(/\s+/g, ' ');
    
    // Create payload excluding UI-only fields
    const { firstName, middleName, lastName, suffix, ...rest } = form;
    const payload = {
      ...rest,
      name: fullName,
      birthdate: form.birthdate || null,
      age: form.age || null
    };

    if (editing) {
      const { success, data } = await bploService.updateApplicant(editing, payload);
      if (success) {
        setBusinesses(businesses.map(b => b.id === editing ? data : b));
        showSnackbar('Applicant updated successfully', 'success');
      } else {
        showSnackbar('Failed to update applicant', 'error');
        return;
      }
    } else {
      const { success, data } = await bploService.createApplicant(payload);
      if (success) {
        setBusinesses([data, ...businesses]);
        showSnackbar('Applicant added successfully', 'success');
      } else {
        showSnackbar('Failed to add applicant', 'error');
        return;
      }
    }
    setShowModal(false);
    reset();
  };

  const removeApplicant = async (applicant) => {
    const { success: cntOk, count } = await bploService.getApplicationCountByApplicant(applicant.id);
    const appsCount = cntOk ? count : 0;
    const message = `You are about to permanently delete the applicant "${applicant.name}".\n` +
      `${appsCount} application(s) associated with this applicant will also be deleted.\n` +
      `This action cannot be undone.`;
    
    showConfirm(
      'Delete Applicant',
      message,
      async () => {
        setConfirmDialog({ ...confirmDialog, open: false });
        const delApps = await bploService.deleteApplicationsByApplicant(applicant.id);
        if (!delApps.success) {
          showSnackbar(delApps.error || 'Failed to delete applications for this applicant.', 'error');
          return;
        }
        const delApplicant = await bploService.deleteApplicant(applicant.id);
        if (!delApplicant.success) {
          showSnackbar(delApplicant.error || 'Failed to delete applicant.', 'error');
          return;
        }
        setBusinesses(businesses.filter(b => b.id !== applicant.id));
        setPermits(permits.filter(p => p.businessId !== applicant.id));
        showSnackbar(`Applicant "${applicant.name}" deleted successfully.`, 'success');
      }
    );
  };

  const [applicationTypes, setApplicationTypes] = useState([]);
  const [typeChargesMap, setTypeChargesMap] = useState({});

  const formSchemas = {
    'Business Permit': [
      { name: 'applicationNo', label: 'Application No.', type: 'text', readOnly: true },
      { name: 'typeOfBusiness', label: 'Type of Business', type: 'select', options: ['New','Renewal'], required: true },
      { name: 'businessName', label: 'Name of Business', type: 'text', required: true },
      { name: 'businessAddress', label: 'Business Address', type: 'text', required: true },
      { name: 'natureOfBusiness', label: 'Nature of Business', type: 'select', options: natureOptions, required: true },
      { name: 'capital', label: 'Capital', type: 'number', required: false },
      { name: 'gross', label: 'Gross', type: 'number', required: false }
    ],
    'Motorcycle and Tricycle Permit': [
      { name: 'applicationNo', label: 'Application No.', type: 'text', readOnly: true },
      // Vehicles are now handled in a separate list, so these individual fields are no longer required in the main form validation loop
      { name: 'vehicleType', label: 'Vehicle Type', type: 'select', options: ['Motorcycle', 'Tricycle'], required: false },
      { name: 'serviceType', label: 'Service Type', type: 'select', options: ['Private', 'For Hire'], required: false },
      { name: 'brandModel', label: 'Brand/Model', type: 'text' },
      { name: 'plateNumber', label: 'Plate Number', type: 'text' },
      { name: 'stickerNumber', label: 'Sticker No.', type: 'text' },
      { name: 'licenseNumber', label: 'Driver\'s License No.', type: 'text' },
      { name: 'licenseExpiry', label: 'License Expiration Date', type: 'date', shrink: true }
    ],
    'Motorboat Permit': [
      { name: 'applicationNo', label: 'Application No.', type: 'text', readOnly: true },
      { name: 'boatName', label: 'Boat Name', type: 'text' },
      { name: 'registrationNo', label: 'Registration No', type: 'text' }
    ],
    'Fishing Permit': [
      { name: 'applicationNo', label: 'Application No.', type: 'text', readOnly: true },
      { name: 'fishingArea', label: 'Fishing Area', type: 'text' },
      { name: 'gearType', label: 'Gear Type', type: 'text' },
      { name: 'licenseNo', label: 'License No', type: 'text' }
    ],
    'Sanitary Permit': [
      { name: 'applicationNo', label: 'Application No.', type: 'text', readOnly: true },
      { name: 'orNo', label: 'OR No.', type: 'text', required: false },
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'barangay', label: 'Barangay', type: 'select', options: barangays, required: true },
      { name: 'issueDate', label: 'Date of Issuance', type: 'date', readOnly: true },
      { name: 'expiryDate', label: 'Date of Expiration', type: 'date', readOnly: true },
      { name: 'establishmentName', label: 'Name Of Establishment', type: 'text', required: true }
    ]
  };

  // Common fields removed from UI; values are auto-generated on save

  const addModalGrossItem = (type) => {
    if (!modalGrossItem.label || !modalGrossItem.amount) return;
    const currentForm = appForms[type] || {};
    const currentBreakdown = currentForm.grossBreakdown || [];
    const newBreakdown = [...currentBreakdown, { ...modalGrossItem, id: Date.now() }];
    const total = newBreakdown.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    
    setAppForms({
      ...appForms,
      [type]: { ...currentForm, grossBreakdown: newBreakdown, gross: total }
    });
    setModalGrossItem({ label: '', amount: '' });
  };

  const removeModalGrossItem = (type, id) => {
    const currentForm = appForms[type] || {};
    const currentBreakdown = currentForm.grossBreakdown || [];
    const newBreakdown = currentBreakdown.filter(item => item.id !== id);
    const total = newBreakdown.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    
    setAppForms({
      ...appForms,
      [type]: { ...currentForm, grossBreakdown: newBreakdown, gross: total }
    });
  };

  const addEditGrossItem = () => {
    if (!editGrossItem.label || !editGrossItem.amount) return;
    const currentBreakdown = editAppForm.grossBreakdown || [];
    const newBreakdown = [...currentBreakdown, { ...editGrossItem, id: Date.now() }];
    const total = newBreakdown.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    
    setEditAppForm({ ...editAppForm, grossBreakdown: newBreakdown, gross: total });
    setEditGrossItem({ label: '', amount: '' });
  };

  const removeEditGrossItem = (id) => {
    const currentBreakdown = editAppForm.grossBreakdown || [];
    const newBreakdown = currentBreakdown.filter(item => item.id !== id);
    const total = newBreakdown.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    
    setEditAppForm({ ...editAppForm, grossBreakdown: newBreakdown, gross: total });
  };

  const handleGearToggle = (gearRef) => {
    setModalFishingGears(prev => {
      const exists = prev.find(g => g.name === gearRef.name);
      if (exists) {
        return prev.filter(g => g.name !== gearRef.name);
      } else {
        return [...prev, { name: gearRef.name, category: gearRef.category, quantity: 1 }];
      }
    });
  };

  const billingChargesSection = (
    <Box sx={{ mt: 2 }}>
      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
         <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
           <Typography variant="subtitle2">Billing Charges</Typography>
           <Chip label={`${(modalBillingCharges||[]).length} items`} size="small" />
         </Box>
         
         {/* Add Charge Input */}
         <Box sx={{ display: 'flex', gap: 1, mb: 2, position: 'relative' }}>
           <Box sx={{ flex: 1, position: 'relative' }}>
             <TextField
               size="small"
               fullWidth
               placeholder="Search charge item..."
               value={modalChargesSearch}
               onChange={(e) => setModalChargesSearch(e.target.value)}
             />
             {modalChargesSearch.trim().length > 0 && (
                <Paper elevation={4} sx={{ mt: 0.5, maxHeight: 200, overflow: 'auto', position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10 }}>
                  <List dense>
                    {(modalAvailableCharges||[]).filter(c => String(c.item||'').toLowerCase().includes(modalChargesSearch.trim().toLowerCase())).map((c, i) => (
                      <ListItem key={i} button onClick={() => {
                          setModalBillingCharges([...modalBillingCharges, { item: c.item, amount: parseFloat(c.amount) }]);
                          setModalChargesSearch('');
                      }}>
                        <ListItemText primary={c.item} secondary={`₱${parseFloat(c.amount).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`} />
                      </ListItem>
                    ))}
                    {(modalAvailableCharges||[]).filter(c => String(c.item||'').toLowerCase().includes(modalChargesSearch.trim().toLowerCase())).length === 0 && (
                      <ListItem><ListItemText primary="No matches found" /></ListItem>
                    )}
                  </List>
                </Paper>
             )}
           </Box>
         </Box>
         
         <Divider sx={{ my: 1 }} />
         
         {/* Custom Charge Form */}
         <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
           <TextField
             size="small"
             label="Item"
             fullWidth
             value={modalCustomCharge.item}
             onChange={(e) => setModalCustomCharge({...modalCustomCharge, item: e.target.value})}
           />
           <TextField
             size="small"
             label="Amount"
             type="number"
             sx={{ width: 150 }}
             value={modalCustomCharge.amount}
             onChange={(e) => setModalCustomCharge({...modalCustomCharge, amount: e.target.value})}
           />
           <Button 
             variant="contained" 
             size="small"
             startIcon={<Add />}
             onClick={() => {
                if(modalCustomCharge.item && modalCustomCharge.amount) {
                    setModalBillingCharges([...modalBillingCharges, { 
                        item: modalCustomCharge.item, 
                        amount: parseFloat(modalCustomCharge.amount) 
                    }]);
                    setModalCustomCharge({ item: '', amount: '' });
                }
             }}
           >
             Add
           </Button>
         </Box>

         {/* Charges List */}
         <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
            <Table size="small">
              <TableHead sx={{ bgcolor: 'white' }}>
                 <TableRow>
                    <TableCell>Item</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell width={40}></TableCell>
                 </TableRow>
              </TableHead>
              <TableBody>
                 {(modalBillingCharges||[]).length === 0 ? (
                   <TableRow><TableCell colSpan={3} align="center" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>No charges added</TableCell></TableRow>
                 ) : (modalBillingCharges||[]).map((c, idx) => (
                    <TableRow key={idx}>
                       <TableCell>{c.item}</TableCell>
                       <TableCell align="right">₱{parseFloat(c.amount).toLocaleString('en-PH', { minimumFractionDigits: 2 })}</TableCell>
                       <TableCell>
                         <IconButton size="small" color="error" onClick={() => setModalBillingCharges(modalBillingCharges.filter((_, i) => i !== idx))}>
                           <Delete fontSize="small" />
                         </IconButton>
                       </TableCell>
                    </TableRow>
                 ))}
                 <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell><strong>Total</strong></TableCell>
                    <TableCell align="right"><strong>₱{(modalBillingCharges||[]).reduce((s,c)=>s+parseFloat(c.amount||0),0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}</strong></TableCell>
                    <TableCell></TableCell>
                 </TableRow>
              </TableBody>
            </Table>
         </TableContainer>
      </Paper>
    </Box>
  );

  const updateMotorcycleCharges = (vehicleType, serviceType) => {
    if (!vehicleType || !serviceType) return;
    
    // Find matching charge
    const match = modalAvailableCharges.find(c => {
        const itemLower = c.item.toLowerCase();
        return itemLower.includes(vehicleType.toLowerCase()) && itemLower.includes(serviceType.toLowerCase());
    });

    if (match) {
        setModalBillingCharges(prev => {
            // Add the charge corresponding to the new vehicle
            return [...prev, { item: match.item, amount: parseFloat(match.amount) }];
        });
    }
  };

  const handleGearQuantityChange = (gearName, qty) => {
    setModalFishingGears(prev => prev.map(g => g.name === gearName ? { ...g, quantity: qty } : g));
  };

  const openAddApplication = (applicant) => {
    setSelectedApplicant(applicant);
    setSelectedAppTypes([]);
    setAppForms({});
    // Reset fishing vessel modal states
    setModalFishingVessels([]);
    setModalFishingGears([]);
    setModalBillingCharges([]);
    setModalTabValue(0);
    setModalShowVesselForm(false);
    // Reset vehicle modal states
    setVehicles([]);
    setShowVehicleForm(false);
    setShowAppTypeModal(true);
  };

  const viewApplications = async (applicant) => {
    setSelectedApplicantView(applicant);
    setLoadingApps(true);
    const { success, data } = await bploService.getApplications();
    const list = success ? (data || []).filter(a => a.applicant_id === applicant.id) : [];
    setAppList(list);
    setLoadingApps(false);
    setShowApplicantAppsModal(true);
  };

  const openEditApplication = (app) => {
    setEditingApp(app);
    const base = { status: app.status || 'Active', expiry_date: app.expiry_date || '' };
    let extras = {};
    if (app.application_type === 'Business Permit') {
      extras = {
        type_of_business: app.type_of_business || '',
        business_name: app.business_name || '',
        business_address: app.business_address || '',
        nature_of_business: app.nature_of_business || '',
        capital: app.capital || '',
        gross: app.gross || '',
        grossBreakdown: app.gross_breakdown || []
      };
    } else if (app.application_type === 'Motorcycle and Tricycle Permit') {
      extras = {
        vehicle_type: app.vehicle_type || '',
        service_type: app.service_type || '',
        brand_model: app.brand_model || '',
        plate_number: app.plate_number || '',
        sticker_number: app.sticker_number || '',
        license_number: app.license_number || '',
        license_expiry: app.license_expiry || ''
      };
      setModalBillingCharges(app.billingCharges || []);
    } else if (app.application_type === 'Fishing Vessel and Gear Registration') {
        setModalFishingVessels(app.fishing_vessels || []);
        setModalFishingGears(app.fishing_gears || []);
        setModalBillingCharges(app.billingCharges || []);
    } else if (app.application_type === 'Occupation/Calling') {
        setModalOccupations(app.occupations || app.occupations_json || []);
        setModalBillingCharges(app.billingCharges || []);
    }
    setEditAppForm({ ...base, ...extras });
    setShowEditAppModal(true);
  };

  const saveEditedApplication = async () => {
    if (!editingApp) return;
    setAppSaveLoading(true);
    try {
        const errs = {};
        const payload = {
          status: editAppForm.status || 'Active',
          expiry_date: editAppForm.expiry_date || null
        };
        if (editingApp.application_type === 'Business Permit') {
          if (!String(editAppForm.business_name || '').trim()) errs.business_name = 'Name of Business is required';
          if (!String(editAppForm.business_address || '').trim()) errs.business_address = 'Business Address is required';
          if (!String(editAppForm.nature_of_business || '').trim()) errs.nature_of_business = 'Nature of Business is required';
          payload.type_of_business = editAppForm.type_of_business || null;
          payload.business_name = editAppForm.business_name || null;
          payload.business_address = editAppForm.business_address || null;
          payload.nature_of_business = editAppForm.nature_of_business || null;
          payload.capital = editAppForm.type_of_business === 'New' && editAppForm.capital !== '' ? parseFloat(editAppForm.capital) : null;
          payload.gross = (editAppForm.grossBreakdown && editAppForm.grossBreakdown.length > 0)
            ? (editAppForm.gross !== '' ? parseFloat(editAppForm.gross) : editAppForm.grossBreakdown.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0))
            : (editAppForm.type_of_business === 'Renewal' && editAppForm.gross !== '' ? parseFloat(editAppForm.gross) : null);
          payload.gross_breakdown = editAppForm.grossBreakdown || [];
        } else if (editingApp.application_type === 'Motorcycle and Tricycle Permit') {
          if (!String(editAppForm.vehicle_type || '').trim()) errs.vehicle_type = 'Vehicle Type is required';
          if (!String(editAppForm.service_type || '').trim()) errs.service_type = 'Service Type is required';
          payload.vehicle_type = editAppForm.vehicle_type || null;
          payload.service_type = editAppForm.service_type || null;
          payload.brand_model = editAppForm.brand_model || null;
          payload.plate_number = editAppForm.plate_number || null;
          payload.sticker_number = editAppForm.sticker_number || null;
          payload.license_number = editAppForm.license_number || null;
          payload.license_expiry = editAppForm.license_expiry || null;
          payload.billing_charges = modalBillingCharges;
        } else if (editingApp.application_type === 'Fishing Vessel and Gear Registration') {
          payload.fishing_vessels = modalFishingVessels;
          payload.fishing_gears = modalFishingGears;
          payload.billing_charges = modalBillingCharges;
        } else if (editingApp.application_type === 'Occupation/Calling') {
          if (modalOccupations.length === 0) {
              setModalOccupationsError('Please select at least one occupation');
              setAppSaveLoading(false);
              return;
          } else {
              setModalOccupationsError(null);
          }
          payload.occupations_json = modalOccupations;
          payload.billing_charges = modalBillingCharges;
        }
        const missing = Object.values(errs);
        if (missing.length > 0) {
          setEditAppErrors(errs);
          setEditAppAlert(`Please complete required fields: ${missing.join(', ')}`);
          setAppSaveLoading(false);
          return;
        }
        setEditAppErrors({});
        setEditAppAlert(null);
        const res = await bploService.updateApplication(editingApp.id, payload);
        if (res.success) {
          await viewApplications(selectedApplicantView);
          setShowEditAppModal(false);
          setEditingApp(null);
          showSnackbar('Application updated successfully', 'success');
        } else {
            throw new Error(res.error || 'Failed to update application');
        }
    } catch (err) {
        console.error(err);
        setEditAppAlert(err.message || 'An error occurred while saving.');
    } finally {
        setAppSaveLoading(false);
    }
  };

  const deleteApplication = (id) => {
    const app = appList.find(a => a.id === id);
    const appNo = app ? app.application_no : '';

    showConfirm(
      'Delete Application',
      `Are you sure you want to delete application "${appNo}"?\nThis action cannot be undone.`,
      async () => {
        setConfirmDialog(prev => ({ ...prev, open: false }));
        const res = await bploService.deleteApplication(id);
        if (res.success) {
          setAppList(prev => prev.filter(a => a.id !== id));
          setPermits(prev => prev.filter(p => p.id !== id));
          showSnackbar('Application deleted successfully', 'success');
        } else {
          showSnackbar(res.error || 'Failed to delete application', 'error');
        }
      }
    );
  };

  const fetchDynamicCharges = async (type, natureName = null) => {
    const typeObj = appTypeRecords.find(t => t.name === type);
    if (!typeObj) return;

    let natureId = null;
    if (type === 'Business Permit' && natureName) {
      const natureObj = natureRecords.find(n => n.name === natureName);
      if (natureObj) natureId = natureObj.id;
    }
    if (type === 'Motorcycle and Tricycle Permit' || type === 'Fishing Vessel and Gear Registration') {
        // For these types, we want ALL charges available for search, regardless of type/nature
        const { success, data } = await bploService.getCharges();
        const items = success ? (data || []) : [];
        setTypeChargesMap(prev => ({ ...prev, [type]: items }));
        setModalAvailableCharges(items);
    } else {
        // For Business Permit, stick to strict filtering
        if (!natureId) {
          // No nature selected yet: show nothing to avoid misleading defaults
          setTypeChargesMap(prev => ({ ...prev, [type]: [] }));
        } else {
          const { success, data } = await bploService.getDefaultChargesByNatureId(natureId);
          const items = success ? ((data || []).map(c => ({ id: c.id, item: c.item, amount: c.amount }))) : [];
          setTypeChargesMap(prev => ({ ...prev, [type]: items }));
        }
    }
  };

  const toggleType = async (type) => {
    setSelectedAppTypes(prev => {
      const exists = prev.includes(type);
      const next = exists ? prev.filter(t => t !== type) : [...prev, type];
      return next;
    });
    setAppForms(prev => {
      const exists = selectedAppTypes.includes(type);
      if (exists) {
        const { [type]: _, ...rest } = prev;
        return rest;
      }
      const base = {};
      const schema = formSchemas[type] || [];
      const extras = schema.reduce((acc, f) => {
        let val = '';
        // Removed default selection of first option to show placeholder
        return { ...acc, [f.name]: val };
      }, {});
      const initial = { ...base, ...extras };
      if (type === 'Business Permit') {
        initial.typeOfBusiness = '';
        initial.businessAddress = selectedApplicant?.barangay || '';
      } else if (type === 'Sanitary Permit') {
        const now = new Date();
        const issue = now.toISOString().slice(0, 10);
        const nextYear = new Date(now);
        nextYear.setFullYear(now.getFullYear() + 1);
        const expiry = nextYear.toISOString().slice(0, 10);
        initial.issueDate = issue;
        initial.expiryDate = expiry;
        initial.barangay = selectedApplicant?.barangay || initial.barangay;
        initial.name = selectedApplicant?.name || '';
      }
      
      // Auto-generate application number for ALL types
      (async () => {
        const { success, data } = await bploService.generateNextApplicationNo(type);
        if (success && data) {
          setAppForms(curr => ({
            ...curr,
            [type]: { ...curr[type], applicationNo: data }
          }));
        }
      })();

      return { ...prev, [type]: initial };
    });
    const exists = selectedAppTypes.includes(type);
    if (exists) {
      setTypeChargesMap(prev => { const { [type]: _, ...rest } = prev; return rest; });
    } else {
      let initialNature = null;
      // Removed default nature selection
      // Ensure charges are fetched for MC/Fishing types too
      await fetchDynamicCharges(type, initialNature);
    }
  };

  const saveApplication = async () => {
    if (!selectedApplicant || selectedAppTypes.length === 0) return;
    setAppSaveLoading(true);
    try {
        const newRecords = [];
        const collectedErrors = {};
        const missingAll = [];
        for (const type of selectedAppTypes) {
          const formData = appForms[type] || {};
          const schema = formSchemas[type] || [];
          for (const f of schema) {
            if (type === 'Business Permit' && f.name === 'gross' && formData.typeOfBusiness !== 'Renewal') continue;
            if (f.required && !String(formData[f.name] || '').trim()) {
              if (!collectedErrors[type]) collectedErrors[type] = {};
              collectedErrors[type][f.name] = `${f.label} is required`;
              missingAll.push(`${type}: ${f.label}`);
            }
          }
          if (type === 'Motorcycle and Tricycle Permit') {
             if (vehicles.length === 0) {
               setAppSaveAlert('Please add at least one vehicle.');
               setAppSaveLoading(false);
               return;
             }
          }
          if (type === 'Occupation/Calling') {
              if (modalOccupations.length === 0) {
                  setModalOccupationsError('Please select at least one occupation');
                  missingAll.push(`${type}: Occupations`);
              } else {
                  setModalOccupationsError(null);
              }
          }

          if (collectedErrors[type]) continue;
          
          if (missingAll.length > 0) {
              setAppFormErrors(collectedErrors);
              setAppSaveAlert(`Please complete required fields: ${missingAll.join(', ')}`);
              setAppSaveLoading(false);
              return;
          }

          const generatedNo = formData.applicationNo || `APP-${Date.now()}`;
          
          let extraData = {};
          if (type === 'Fishing Vessel and Gear Registration') {
            extraData = {
              fishingVessels: modalFishingVessels,
              fishingGears: modalFishingGears,
              billingCharges: modalBillingCharges
            };
          } else if (type === 'Motorcycle and Tricycle Permit') {
            extraData = {
                billingCharges: modalBillingCharges,
                vehicles: vehicles
            };
          } else if (type === 'Occupation/Calling') {
              extraData = {
                  occupations_json: modalOccupations,
                  billingCharges: modalBillingCharges
              };
          }
    
          const supabaseRecord = {
            applicant_id: selectedApplicant.id,
            application_no: generatedNo,
            application_type: type,
            issue_date: new Date().toISOString().slice(0,10),
            expiry_date: null,
            status: type === 'Sanitary Permit' ? 'Paid' : 'Active',
            type_of_business: type === 'Business Permit' ? (formData.typeOfBusiness || null) : null,
            business_name: type === 'Business Permit' ? (formData.businessName || null) : null,
            business_address: type === 'Business Permit' ? (formData.businessAddress || null) : null,
            nature_of_business: type === 'Business Permit' ? (formData.natureOfBusiness || null) : null,
            capital: type === 'Business Permit' ? (formData.typeOfBusiness === 'New' ? (formData.capital ? parseFloat(formData.capital) : null) : null) : null,
            gross: type === 'Business Permit' 
              ? (
                  (formData.grossBreakdown && formData.grossBreakdown.length > 0)
                    ? (formData.gross ? parseFloat(formData.gross) : formData.grossBreakdown.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0))
                    : (formData.typeOfBusiness === 'Renewal' ? (formData.gross ? parseFloat(formData.gross) : null) : null)
                )
              : null,
            gross_breakdown: type === 'Business Permit' ? (formData.grossBreakdown || []) : null,
            
            // Sanitary Permit fields
            or_no: type === 'Sanitary Permit' ? (formData.orNo || null) : null,
            name: type === 'Sanitary Permit' ? (formData.name || null) : null,
            barangay: type === 'Sanitary Permit' ? (formData.barangay || null) : null,
            establishment_name: type === 'Sanitary Permit' ? (formData.establishmentName || null) : null,
    
            // Motorcycle/Tricycle fields
            vehicles: type === 'Motorcycle and Tricycle Permit' ? vehicles : null,
            vehicle_type: type === 'Motorcycle and Tricycle Permit' ? (vehicles[0]?.vehicleType || null) : null,
            service_type: type === 'Motorcycle and Tricycle Permit' ? (vehicles[0]?.serviceType || null) : null,
            brand_model: type === 'Motorcycle and Tricycle Permit' ? (vehicles[0]?.brandModel || null) : null,
            no_of_units: type === 'Motorcycle and Tricycle Permit' ? vehicles.length : null,
            plate_number: type === 'Motorcycle and Tricycle Permit' ? (vehicles[0]?.plateNumber || null) : null,
            sticker_number: type === 'Motorcycle and Tricycle Permit' ? (vehicles[0]?.stickerNumber || null) : null,
            license_number: type === 'Motorcycle and Tricycle Permit' ? (vehicles[0]?.licenseNumber || null) : null,
            license_expiry: type === 'Motorcycle and Tricycle Permit' ? (vehicles[0]?.licenseExpiry || null) : null,
            // Fishing Vessel fields
            fishing_vessels: type === 'Fishing Vessel and Gear Registration' ? modalFishingVessels : null,
            fishing_gears: type === 'Fishing Vessel and Gear Registration' ? modalFishingGears : null,
            billing_charges: (type === 'Motorcycle and Tricycle Permit' || type === 'Fishing Vessel and Gear Registration' || type === 'Occupation/Calling') ? modalBillingCharges : null,
            
            // Occupation fields
            occupations_json: type === 'Occupation/Calling' ? modalOccupations : null
          };
          const created = await bploService.createApplication(supabaseRecord);
          if (created.success) {
            const rec = {
              id: created.data.id,
              businessId: selectedApplicant.id,
              permitNo: generatedNo,
              permitType: type,
              issueDate: new Date().toISOString().slice(0,10),
              expiryDate: '',
              status: type === 'Sanitary Permit' ? 'Paid' : 'Active',
              ...extraData
            };
            newRecords.push(rec);
          } else {
             throw new Error(created.error || 'Failed to create application');
          }
        }
        
        setAppFormErrors({});
        setAppSaveAlert(null);
        if (newRecords.length > 0) setPermits([...newRecords, ...permits]);
        if (selectedApplicantView && selectedApplicant && selectedApplicantView.id === selectedApplicant.id) {
          await viewApplications(selectedApplicantView);
        }
        setShowAppTypeModal(false);
        setSelectedApplicant(null);
        setSelectedAppTypes([]);
        setAppForms({});
        setModalGrossItem({ label: '', amount: '' });
        setModalBillingCharges([]);
        setModalOccupations([]);
    } catch (err) {
        console.error(err);
        setAppSaveAlert(err.message || 'An error occurred while saving.');
    } finally {
        setAppSaveLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">Applicants Registry</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => { reset(); setShowModal(true); }}>
          Add Applicant
        </Button>
      </Box>
      
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name, barangay or mobile"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Search /></InputAdornment>
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Barangay</InputLabel>
              <Select
                value={filterBarangay}
                label="Barangay"
                onChange={(e) => setFilterBarangay(e.target.value)}
              >
                <MenuItem value="All">All Barangays</MenuItem>
                {barangays.map((b) => (
                  <MenuItem key={b} value={b}>{b}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Gender</InputLabel>
              <Select
                value={filterGender}
                label="Gender"
                onChange={(e) => setFilterGender(e.target.value)}
              >
                <MenuItem value="All">All Genders</MenuItem>
                <MenuItem value="Male">Male</MenuItem>
                <MenuItem value="Female">Female</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                Total: {filtered.length}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ bgcolor: 'grey.100' }}>
            <TableRow>
              <TableCell>Full Name</TableCell>
              <TableCell>Gender</TableCell>
              <TableCell>Barangay</TableCell>
              <TableCell>Birthdate</TableCell>
              <TableCell>Age</TableCell>
              <TableCell>Mobile</TableCell>
              <TableCell>Email</TableCell>
              <TableCell align="center">Applications</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={9} align="center">No applicants found</TableCell></TableRow>
            ) : filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(b => (
              <TableRow key={b.id} hover onClick={() => { viewApplications(b); }} sx={{ cursor: 'pointer' }}>
                <TableCell><strong>{formatName(b.name)}</strong></TableCell>
                <TableCell>{b.gender}</TableCell>
                <TableCell>{b.barangay}</TableCell>
                <TableCell>{b.birthdate}</TableCell>
                <TableCell>{b.age}</TableCell>
                <TableCell>{b.mobile}</TableCell>
                <TableCell>{b.email}</TableCell>
                <TableCell align="center">
                  <Chip 
                    label={permits.filter(p => p.businessId === b.id).length} 
                    color={permits.filter(p => p.businessId === b.id).length > 0 ? "primary" : "default"} 
                    size="small" 
                  />
                </TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={(e) => { 
                    e.stopPropagation(); 
                    setEditing(b.id); 
                    const parsed = parseName(b.name);
                    setForm({ ...parsed, name: b.name, gender: b.gender || 'Male', barangay: b.barangay || barangays[0], birthdate: b.birthdate, age: b.age, mobile: b.mobile, email: b.email, status: b.status, date_registered: b.date_registered }); 
                    setShowModal(true); 
                  }}>
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); removeApplicant(b); }}>
                    <Delete fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={filtered.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

        <Dialog open={showApplicantAppsModal} onClose={() => { setShowApplicantAppsModal(false); setSelectedApplicantView(null); }} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Applications for {selectedApplicantView?.name}</Typography>
            {selectedApplicantView && (
              <Button variant="contained" startIcon={<Add />} onClick={() => { openAddApplication(selectedApplicantView); }}>Add Application</Button>
            )}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead sx={{ bgcolor: 'grey.100' }}>
                <TableRow>
                  <TableCell>Application No</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Issue Date</TableCell>
                  <TableCell>Expiry Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingApps ? (
                  <TableRow><TableCell colSpan={6} align="center">Loading...</TableCell></TableRow>
                ) : appList.length === 0 ? (
                  <TableRow><TableCell colSpan={6} align="center">No applications found</TableCell></TableRow>
                ) : appList.map(a => (
                  <TableRow key={a.id}>
                    <TableCell><strong>{a.application_no}</strong></TableCell>
                    <TableCell>{a.application_type}</TableCell>
                    <TableCell>{a.issue_date}</TableCell>
                    <TableCell>{a.expiry_date || ''}</TableCell>
                    <TableCell>
                      <Chip label={a.status} color={a.status === 'Active' ? 'success' : 'warning'} size="small" />
                    </TableCell>
                    <TableCell align="center">
                  <IconButton size="small" onClick={() => { openEditApplication(a); }}><Edit fontSize="small" /></IconButton>
                      <IconButton size="small" color="error" onClick={() => deleteApplication(a.id)}><Delete fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setShowApplicantAppsModal(false); setSelectedApplicantView(null); }}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showModal} onClose={() => setShowModal(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Person />
          {editing ? 'Edit Applicant' : 'Add Applicant'}
        </DialogTitle>
        <DialogContent dividers>
          {applicantAlert && (
            <Alert severity="error" sx={{ mb: 2 }} aria-live="polite">{applicantAlert}</Alert>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary', fontWeight: 'bold' }}>PERSONAL INFORMATION</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="First Name" value={form.firstName} onChange={(e)=>{ setForm({ ...form, firstName: e.target.value }); setApplicantErrors(prev => ({ ...prev, firstName: e.target.value ? undefined : prev.firstName })); }} required error={Boolean(applicantErrors.firstName)} helperText={applicantErrors.firstName || ''} inputProps={{ 'aria-invalid': Boolean(applicantErrors.firstName) }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="Middle Name" value={form.middleName} onChange={(e)=>setForm({ ...form, middleName: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="Last Name" value={form.lastName} onChange={(e)=>{ setForm({ ...form, lastName: e.target.value }); setApplicantErrors(prev => ({ ...prev, lastName: e.target.value ? undefined : prev.lastName })); }} required error={Boolean(applicantErrors.lastName)} helperText={applicantErrors.lastName || ''} inputProps={{ 'aria-invalid': Boolean(applicantErrors.lastName) }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="Extension" value={form.suffix} onChange={(e)=>setForm({ ...form, suffix: e.target.value })} placeholder="Jr., III" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <AccessibleSelect
                    label="Gender"
                    value={form.gender}
                    onChange={(e) => {
                      setForm({ ...form, gender: e.target.value });
                      setApplicantErrors(prev => ({ ...prev, gender: e.target.value ? undefined : prev.gender }));
                    }}
                    options={['Male', 'Female', 'Other']}
                    required
                    error={Boolean(applicantErrors.gender)}
                    helperText={applicantErrors.gender || ''}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" type="date" label="Birthdate" InputLabelProps={{ shrink: true }} value={form.birthdate} onChange={(e)=>setForm({ ...form, birthdate: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="Age" value={form.age} InputProps={{ readOnly: true }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <AccessibleSelect
                    label="Barangay"
                    value={form.barangay}
                    onChange={(e) => {
                      setForm({ ...form, barangay: e.target.value });
                      setApplicantErrors(prev => ({ ...prev, barangay: e.target.value ? undefined : prev.barangay }));
                    }}
                    options={barangays}
                    required
                    error={Boolean(applicantErrors.barangay)}
                    helperText={applicantErrors.barangay || ''}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
               <Divider sx={{ my: 2 }} />
               <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary', fontWeight: 'bold' }}>CONTACT INFORMATION</Typography>
               <Grid container spacing={2}>
                 <Grid item xs={12} sm={6}>
                   <TextField
                     fullWidth
                     size="small"
                     label="Mobile Number"
                     value={form.mobile}
                     onChange={(e)=>setForm({ ...form, mobile: e.target.value })}
                     InputProps={{ startAdornment: <InputAdornment position="start"><Phone /></InputAdornment> }}
                   />
                 </Grid>
                 <Grid item xs={12} sm={6}>
                   <TextField
                     fullWidth
                     size="small"
                     type="email"
                     label="Email Address"
                     value={form.email}
                     onChange={(e)=>setForm({ ...form, email: e.target.value })}
                     InputProps={{ startAdornment: <InputAdornment position="start"><Email /></InputAdornment> }}
                   />
                 </Grid>
               </Grid>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowModal(false)}>Cancel</Button>
          <Button onClick={save} variant="contained" startIcon={<Save />}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showAppTypeModal} onClose={() => { setShowAppTypeModal(false); setSelectedApplicant(null); setSelectedAppTypes([]); setAppForms({}); setModalGrossItem({ label: '', amount: '' }); setVehicles([]); setShowVehicleForm(false); }} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedAppTypes.length > 0 ? 'New Applications' : 'Select Application Types'}
        </DialogTitle>
        <DialogContent dividers>
          {selectedAppTypes.length === 0 ? (
            applicationTypes.length === 0 ? (
              <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                <Typography>No application types configured</Typography>
              </Box>
            ) : (
              <Grid container spacing={2} sx={{ p: 1 }}>
                {applicationTypes.map(t => {
                   let Icon = Description;
                   if (t.includes('Business')) Icon = BusinessCenter;
                   else if (t.includes('Motorcycle') || t.includes('Tricycle')) Icon = DirectionsBike;
                   else if (t.includes('Sanitary')) Icon = MedicalServices;
                   else if (t.includes('Fishing')) Icon = Sailing;
                   
                   return (
                    <Grid item xs={12} sm={6} key={t}>
                      <Card 
                        variant="outlined"
                        sx={{ 
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.50', transform: 'translateY(-2px)' }
                        }}
                        onClick={() => toggleType(t)}
                      >
                        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                            <Icon />
                          </Avatar>
                          <Typography variant="subtitle1" fontWeight="bold">{t}</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                   );
                })}
              </Grid>
            )
          ) : (
            <Stack spacing={3}>
              {selectedAppTypes.map((type) => {
                let Icon = Description;
                if (type.includes('Business')) Icon = BusinessCenter;
                else if (type.includes('Motorcycle') || type.includes('Tricycle')) Icon = DirectionsBike;
                else if (type.includes('Sanitary')) Icon = MedicalServices;
                else if (type.includes('Fishing')) Icon = Sailing;

                return (
                <Paper key={type} elevation={0} variant="outlined" sx={{ overflow: 'hidden', borderRadius: 2 }}>
                    <Box sx={{ bgcolor: 'grey.100', px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                            <Icon fontSize="small" />
                        </Avatar>
                        <Typography variant="h6" fontWeight="bold" color="text.primary">{type}</Typography>
                    </Box>
                    <Box sx={{ p: 3 }}>
                      
                      {type === 'Occupation/Calling' ? (
                        <Box sx={{ mt: 2 }}>
                          <OccupationSelector 
                            value={modalOccupations} 
                            onChange={(val) => setModalOccupations(val)}
                            error={Boolean(modalOccupationsError)}
                            helperText={modalOccupationsError}
                          />
                          {billingChargesSection}
                        </Box>
                      ) : type === 'Fishing Vessel and Gear Registration' ? (
                         <Box sx={{ mt: 2 }}>
                           <Tabs value={modalTabValue} onChange={(e, v) => setModalTabValue(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                             <Tab label="Fishing Vessels" />
                             <Tab label="Fishing Gears" />
                           </Tabs>
 
                           {modalTabValue === 0 && (
                             <Box>
                               {!modalShowVesselForm ? (
                                 <Box>
                                   <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                     <Typography variant="subtitle2">Registered Vessels: {modalFishingVessels.length}</Typography>
                                     <Button variant="outlined" size="small" startIcon={<Add />} onClick={() => { 
                                       setModalNewVessel({ registrationType: 'New', type: 'Motorized', name: '', homeport: '', placeBuilt: '', yearBuilt: '', material: 'Wood', engines: [] }); 
                                       setModalShowVesselForm(true); 
                                     }}>
                                       Add Vessel
                                     </Button>
                                   </Box>
                                   {modalFishingVessels.length === 0 ? (
                                     <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>No vessels added.</Typography>
                                   ) : (
                                     <Stack spacing={1}>
                                       {modalFishingVessels.map((v, idx) => (
                                         <Paper key={idx} variant="outlined" sx={{ p: 1.5 }}>
                                           <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                             <Box>
                                               <Typography variant="subtitle2">{v.name} ({v.type})</Typography>
                                               <Typography variant="caption" color="text.secondary">Reg: {v.registrationType} | Mat: {v.material}</Typography>
                                               {v.engines.length > 0 && (
                                                 <Typography variant="caption" display="block">Engines: {v.engines.map(e => `${e.name} (${e.horsepower}HP)`).join(', ')}</Typography>
                                               )}
                                             </Box>
                                             <IconButton size="small" color="error" onClick={() => setModalFishingVessels(modalFishingVessels.filter((_, i) => i !== idx))}><Delete fontSize="small" /></IconButton>
                                           </Box>
                                         </Paper>
                                       ))}
                                     </Stack>
                                   )}
                                 </Box>
                               ) : (
                                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                                  <Typography variant="subtitle2" sx={{ mb: 2 }}>New Vessel Details</Typography>
                                  {modalVesselAlert && (
                                    <Alert severity="error" sx={{ mb: 2 }} aria-live="polite">{modalVesselAlert}</Alert>
                                  )}
                                  <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                      <AccessibleSelect
                                        label="Registration Type"
                                        value={modalNewVessel.registrationType}
                                        onChange={(e) => { setModalNewVessel({...modalNewVessel, registrationType: e.target.value}); setModalVesselErrors(prev => ({ ...prev, registrationType: e.target.value ? undefined : (prev.registrationType) })); }}
                                        options={[ 'New', 'Renew' ]}
                                        required
                                        error={Boolean(modalVesselErrors.registrationType)}
                                        helperText={modalVesselErrors.registrationType || ''}
                                        name="registrationType"
                                      />
                                    </Grid>
                                    <Grid item xs={12}>
                                      <AccessibleSelect
                                        label="Type"
                                        value={modalNewVessel.type}
                                        onChange={(e) => { setModalNewVessel({...modalNewVessel, type: e.target.value}); setModalVesselErrors(prev => ({ ...prev, type: e.target.value ? undefined : (prev.type) })); }}
                                        options={[ 'Motorized', 'Non-motorized' ]}
                                        required
                                        error={Boolean(modalVesselErrors.type)}
                                        helperText={modalVesselErrors.type || ''}
                                        name="vesselType"
                                      />
                                    </Grid>
                                    <Grid item xs={12}>
                                      <TextField fullWidth size="small" label="Name of Fishing Vessel *" value={modalNewVessel.name} onChange={(e) => { setModalNewVessel({...modalNewVessel, name: e.target.value}); setModalVesselErrors(prev => ({ ...prev, name: e.target.value ? undefined : (prev.name) })); }} error={Boolean(modalVesselErrors.name)} helperText={modalVesselErrors.name || ''} inputProps={{ 'aria-invalid': Boolean(modalVesselErrors.name) }} />
                                    </Grid>
                                     <Grid item xs={12}>
                                       <TextField fullWidth size="small" label="Homeport (Optional)" value={modalNewVessel.homeport} onChange={(e) => setModalNewVessel({...modalNewVessel, homeport: e.target.value})} />
                                     </Grid>
                                     <Grid item xs={12}>
                                       <TextField fullWidth size="small" label="Place Built (Optional)" value={modalNewVessel.placeBuilt} onChange={(e) => setModalNewVessel({...modalNewVessel, placeBuilt: e.target.value})} />
                                     </Grid>
                                     <Grid item xs={12}>
                                       <TextField fullWidth size="small" label="Month & Year Built (Optional)" value={modalNewVessel.yearBuilt} onChange={(e) => setModalNewVessel({...modalNewVessel, yearBuilt: e.target.value})} placeholder="e.g. Jan 2020" />
                                     </Grid>
                                     <Grid item xs={12}>
                                      <AccessibleSelect
                                        label="Material Used"
                                        value={modalNewVessel.material}
                                        onChange={(e) => setModalNewVessel({...modalNewVessel, material: e.target.value})}
                                        options={['Wood', 'Fiber Glass', 'Composite']}
                                        fullWidth
                                      />
                                    </Grid>
                                     
                                     {modalNewVessel.type === 'Motorized' && (
                                     <Grid item xs={12}>
                                       <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.paper', mt: 1 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                          <Typography variant="subtitle2" fontWeight="bold" color="text.secondary">ENGINE DETAILS</Typography>
                                          {!modalShowEngineForm && (
                                            <Button size="small" startIcon={<Add />} onClick={() => setModalShowEngineForm(true)} variant="outlined">
                                              Add Engine
                                            </Button>
                                          )}
                                        </Box>

                                        {modalNewVessel.engines.length === 0 && !modalShowEngineForm && (
                                          <Typography variant="body2" color="error" align="center" sx={{ py: 1, fontStyle: 'italic' }}>
                                            No engines added (Required for Motorized)
                                          </Typography>
                                        )}

                                        <Stack spacing={1} sx={{ mb: modalShowEngineForm ? 2 : 0 }}>
                                          {modalNewVessel.engines.map((eng, idx) => (
                                            <Box key={idx} sx={{ 
                                              display: 'flex', 
                                              justifyContent: 'space-between', 
                                              alignItems: 'center',
                                              bgcolor: 'grey.50', 
                                              p: 1.5, 
                                              borderRadius: 1, 
                                              border: '1px solid',
                                              borderColor: 'divider'
                                            }}>
                                              <Box>
                                                <Typography variant="subtitle2">{eng.name}</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                  {eng.horsepower} HP {eng.serial && `• SN: ${eng.serial}`}
                                                </Typography>
                                              </Box>
                                              <IconButton size="small" color="error" onClick={() => setModalNewVessel({...modalNewVessel, engines: modalNewVessel.engines.filter((_, i) => i !== idx)})}>
                                                <Delete fontSize="small" />
                                              </IconButton>
                                            </Box>
                                          ))}
                                        </Stack>

                                        {modalShowEngineForm && (
                                          <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1, border: '1px dashed', borderColor: 'divider' }}>
                                            <Typography variant="caption" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>NEW ENGINE</Typography>
                                            <Grid container spacing={2}>
                                              <Grid item xs={12} sm={6}>
                                                <TextField 
                                                  fullWidth 
                                                  size="small" 
                                                  label="Engine Name *" 
                                                  value={modalNewEngine.name} 
                                                  onChange={(e) => { 
                                                    setModalNewEngine({...modalNewEngine, name: e.target.value}); 
                                                    setModalEngineErrors(prev => ({ ...prev, name: e.target.value ? undefined : (prev.name) })); 
                                                  }} 
                                                  error={Boolean(modalEngineErrors.name)} 
                                                  helperText={modalEngineErrors.name || ''} 
                                                />
                                              </Grid>
                                              <Grid item xs={6} sm={3}>
                                                <TextField 
                                                  fullWidth 
                                                  size="small" 
                                                  label="Horsepower *" 
                                                  value={modalNewEngine.horsepower} 
                                                  onChange={(e) => { 
                                                    setModalNewEngine({...modalNewEngine, horsepower: e.target.value}); 
                                                    setModalEngineErrors(prev => ({ ...prev, horsepower: e.target.value ? undefined : (prev.horsepower) })); 
                                                  }} 
                                                  error={Boolean(modalEngineErrors.horsepower)} 
                                                  helperText={modalEngineErrors.horsepower || ''} 
                                                />
                                              </Grid>
                                              <Grid item xs={6} sm={3}>
                                                <TextField 
                                                  fullWidth 
                                                  size="small" 
                                                  label="Serial No. (Opt)" 
                                                  value={modalNewEngine.serial} 
                                                  onChange={(e) => setModalNewEngine({...modalNewEngine, serial: e.target.value})} 
                                                />
                                              </Grid>
                                              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                                <Button size="small" onClick={() => {
                                                  setModalShowEngineForm(false);
                                                  setModalNewEngine({ name: '', horsepower: '', serial: '' });
                                                  setModalEngineErrors({});
                                                }}>Cancel</Button>
                                                <Button variant="contained" size="small" onClick={() => {
                                                  const eErrs = {};
                                                  if (!String(modalNewEngine.name || '').trim()) eErrs.name = 'Required';
                                                  if (!String(modalNewEngine.horsepower || '').trim()) eErrs.horsepower = 'Required';
                                                  if (Object.keys(eErrs).length > 0) { setModalEngineErrors(eErrs); return; }
                                                  setModalEngineErrors({});
                                                  setModalNewVessel({...modalNewVessel, engines: [...modalNewVessel.engines, modalNewEngine]});
                                                  setModalNewEngine({ name: '', horsepower: '', serial: '' });
                                                  setModalShowEngineForm(false);
                                                }}>Add Engine</Button>
                                              </Grid>
                                            </Grid>
                                          </Box>
                                        )}
                                      </Paper>

                                    </Grid>
                                    )}
 
                                    <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                      <Button onClick={() => { setModalShowVesselForm(false); setModalVesselErrors({}); setModalVesselAlert(null); }}>Cancel</Button>
                                      <Button variant="contained" onClick={() => {
                                        const vErrs = {};
                                        if (!String(modalNewVessel.registrationType || '').trim()) vErrs.registrationType = 'Registration Type is required';
                                        if (!String(modalNewVessel.type || '').trim()) vErrs.type = 'Type is required';
                                        if (!String(modalNewVessel.name || '').trim()) vErrs.name = 'Name of Fishing Vessel is required';
                                        
                                        if (modalNewVessel.type === 'Motorized' && modalNewVessel.engines.length === 0) {
                                            vErrs.engines = 'At least one engine is required for Motorized vessels';
                                            setModalVesselAlert('At least one engine is required for Motorized vessels');
                                        }

                                        if (Object.keys(vErrs).length > 0) {
                                          setModalVesselErrors(vErrs);
                                          if (!vErrs.engines) setModalVesselAlert(`Please complete required fields: ${Object.values(vErrs).join(', ')}`);
                                          return;
                                        }
                                        setModalVesselErrors({});
                                        setModalVesselAlert(null);
                                        setModalFishingVessels([...modalFishingVessels, modalNewVessel]);
                                        setModalShowVesselForm(false);
                                      }}>Save Vessel</Button>
                                    </Grid>
                                  </Grid>
                                </Paper>
                               )}
                             </Box>
                           )}
 
                          {modalTabValue === 1 && (
                             <Box>
                             <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                              {fishingGearsRef.length === 0 ? (
                                <Typography color="text.secondary" align="center" sx={{ py: 2 }}>No fishing gears available. Please manage gears in Settings.</Typography>
                              ) : (
                                Object.entries(groupedGears).map(([category, gears]) => (
                                  <Box key={category} sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" sx={{ bgcolor: 'grey.100', p: 1, borderRadius: 1, fontWeight: 'bold' }}>{category}</Typography>
                                    <Grid container spacing={1} sx={{ mt: 0.5, px: 1 }}>
                                      {gears.map(gear => {
                                        const isSelected = modalFishingGears.some(g => g.gearId === gear.id);
                                    const selectedGear = modalFishingGears.find(g => g.gearId === gear.id);
                                    return (
                                      <Grid item xs={12} key={gear.id}>
                                         <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #eee', pb: 0.5 }}>
                                            <FormControlLabel
                                              control={
                                                <Checkbox 
                                                  checked={isSelected}
                                                  onChange={(e) => handleModalGearToggle(gear, e.target.checked)}
                                                  size="small"
                                                />
                                              }
                                              label={String(gear.name)}
                                              sx={{ mr: 1, flex: 1, '& .MuiTypography-root': { fontSize: '0.875rem' } }}
                                            />
                                            {isSelected && (
                                              <TextField
                                                type="number"
                                                size="small"
                                                variant="standard"
                                                placeholder="Qty"
                                                value={selectedGear?.quantity || ''}
                                                onChange={(e) => handleModalGearQuantityChange(gear.id, e.target.value)}
                                                sx={{ width: 50 }}
                                                InputProps={{ inputProps: { min: 1 } }}
                                              />
                                            )}
                                         </Box>
                                      </Grid>
                                    );
                                  })}
                                    </Grid>
                                  </Box>
                                ))
                              )}
                            </Box>
                             </Box>
                           )}
                           {billingChargesSection}
                        </Box>
                     ) : type === 'Motorcycle and Tricycle Permit' ? (
                        <Box>
                             {appSaveAlert && (
                                <Alert severity="error" sx={{ mb: 2 }} aria-live="polite">{appSaveAlert}</Alert>
                             )}
                             <Grid container spacing={2}>
                                {/* Old Type of Permit field removed to avoid redundancy as it's now per-vehicle */}
                                
                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                        <Typography variant="subtitle2">Vehicles: {vehicles.length}</Typography>
                                        {!showVehicleForm && (
                                            <Button variant="outlined" size="small" startIcon={<Add />} onClick={() => {
                                                setNewVehicle({ permitType: 'Renewal', vehicleType: 'Motorcycle', serviceType: 'Private', brandModel: '', plateNumber: '', stickerNumber: '', licenseNumber: '', licenseExpiry: '' });
                                                setShowVehicleForm(true);
                                            }}>Add Vehicle</Button>
                                        )}
                                    </Box>
                                    
                                    {/* Type of Permit field is removed from main form for MC, but we need it available for vehicle addition context if needed */}
                                    {/* Actually, Type of Permit is Application Level, but vehicles are items. */}
                                    {/* If user wants Type of Permit inside the "Adding Vehicle" logic or just displayed? */}
                                    {/* User said: "I want to include the Type of Permit in Adding Vehicle details." */}
                                    {/* This likely means each vehicle might have its own type? Or just display the app's type? */}
                                    {/* Usually Type of Permit (New/Renewal) applies to the whole application. */}
                                    {/* But maybe they want to specify if a specific vehicle is New or Renewal? */}
                                    {/* Let's assume they want to set it PER VEHICLE. */}
                                    
                                    {!showVehicleForm ? (
                                        <Stack spacing={1}>
                                            {vehicles.length === 0 ? <Typography variant="body2" color="text.secondary" align="center">No vehicles added.</Typography> : 
                                            vehicles.map((v, idx) => (
                                                <Paper key={idx} variant="outlined" sx={{ p: 1.5 }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        <Box>
                                                            <Typography variant="subtitle2">{v.vehicleType} - {v.brandModel} ({v.permitType})</Typography>
                                                            <Typography variant="caption" color="text.secondary">{v.serviceType} | Plate: {v.plateNumber}</Typography>
                                                        </Box>
                                                        <IconButton size="small" color="error" onClick={() => {
                                                            const newVehicles = vehicles.filter((_, i) => i !== idx);
                                                            setVehicles(newVehicles);
                                                            // Optional: Recalculate charges based on remaining vehicles
                                                        }}>
                                                            <Delete fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                </Paper>
                                            ))}
                                        </Stack>
                                    ) : (
                                        <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                                            <Typography variant="subtitle2" sx={{ mb: 2 }}>New Vehicle Details</Typography>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12}>
                                                    <AccessibleSelect
                                                        label="Type of Permit"
                                                        value={newVehicle.permitType || 'Renewal'}
                                                        onChange={(e) => setNewVehicle({...newVehicle, permitType: e.target.value})}
                                                        options={['New', 'Renewal']}
                                                        fullWidth
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <AccessibleSelect
                                                        label="Vehicle Type"
                                                        value={newVehicle.vehicleType}
                                                        onChange={(e) => setNewVehicle({...newVehicle, vehicleType: e.target.value})}
                                                        options={['Motorcycle', 'Tricycle']}
                                                        fullWidth
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <AccessibleSelect
                                                        label="Service Type"
                                                        value={newVehicle.serviceType}
                                                        onChange={(e) => setNewVehicle({...newVehicle, serviceType: e.target.value})}
                                                        options={['Private', 'For Hire']}
                                                        fullWidth
                                                    />
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <TextField fullWidth size="small" label="Brand/Model" value={newVehicle.brandModel} onChange={(e) => setNewVehicle({...newVehicle, brandModel: e.target.value})} />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <TextField fullWidth size="small" label="Plate Number" value={newVehicle.plateNumber} onChange={(e) => setNewVehicle({...newVehicle, plateNumber: e.target.value})} />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <TextField fullWidth size="small" label="Sticker Number" value={newVehicle.stickerNumber} onChange={(e) => setNewVehicle({...newVehicle, stickerNumber: e.target.value})} />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <TextField fullWidth size="small" label="Driver's License No." value={newVehicle.licenseNumber} onChange={(e) => setNewVehicle({...newVehicle, licenseNumber: e.target.value})} />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <TextField fullWidth size="small" type="date" label="License Expiry" InputLabelProps={{ shrink: true }} value={newVehicle.licenseExpiry} onChange={(e) => setNewVehicle({...newVehicle, licenseExpiry: e.target.value})} />
                                                </Grid>
                                                <Grid item xs={12} sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                                    <Button onClick={() => setShowVehicleForm(false)}>Cancel</Button>
                                                    <Button variant="contained" onClick={() => {
                                                        if (!newVehicle.vehicleType || !newVehicle.serviceType) {
                                                            alert('Please select Vehicle Type and Service Type');
                                                            return;
                                                        }
                                                        const updatedVehicles = [...vehicles, newVehicle];
                                                        setVehicles(updatedVehicles);
                                                        setShowVehicleForm(false);
                                                        // Update charges based on the newly added vehicle (simple implementation: use last added)
                                                        updateMotorcycleCharges(newVehicle.vehicleType, newVehicle.serviceType);
                                                    }}>Add</Button>
                                                </Grid>
                                            </Grid>
                                        </Paper>
                                    )}
                             </Grid>
                           </Grid>
                           {billingChargesSection}
                        </Box>
                     ) : (
                      <>
                      {appSaveAlert && (
                        <Alert severity="error" sx={{ mb: 2 }} aria-live="polite">{appSaveAlert}</Alert>
                      )}
                      <Grid container spacing={2}>
                        {(formSchemas[type]||[]).filter(f => {
                          if (type === 'Business Permit') {
                            const t = appForms[type]?.typeOfBusiness;
                            if (f.name === 'gross') return false;
                            if (f.name === 'capital' && t === 'Renewal') return false;
                          }
                          return true;
                        }).map((f, index) => (
                          <Grid item xs={12} sm={6} key={`${type}-${f.name}`}>
                            {f.type === 'select' ? (
                              <AccessibleSelect
                                label={f.label}
                                value={(appForms[type]?.[f.name]) || ''}
                                onChange={(e) => {
                                  const newVal = e.target.value;
                                  setAppForms(prev => {
                                      const newForms = { ...prev, [type]: { ...prev[type], [f.name]: newVal } };
                                      return newForms;
                                  });
                                  if (type === 'Business Permit' && f.name === 'natureOfBusiness') {
                                    fetchDynamicCharges(type, newVal);
                                  }
                                }}
                                options={(f.options)||[]}
                                required={Boolean(f.required)}
                                error={Boolean(appFormErrors[type]?.[f.name])}
                                helperText={appFormErrors[type]?.[f.name] || ''}
                                name={`${type}-${f.name}`}
                              />
                            ) : (
                              <TextField
                                fullWidth
                                size="small"
                                type={f.type}
                                label={`${f.label}${f.required ? ' *' : ''}`}
                                value={(appForms[type]?.[f.name]) || ''}
                                onChange={(e)=>{ setAppForms({ ...appForms, [type]: { ...appForms[type], [f.name]: e.target.value } }); setAppFormErrors(prev => ({ ...prev, [type]: { ...(prev[type]||{}), [f.name]: e.target.value ? undefined : (prev[type]?.[f.name]) } })); }}
                                required={Boolean(f.required)}
                                InputLabelProps={f.type === 'date' || f.shrink ? { shrink: true } : undefined}
                                error={Boolean(appFormErrors[type]?.[f.name])}
                                helperText={appFormErrors[type]?.[f.name] || ''}
                              />
                            )}
                          </Grid>
                        ))}
                      </Grid>

                      {type === 'Business Permit' && (
                        <Box sx={{ mt: 2 }}>
                          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                             <Typography variant="subtitle2" sx={{ mb: 2 }}>Gross Sales Breakdown</Typography>
                             <Grid container spacing={1} alignItems="center">
                               <Grid item xs={12} sm>
                                 <TextField 
                                   size="small" 
                                   label="Line of Business / Item" 
                                   fullWidth 
                                   value={modalGrossItem.label} 
                                   onChange={(e) => setModalGrossItem({ ...modalGrossItem, label: e.target.value })}
                                 />
                               </Grid>
                               <Grid item xs="auto">
                                 <TextField 
                                   size="small" 
                                   label="Amount" 
                                   type="number" 
                                   sx={{ width: 160 }} 
                                   value={modalGrossItem.amount} 
                                   onChange={(e) => setModalGrossItem({ ...modalGrossItem, amount: e.target.value })}
                                   InputProps={{ startAdornment: <InputAdornment position="start">₱</InputAdornment> }}
                                 />
                               </Grid>
                               <Grid item xs="auto">
                                 <Button variant="contained" size="small" onClick={() => addModalGrossItem(type)} startIcon={<Add />}>Add</Button>
                               </Grid>
                             </Grid>
                             <Box sx={{ mt: 2 }}>
                               {(appForms[type]?.grossBreakdown || []).length === 0 ? (
                                 <Typography variant="body2" color="text.secondary" align="center">No items added</Typography>
                               ) : (
                                 <Table size="small">
                                   <TableHead>
                                     <TableRow>
                                       <TableCell>Item</TableCell>
                                       <TableCell align="right">Amount</TableCell>
                                       <TableCell align="right">Actions</TableCell>
                                     </TableRow>
                                   </TableHead>
                                   <TableBody>
                                     {(appForms[type]?.grossBreakdown || []).map((item) => (
                                       <TableRow key={item.id}>
                                         <TableCell>{item.label}</TableCell>
                                         <TableCell align="right">₱{parseFloat(item.amount || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}</TableCell>
                                         <TableCell align="right">
                                           <IconButton size="small" color="error" onClick={() => removeModalGrossItem(type, item.id)}>
                                             <Delete fontSize="small" />
                                           </IconButton>
                                         </TableCell>
                                       </TableRow>
                                     ))}
                                     <TableRow>
                                       <TableCell><strong>Total</strong></TableCell>
                                       <TableCell align="right"><strong>₱{(parseFloat(appForms[type]?.gross) || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}</strong></TableCell>
                                       <TableCell />
                                     </TableRow>
                                   </TableBody>
                                 </Table>
                               )}
                             </Box>
                          </Paper>
                           {appForms[type]?.typeOfBusiness === 'Renewal' && (
                             <Box sx={{ mt: 2 }}>
                               <TextField 
                                 fullWidth 
                                 size="small" 
                                 label="Total Gross" 
                                 type="number" 
                                 value={appForms[type]?.gross || ''} 
                                 onChange={(e) => setAppForms({ ...appForms, [type]: { ...appForms[type], gross: e.target.value } })}
                                 InputProps={{ readOnly: (appForms[type]?.grossBreakdown && appForms[type]?.grossBreakdown.length > 0) }}
                                 helperText={appForms[type]?.grossBreakdown && appForms[type]?.grossBreakdown.length > 0 ? "Calculated from breakdown" : "Enter total gross or add breakdown items"}
                               />
                             </Box>
                           )}
                        </Box>
                      )}
                      </>
                      )}
                      
                      {(typeChargesMap[type]||[]).length > 0 && type === 'Business Permit' && (
                        <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                           <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: 'primary.main', textTransform: 'uppercase' }}>Charges related to {type}</Typography>
                           <TableContainer component={Paper} variant="outlined">
                              <Table size="small">
                                <TableHead sx={{ bgcolor: 'grey.50' }}>
                                   <TableRow>
                                      <TableCell>Charge Item</TableCell>
                                      <TableCell align="right">Amount</TableCell>
                                   </TableRow>
                                </TableHead>
                                <TableBody>
                                   {(typeChargesMap[type]||[]).map(c => (
                                      <TableRow key={c.id}>
                                         <TableCell>{c.item}</TableCell>
                                         <TableCell align="right" sx={{ fontWeight: 'medium' }}>₱{parseFloat(c.amount).toLocaleString('en-PH', { minimumFractionDigits: 2 })}</TableCell>
                                      </TableRow>
                                   ))}
                                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                                        <TableCell><strong>Total</strong></TableCell>
                                        <TableCell align="right"><strong>₱{(typeChargesMap[type]||[]).reduce((s,c)=>s+parseFloat(c.amount),0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}</strong></TableCell>
                                    </TableRow>
                                </TableBody>
                              </Table>
                           </TableContainer>
                        </Box>
                      )}
                    </Box>
                  </Paper>
              );
              })}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {selectedAppTypes.length === 0 ? (
            <>
              <Button onClick={() => { setShowAppTypeModal(false); setSelectedApplicant(null); }}>Cancel</Button>
              <Button onClick={() => { if (selectedAppTypes.length > 0) {} }} disabled={selectedAppTypes.length === 0} variant="contained">Next</Button>
            </>
          ) : (
            <>
              <Button onClick={() => setSelectedAppTypes([])} disabled={appSaveLoading}>Back</Button>
              <Button onClick={saveApplication} variant="contained" startIcon={appSaveLoading ? <CircularProgress size={20} color="inherit" /> : <Save />} disabled={appSaveLoading}>
                {appSaveLoading ? 'Saving...' : 'Save'}
              </Button>
            </>
          )}
        </DialogActions>
                  </Dialog>

      <Dialog open={showEditAppModal} onClose={() => setShowEditAppModal(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: 'var(--bs-primary)', color: 'white' }}>
          Edit Application
        </DialogTitle>
        <DialogContent dividers>
          {editAppAlert && (
            <Alert severity="error" sx={{ mb: 2 }} aria-live="polite">{editAppAlert}</Alert>
          )}
          <Grid container spacing={3}>
            {/* Details Section */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, textTransform: 'uppercase' }}>
                  Application Details
                </Typography>
                
                {editingApp?.application_type === 'Business Permit' && (
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <AccessibleSelect
                        label="Type of Business"
                        value={editAppForm.type_of_business || 'New'}
                        onChange={(e)=>setEditAppForm({ ...editAppForm, type_of_business: e.target.value })}
                        options={[ 'New','Renewal' ]}
                        required
                        name="type_of_business"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField 
                        fullWidth 
                        size="small"
                        label="Name of Business *" 
                        value={editAppForm.business_name || ''} 
                        onChange={(e)=>{ setEditAppForm({ ...editAppForm, business_name: e.target.value }); setEditAppErrors(prev => ({ ...prev, business_name: e.target.value ? undefined : (prev.business_name) })); }}
                        error={Boolean(editAppErrors.business_name)}
                        helperText={editAppErrors.business_name || ''}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField 
                        fullWidth 
                        size="small"
                        label="Business Address *" 
                        value={editAppForm.business_address || ''} 
                        onChange={(e)=>{ setEditAppForm({ ...editAppForm, business_address: e.target.value }); setEditAppErrors(prev => ({ ...prev, business_address: e.target.value ? undefined : (prev.business_address) })); }}
                        error={Boolean(editAppErrors.business_address)}
                        helperText={editAppErrors.business_address || ''}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <AccessibleSelect
                        label="Nature of Business"
                        value={editAppForm.nature_of_business || ''}
                        onChange={(e)=>{ setEditAppForm({ ...editAppForm, nature_of_business: e.target.value }); setEditAppErrors(prev => ({ ...prev, nature_of_business: e.target.value ? undefined : (prev.nature_of_business) })); }}
                        options={natureOptions}
                        required
                        error={Boolean(editAppErrors.nature_of_business)}
                        helperText={editAppErrors.nature_of_business || ''}
                        name="nature_of_business"
                      />
                    </Grid>
                    {editAppForm.type_of_business === 'New' && (
                      <Grid item xs={12}>
                        <TextField 
                          fullWidth 
                          size="small"
                          type="number" 
                          label="Capital" 
                          value={editAppForm.capital || ''} 
                          onChange={(e)=>setEditAppForm({ ...editAppForm, capital: e.target.value })} 
                        />
                      </Grid>
                    )}
                    <Grid item xs={12}>
                       <Box sx={{ mt: 2 }}>
                          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                             <Typography variant="subtitle2" sx={{ mb: 2 }}>Gross Sales Breakdown</Typography>
                             <Grid container spacing={1} alignItems="center">
                               <Grid item xs={12} sm>
                                 <TextField 
                                   size="small" 
                                   label="Line of Business / Item" 
                                   fullWidth 
                                   value={editGrossItem.label} 
                                   onChange={(e) => setEditGrossItem({ ...editGrossItem, label: e.target.value })}
                                 />
                               </Grid>
                               <Grid item xs="auto">
                                 <TextField 
                                   size="small" 
                                   label="Amount" 
                                   type="number" 
                                   sx={{ width: 160 }} 
                                   value={editGrossItem.amount} 
                                   onChange={(e) => setEditGrossItem({ ...editGrossItem, amount: e.target.value })}
                                   InputProps={{ startAdornment: <InputAdornment position="start">₱</InputAdornment> }}
                                 />
                               </Grid>
                               <Grid item xs="auto">
                                 <Button variant="contained" size="small" onClick={addEditGrossItem} startIcon={<Add />}>Add</Button>
                               </Grid>
                             </Grid>
                             <Box sx={{ mt: 2 }}>
                               {(editAppForm.grossBreakdown || []).length === 0 ? (
                                 <Typography variant="body2" color="text.secondary" align="center">No items added</Typography>
                               ) : (
                                 <Table size="small">
                                   <TableHead>
                                     <TableRow>
                                       <TableCell>Item</TableCell>
                                       <TableCell align="right">Amount</TableCell>
                                       <TableCell align="right">Actions</TableCell>
                                     </TableRow>
                                   </TableHead>
                                   <TableBody>
                                     {(editAppForm.grossBreakdown || []).map((item) => (
                                       <TableRow key={item.id}>
                                         <TableCell>{item.label}</TableCell>
                                         <TableCell align="right">₱{parseFloat(item.amount || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}</TableCell>
                                         <TableCell align="right">
                                           <IconButton size="small" color="error" onClick={() => removeEditGrossItem(item.id)}>
                                             <Delete fontSize="small" />
                                           </IconButton>
                                         </TableCell>
                                       </TableRow>
                                     ))}
                                     <TableRow>
                                       <TableCell><strong>Total</strong></TableCell>
                                       <TableCell align="right"><strong>₱{(parseFloat(editAppForm.gross) || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}</strong></TableCell>
                                       <TableCell />
                                     </TableRow>
                                   </TableBody>
                                 </Table>
                               )}
                             </Box>
                          </Paper>
                           {editAppForm.type_of_business === 'Renewal' && (
                             <Box sx={{ mt: 2 }}>
                               <TextField 
                                 fullWidth 
                                 size="small" 
                                 label="Total Gross" 
                                 type="number" 
                                 value={editAppForm.gross || ''} 
                                 onChange={(e)=>setEditAppForm({ ...editAppForm, gross: e.target.value })} 
                                 InputProps={{ readOnly: (editAppForm.grossBreakdown && editAppForm.grossBreakdown.length > 0) }}
                                 helperText={editAppForm.grossBreakdown && editAppForm.grossBreakdown.length > 0 ? "Calculated from breakdown" : "Enter total gross or add breakdown items"}
                               />
                             </Box>
                           )}
                        </Box>
                    </Grid>
                  </Grid>
                )}

                {editingApp?.application_type === 'Motorcycle and Tricycle Permit' && (
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <AccessibleSelect
                        label="Vehicle Type"
                        value={editAppForm.vehicle_type || 'Motorcycle'}
                        onChange={(e)=>{ setEditAppForm({ ...editAppForm, vehicle_type: e.target.value }); setEditAppErrors(prev => ({ ...prev, vehicle_type: e.target.value ? undefined : (prev.vehicle_type) })); }}
                        options={[ 'Motorcycle','Tricycle' ]}
                        required
                        error={Boolean(editAppErrors.vehicle_type)}
                        helperText={editAppErrors.vehicle_type || ''}
                        name="vehicle_type"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <AccessibleSelect
                        label="Service Type"
                        value={editAppForm.service_type || 'Private'}
                        onChange={(e)=>{ setEditAppForm({ ...editAppForm, service_type: e.target.value }); setEditAppErrors(prev => ({ ...prev, service_type: e.target.value ? undefined : (prev.service_type) })); }}
                        options={[ 'Private','For Hire' ]}
                        required
                        error={Boolean(editAppErrors.service_type)}
                        helperText={editAppErrors.service_type || ''}
                        name="service_type"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField 
                        fullWidth 
                        size="small"
                        label="Brand/Model" 
                        value={editAppForm.brand_model || ''} 
                        onChange={(e)=>setEditAppForm({ ...editAppForm, brand_model: e.target.value })} 
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField 
                        fullWidth 
                        size="small"
                        label="Plate Number" 
                        value={editAppForm.plate_number || ''} 
                        onChange={(e)=>setEditAppForm({ ...editAppForm, plate_number: e.target.value })} 
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField 
                        fullWidth 
                        size="small"
                        label="Sticker Number" 
                        value={editAppForm.sticker_number || ''} 
                        onChange={(e)=>setEditAppForm({ ...editAppForm, sticker_number: e.target.value })} 
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField 
                        fullWidth 
                        size="small"
                        label="Driver's License No." 
                        value={editAppForm.license_number || ''} 
                        onChange={(e)=>setEditAppForm({ ...editAppForm, license_number: e.target.value })} 
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField 
                        fullWidth 
                        size="small"
                        type="date" 
                        label="License Expiry" 
                        InputLabelProps={{ shrink: true }}
                        value={editAppForm.license_expiry || ''} 
                        onChange={(e)=>setEditAppForm({ ...editAppForm, license_expiry: e.target.value })} 
                      />
                    </Grid>
                  </Grid>
                )}

                {editingApp?.application_type === 'Fishing Vessel and Gear Registration' && (
                  <Box sx={{ mt: 2 }}>
                    <Tabs value={modalTabValue} onChange={(e, v) => setModalTabValue(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                      <Tab label="Fishing Vessels" />
                      <Tab label="Fishing Gears" />
                    </Tabs>

                    {modalTabValue === 0 && (
                      <Box>
                        {!modalShowVesselForm ? (
                          <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                              <Typography variant="subtitle2">Registered Vessels: {modalFishingVessels.length}</Typography>
                              <Button variant="outlined" size="small" startIcon={<Add />} onClick={() => { 
                                setModalNewVessel({ registrationType: 'New', type: 'Motorized', name: '', homeport: '', placeBuilt: '', yearBuilt: '', material: 'Wood', engines: [] }); 
                                setModalShowVesselForm(true); 
                              }}>
                                Add Vessel
                              </Button>
                            </Box>
                            {modalFishingVessels.length === 0 ? (
                              <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>No vessels added.</Typography>
                            ) : (
                              <Stack spacing={1}>
                                {modalFishingVessels.map((v, idx) => (
                                  <Paper key={idx} variant="outlined" sx={{ p: 1.5 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                      <Box>
                                        <Typography variant="subtitle2">{v.name} ({v.type})</Typography>
                                        <Typography variant="caption" color="text.secondary">Reg: {v.registrationType} | Mat: {v.material}</Typography>
                                        {v.engines.length > 0 && (
                                          <Typography variant="caption" display="block">Engines: {v.engines.map(e => `${e.name} (${e.horsepower}HP)`).join(', ')}</Typography>
                                        )}
                                      </Box>
                                      <IconButton size="small" color="error" onClick={() => setModalFishingVessels(modalFishingVessels.filter((_, i) => i !== idx))}><Delete fontSize="small" /></IconButton>
                                    </Box>
                                  </Paper>
                                ))}
                              </Stack>
                            )}
                          </Box>
                        ) : (
                          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                            <Typography variant="subtitle2" sx={{ mb: 2 }}>New Vessel Details</Typography>
                            <Grid container spacing={2}>
                              <Grid item xs={12}>
                                <AccessibleSelect
                                  label="Registration Type"
                                  value={modalNewVessel.registrationType}
                                  onChange={(e) => setModalNewVessel({...modalNewVessel, registrationType: e.target.value})}
                                  options={['New', 'Renew']}
                                  fullWidth
                                />
                              </Grid>
                              <Grid item xs={12}>
                                <AccessibleSelect
                                  label="Type"
                                  value={modalNewVessel.type}
                                  onChange={(e) => setModalNewVessel({...modalNewVessel, type: e.target.value})}
                                  options={['Motorized', 'Non-motorized']}
                                  fullWidth
                                />
                              </Grid>
                              <Grid item xs={12}>
                                <TextField fullWidth size="small" label="Name of Fishing Vessel" value={modalNewVessel.name} onChange={(e) => setModalNewVessel({...modalNewVessel, name: e.target.value})} />
                              </Grid>
                              <Grid item xs={12}>
                                <TextField fullWidth size="small" label="Homeport (Optional)" value={modalNewVessel.homeport} onChange={(e) => setModalNewVessel({...modalNewVessel, homeport: e.target.value})} />
                              </Grid>
                              <Grid item xs={12}>
                                <TextField fullWidth size="small" label="Place Built (Optional)" value={modalNewVessel.placeBuilt} onChange={(e) => setModalNewVessel({...modalNewVessel, placeBuilt: e.target.value})} />
                              </Grid>
                              <Grid item xs={12}>
                                <TextField fullWidth size="small" label="Month & Year Built (Optional)" value={modalNewVessel.yearBuilt} onChange={(e) => setModalNewVessel({...modalNewVessel, yearBuilt: e.target.value})} placeholder="e.g. Jan 2020" />
                              </Grid>
                              <Grid item xs={12}>
                                <AccessibleSelect
                                  label="Material Used"
                                  value={modalNewVessel.material}
                                  onChange={(e) => setModalNewVessel({...modalNewVessel, material: e.target.value})}
                                  options={['Wood', 'Fiber Glass', 'Composite']}
                                  fullWidth
                                />
                              </Grid>
                              
                              {modalNewVessel.type === 'Motorized' && (
                              <Grid item xs={12}>
                                <Divider sx={{ my: 1 }}><Typography variant="caption">ENGINE DETAILS</Typography></Divider>
                                
                                {modalNewVessel.engines.length === 0 && !modalShowEngineForm && (
                                  <Typography variant="body2" color="error" align="center" sx={{ py: 1, fontStyle: 'italic' }}>
                                    No engines added (Required for Motorized)
                                  </Typography>
                                )}

                                {modalNewVessel.engines.map((eng, idx) => (
                                  <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', bgcolor: 'white', p: 1, borderRadius: 1, mb: 1, border: '1px solid #eee' }}>
                                    <Typography variant="body2">{eng.name} - {eng.horsepower}HP {eng.serial && `(SN: ${eng.serial})`}</Typography>
                                    <IconButton size="small" color="error" onClick={() => setModalNewVessel({...modalNewVessel, engines: modalNewVessel.engines.filter((_, i) => i !== idx)})}><Delete fontSize="small" /></IconButton>
                                  </Box>
                                ))}
                                {modalShowEngineForm ? (
                                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
                                    <TextField size="small" label="Name" value={modalNewEngine.name} onChange={(e) => setModalNewEngine({...modalNewEngine, name: e.target.value})} sx={{ flex: 2 }} />
                                    <TextField size="small" label="HP" value={modalNewEngine.horsepower} onChange={(e) => setModalNewEngine({...modalNewEngine, horsepower: e.target.value})} sx={{ flex: 1 }} />
                                    <TextField size="small" label="Serial (Opt)" value={modalNewEngine.serial} onChange={(e) => setModalNewEngine({...modalNewEngine, serial: e.target.value})} sx={{ flex: 1 }} />
                                    <Button variant="contained" size="small" onClick={() => {
                                      if (modalNewEngine.name && modalNewEngine.horsepower) {
                                        setModalNewVessel({...modalNewVessel, engines: [...modalNewVessel.engines, modalNewEngine]});
                                        setModalNewEngine({ name: '', horsepower: '', serial: '' });
                                        setModalShowEngineForm(false);
                                      }
                                    }}>OK</Button>
                                    <Button size="small" onClick={() => setModalShowEngineForm(false)}>X</Button>
                                  </Box>
                                ) : (
                                  <Button size="small" startIcon={<Add />} onClick={() => setModalShowEngineForm(true)}>Add Engine</Button>
                                )}
                              </Grid>
                              )}

                              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                <Button onClick={() => setModalShowVesselForm(false)}>Cancel</Button>
                                <Button variant="contained" onClick={() => {
                                  const vErrs = {};
                                  if (!String(modalNewVessel.name || '').trim()) vErrs.name = 'Name of Fishing Vessel is required';
                                  
                                  if (modalNewVessel.type === 'Motorized' && modalNewVessel.engines.length === 0) {
                                      vErrs.engines = 'At least one engine is required for Motorized vessels';
                                      alert('At least one engine is required for Motorized vessels'); // Fallback alert
                                  }

                                  if (Object.keys(vErrs).length > 0) {
                                      // Since we don't have modalVesselErrors state wired up fully here in this specific block, 
                                      // we rely on the alert for engines, and we might want to alert for name too if it's missing.
                                      if (vErrs.name) alert(vErrs.name);
                                      return;
                                  }
                                  
                                  setModalFishingVessels([...modalFishingVessels, modalNewVessel]);
                                  setModalShowVesselForm(false);
                                }}>Save Vessel</Button>
                              </Grid>
                            </Grid>
                          </Paper>
                        )}
                      </Box>
                    )}

                    {modalTabValue === 1 && (
                      <Box>
                        <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                          {fishingGearsRef.length === 0 ? (
                            <Typography color="text.secondary" align="center" sx={{ py: 2 }}>No fishing gears available. Please manage gears in Settings.</Typography>
                          ) : (
                            Object.entries(groupedGears).map(([category, gears]) => (
                              <Box key={category} sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ bgcolor: 'grey.100', p: 1, borderRadius: 1, fontWeight: 'bold' }}>{category}</Typography>
                                <Grid container spacing={1} sx={{ mt: 0.5, px: 1 }}>
                                  {gears.map(gear => {
                                    const isSelected = modalFishingGears.some(g => g.gearId === gear.id);
                                    const selectedGear = modalFishingGears.find(g => g.gearId === gear.id);
                                    return (
                                      <Grid item xs={12} key={gear.id}>
                                         <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #eee', pb: 0.5 }}>
                                            <FormControlLabel
                                              control={
                                                <Checkbox 
                                                  checked={isSelected}
                                                  onChange={(e) => handleModalGearToggle(gear, e.target.checked)}
                                                  size="small"
                                                />
                                              }
                                              label={String(gear.name)}
                                              sx={{ mr: 1, flex: 1, '& .MuiTypography-root': { fontSize: '0.875rem' } }}
                                            />
                                            {isSelected && (
                                              <TextField
                                                type="number"
                                                size="small"
                                                variant="standard"
                                                placeholder="Qty"
                                                value={selectedGear?.quantity || ''}
                                                onChange={(e) => handleModalGearQuantityChange(gear.id, e.target.value)}
                                                sx={{ width: 50 }}
                                                InputProps={{ inputProps: { min: 1 } }}
                                              />
                                            )}
                                         </Box>
                                      </Grid>
                                    );
                                  })}
                                </Grid>
                              </Box>
                            ))
                          )}
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}

                {editingApp?.application_type === 'Occupation/Calling' && (
                  <Box sx={{ mt: 2 }}>
                    <OccupationSelector 
                      value={modalOccupations} 
                      onChange={(val) => setModalOccupations(val)}
                      error={Boolean(modalOccupationsError)}
                      helperText={modalOccupationsError}
                    />
                    {billingChargesSection}
                  </Box>
                )}

              </Paper>
            </Grid>
            <Grid item xs={12} />
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowEditAppModal(false)}>Cancel</Button>
          <Button onClick={saveEditedApplication} variant="contained" startIcon={<Save />}>Save Changes</Button>
        </DialogActions>
      </Dialog>
      
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}>
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ whiteSpace: 'pre-line' }}>{confirmDialog.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}>Cancel</Button>
          <Button onClick={confirmDialog.onConfirm} variant="contained" sx={{ bgcolor: 'var(--bs-primary)', color: '#fff', '&:hover': { bgcolor: 'var(--bs-primary)' } }}>Confirm</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const PaymentRow = ({ pay, onUpdated, showSnackbar }) => {
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({
    or_no: pay.or_no || '',
    payment_date: pay.payment_date || '',
    amount: pay.amount || 0,
    notes: pay.notes || ''
  });
  const [saving, setSaving] = useState(false);
  const particulars = pay.particulars || [];

  const handleSave = async () => {
    if (!form.payment_date || !form.amount) return;
    setSaving(true);
    const payload = {
      or_no: form.or_no || null,
      payment_date: form.payment_date,
      amount: parseFloat(form.amount),
      notes: form.notes || null
    };
    const res = await bploService.updatePayment(pay.id, payload);
    setSaving(false);
    if (res.success && res.data) {
      if (onUpdated) onUpdated(res.data);
      if (showSnackbar) showSnackbar('Payment updated successfully', 'success');
      setEditOpen(false);
    } else {
      if (showSnackbar) showSnackbar(res.error || 'Failed to update payment', 'error');
    }
  };

  return (
    <>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell>
          {particulars.length > 0 && (
            <IconButton size="small" onClick={() => setOpen(!open)}>
              {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </IconButton>
          )}
        </TableCell>
        <TableCell sx={{ fontWeight: 'bold' }}>{pay.or_no || '-'}</TableCell>
        <TableCell>{pay.payment_date}</TableCell>
        <TableCell align="right">₱{parseFloat(pay.amount).toLocaleString('en-PH', { minimumFractionDigits: 2 })}</TableCell>
        <TableCell>{pay.notes || '-'}</TableCell>
        <TableCell align="right">
          <IconButton size="small" onClick={() => setEditOpen(true)}>
            <Edit fontSize="small" />
          </IconButton>
        </TableCell>
      </TableRow>
      {particulars.length > 0 && (
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ margin: 1 }}>
                <Typography variant="caption" gutterBottom component="div" fontWeight="bold" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                  Charges Breakdown
                </Typography>
                <Table size="small" aria-label="purchases">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Item</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {particulars.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell component="th" scope="row">
                          {item.item}
                        </TableCell>
                        <TableCell align="right">₱{parseFloat(item.amount).toLocaleString('en-PH', { minimumFractionDigits: 2 })}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
      <Dialog open={editOpen} onClose={() => !saving && setEditOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit Payment</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="OR No."
              size="small"
              value={form.or_no}
              onChange={(e) => setForm({ ...form, or_no: e.target.value })}
              fullWidth
            />
            <TextField
              label="Payment Date"
              type="date"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={form.payment_date}
              onChange={(e) => setForm({ ...form, payment_date: e.target.value })}
              fullWidth
            />
            <TextField
              label="Amount"
              type="number"
              size="small"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              fullWidth
            />
            <TextField
              label="Notes"
              size="small"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              fullWidth
              multiline
              minRows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => !saving && setEditOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const Permits = () => {
  // Alert state
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const [businesses, setBusinesses] = useState([]);
  const [permits, setPermits] = useLocalStorage('bplo_permits', []);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ businessId: '', permitNo: '', permitType: '', issueDate: new Date().toISOString().slice(0,10), expiryDate: '', status: 'Active', grossBreakdown: [] });
  const [grossItem, setGrossItem] = useState({ label: '', amount: '' });
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paying, setPaying] = useState(null);
  const [payingApplication, setPayingApplication] = useState(null);
  const [paymentForm, setPaymentForm] = useState({ amount: '', payment_date: new Date().toISOString().slice(0,10), or_no: '', notes: '' });
  const [defaultCharges, setDefaultCharges] = useState([]);
  const [billingCharges, setBillingCharges] = useState([]);
  const [chargesSearch, setChargesSearch] = useState('');
  const [availableCharges, setAvailableCharges] = useState([]);
  const [modalBillingCharges, setModalBillingCharges] = useState([]);
  const [modalChargesSearch, setModalChargesSearch] = useState('');
  const [modalAvailableCharges, setModalAvailableCharges] = useState([]);
  const [modalOccupations, setModalOccupations] = useState([]);
  const [modalOccupationsError, setModalOccupationsError] = useState(null);
  const [natureOptions, setNatureOptions] = useState([]);
  const [natureRecords, setNatureRecords] = useState([]);
  const [showAppDetails, setShowAppDetails] = useState(false);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');
  const [applicationTypes, setApplicationTypes] = useState([]);
  const [appTypeRecords, setAppTypeRecords] = useState([]);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [showCustomChargeModal, setShowCustomChargeModal] = useState(false);
  const [customChargeForm, setCustomChargeForm] = useState({ item: '', amount: '', percentage: 100 });
  const [customChargeItems, setCustomChargeItems] = useState([]);
  const [customChargePercentages, setCustomChargePercentages] = useState([]);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingApplication, setViewingApplication] = useState(null);
  const [viewingPayments, setViewingPayments] = useState([]);
  const [viewingApplicant, setViewingApplicant] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showTaxTable, setShowTaxTable] = useState(false);
  const [taxSettings, setTaxSettings] = useState([]);
  const [loadingTax, setLoadingTax] = useState(false);
  const [showGrossTaxModal, setShowGrossTaxModal] = useState(false);
  const [grossTaxForm, setGrossTaxForm] = useState({ label: '', grossSale: 0, grossTax: '' });
  const [selectedGrossItems, setSelectedGrossItems] = useState([]);
  const [showUnpayConfirm, setShowUnpayConfirm] = useState(false);
  const [unpayTargets, setUnpayTargets] = useState([]);

  const handleSelectGross = (index) => {
    if (selectedGrossItems.includes(index)) {
      setSelectedGrossItems(selectedGrossItems.filter(i => i !== index));
    } else {
      setSelectedGrossItems([...selectedGrossItems, index]);
    }
  };

  const handleSelectAllGross = (event) => {
    if (event.target.checked) {
      const allIndices = payingApplication?.gross_breakdown?.map((_, idx) => idx) || [];
      setSelectedGrossItems(allIndices);
    } else {
      setSelectedGrossItems([]);
    }
  };

  const handleAddCombinedGrossTax = () => {
    if (selectedGrossItems.length < 2) return;
    
    const selectedItems = payingApplication.gross_breakdown.filter((_, idx) => selectedGrossItems.includes(idx));
    const totalGross = selectedItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    // Default to 'Sari-sari Store Gross Tax' when combining items
    const combinedLabel = 'Sari-sari Store Gross Tax';
    
    setGrossTaxForm({ 
      label: combinedLabel, 
      grossSale: totalGross, 
      grossTax: '' 
    });
    setShowGrossTaxModal(true);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Fishing Vessel States
  const [tabValue, setTabValue] = useState(0);
  const [fishingVessels, setFishingVessels] = useState([]);
  const [fishingGears, setFishingGears] = useState([]);
  const [showVesselForm, setShowVesselForm] = useState(false);
  const [newVessel, setNewVessel] = useState({
    registrationType: 'New',
    type: 'Motorized',
    name: '',
    homeport: '',
    placeBuilt: '',
    yearBuilt: '',
    material: 'Wood',
    engines: []
  });
  const [newEngine, setNewEngine] = useState({ name: '', horsepower: '', serial: '' });
  const [showEngineForm, setShowEngineForm] = useState(false);
  const [newGear, setNewGear] = useState({ name: '', category: '', quantity: '' });
  const [showGearForm, setShowGearForm] = useState(false);
  const [fishingGearsRef, setFishingGearsRef] = useState([]);
  const [fishingGearCategories, setFishingGearCategories] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [newVehicle, setNewVehicle] = useState({ vehicleType: 'Motorcycle', serviceType: 'Private', brandModel: '', plateNumber: '', stickerNumber: '', licenseNumber: '', licenseExpiry: '' });
  const [showVehicleForm, setShowVehicleForm] = useState(false);

  const groupedGears = useMemo(() => {
    return fishingGearsRef.reduce((acc, gear) => {
      const cat = gear.category || 'Uncategorized';
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(gear);
      return acc;
    }, {});
  }, [fishingGearsRef]);

  const handleGearToggle = (gear, checked) => {
    if (checked) {
      setFishingGears([...fishingGears, { gearId: gear.id, name: gear.name, category: gear.category, quantity: 1 }]);
    } else {
      setFishingGears(fishingGears.filter(g => g.gearId !== gear.id));
    }
  };

  const handleGearQuantityChange = (gearId, qty) => {
    setFishingGears(fishingGears.map(g => 
      g.gearId === gearId ? { ...g, quantity: qty } : g
    ));
  };

  // Fetch data on mount
  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const [applicationsRes, natureRes, applicantsRes, gearsRes, typesRes, itemsRes, percentagesRes] = await Promise.all([
          bploService.getApplications(),
          bploService.getNatureOfBusiness(),
          bploService.getApplicants(),
          bploService.getFishingGears(),
          bploService.getApplicationTypes(),
          bploService.getCustomChargeItems(),
          bploService.getCustomChargePercentages()
        ]);

        if (!mounted) return;

        if (applicationsRes.success) {
          const mapped = (applicationsRes.data || []).map(a => ({
            id: a.id,
            businessId: a.applicant_id,
            permitNo: a.application_no,
            permitType: a.application_type,
            issueDate: a.issue_date,
            expiryDate: a.expiry_date,
            status: a.status,
            typeOfBusiness: a.type_of_business,
            businessName: a.business_name,
            businessAddress: a.business_address,
            natureOfBusiness: a.nature_of_business,
            capital: a.capital,
            gross: a.gross,
            grossBreakdown: a.gross_breakdown,
            vehicleType: a.vehicle_type,
            serviceType: a.service_type,
            brandModel: a.brand_model,
            plateNumber: a.plate_number,
            stickerNumber: a.sticker_number,
            licenseNumber: a.license_number,
            licenseExpiry: a.license_expiry,
            fishingVessels: a.fishing_vessels,
            fishingGears: a.fishing_gears,
            billingCharges: a.billing_charges,
            occupations: a.occupations
          }));
          setPermits(mapped);
        }

        if (natureRes.success) {
          setNatureOptions((natureRes.data || []).map(d => d.name));
          setNatureRecords(natureRes.data || []);
        }
        if (applicantsRes.success) setBusinesses(applicantsRes.data);
        if (gearsRes.success) setFishingGearsRef(gearsRes.data || []);
        if (typesRes.success) {
          setApplicationTypes((typesRes.data || []).map(d => d.name));
          setAppTypeRecords(typesRes.data || []);
        }
        if (itemsRes.success) {
          setCustomChargeItems(itemsRes.data || []);
          setModalAvailableCharges(itemsRes.data || []);
        }
        if (percentagesRes.success) setCustomChargePercentages(percentagesRes.data || []);
      } catch (error) {
        console.error("Failed to load data", error);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // When opening modal for "Fishing Vessel and Gear Registration", ensure gears are fresh if empty
  useEffect(() => {
      if (showModal && form.permitType === 'Fishing Vessel and Gear Registration' && fishingGearsRef.length === 0) {
          (async () => {
              const res = await bploService.getFishingGears();
              if (res.success) setFishingGearsRef(res.data || []);
          })();
      }
  }, [showModal, form.permitType, fishingGearsRef.length]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return (permits || []).filter(p => {
      const biz = businesses.find(b => b.id === p.businessId);
      const bizName = biz ? biz.name : '';
      const matchesSearch = !s || String(p.permitNo||'').toLowerCase().includes(s) || String(p.permitType||'').toLowerCase().includes(s) || String(bizName).toLowerCase().includes(s);
      const matchesType = filterType === 'All' || p.permitType === filterType;
      const matchesStatus = filterStatus === 'All' || 
                            (filterStatus === 'Unpaid' && p.status !== 'Paid') ||
                            p.status === filterStatus;
      return matchesSearch && matchesType && matchesStatus;
    });
  }, [permits, businesses, search, filterType, filterStatus]);

  const typeSummary = useMemo(() => {
    const counts = {};
    (permits || []).forEach(p => {
      counts[p.permitType] = (counts[p.permitType] || 0) + 1;
    });
    return counts;
  }, [permits]);

  const reset = () => { 
    setForm({ businessId: '', permitNo: '', permitType: '', issueDate: new Date().toISOString().slice(0,10), expiryDate: '', status: 'Active', grossBreakdown: [] }); 
    setEditing(null);
    setFishingVessels([]);
    setFishingGears([]);
    setVehicles([]);
    setShowVehicleForm(false);
    setGrossItem({ label: '', amount: '' });
    setModalBillingCharges([]);
    setModalOccupations([]);
  };

  const addGrossItem = () => {
    if (!grossItem.label || !grossItem.amount) return;
    const newBreakdown = [...(form.grossBreakdown || []), { ...grossItem, id: Date.now() }];
    const total = newBreakdown.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    setForm({ ...form, grossBreakdown: newBreakdown, gross: total });
    setGrossItem({ label: '', amount: '' });
  };

  const removeGrossItem = (id) => {
    const newBreakdown = (form.grossBreakdown || []).filter(item => item.id !== id);
    const total = newBreakdown.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    setForm({ ...form, grossBreakdown: newBreakdown, gross: total });
  };

  const save = async () => {
    if (!form.businessId || !form.permitNo) return;
    
    if (form.permitType === 'Occupation/Calling' && modalOccupations.length === 0) {
        setModalOccupationsError('Please select at least one occupation');
        return;
    }
    setModalOccupationsError(null);
    
    let extraData = {};
    if (form.permitType === 'Fishing Vessel and Gear Registration') {
      extraData = {
        fishingVessels,
        fishingGears
      };
    } else if (form.permitType === 'Motorcycle and Tricycle Permit') {
      extraData = {
        vehicles
      };
    }

    const payload = {
        applicant_id: form.businessId,
        application_no: form.permitNo,
        application_type: form.permitType,
        issue_date: form.issueDate,
        expiry_date: form.expiryDate || null,
        status: form.status,
        type_of_business: (form.permitType === 'Business Permit' || form.permitType === 'Motorcycle and Tricycle Permit') ? form.typeOfBusiness : null,
        business_name: form.permitType === 'Business Permit' ? form.businessName : null,
        business_address: form.permitType === 'Business Permit' ? form.businessAddress : null,
        nature_of_business: form.permitType === 'Business Permit' ? form.natureOfBusiness : null,
        capital: form.permitType === 'Business Permit' && form.typeOfBusiness === 'New' ? (form.capital ? parseFloat(form.capital) : null) : null,
        gross: form.permitType === 'Business Permit' ? (parseFloat(form.gross) || null) : null,
        gross_breakdown: form.permitType === 'Business Permit' ? (form.grossBreakdown || []) : [],
        
        vehicles: form.permitType === 'Motorcycle and Tricycle Permit' ? vehicles : null,
        // Backward compatibility: Populate flat fields with first vehicle
        vehicle_type: form.permitType === 'Motorcycle and Tricycle Permit' ? (vehicles[0]?.vehicleType || null) : null,
        service_type: form.permitType === 'Motorcycle and Tricycle Permit' ? (vehicles[0]?.serviceType || null) : null,
        brand_model: form.permitType === 'Motorcycle and Tricycle Permit' ? (vehicles[0]?.brandModel || null) : null,
        plate_number: form.permitType === 'Motorcycle and Tricycle Permit' ? (vehicles[0]?.plateNumber || null) : null,
        sticker_number: form.permitType === 'Motorcycle and Tricycle Permit' ? (vehicles[0]?.stickerNumber || null) : null,
        license_number: form.permitType === 'Motorcycle and Tricycle Permit' ? (vehicles[0]?.licenseNumber || null) : null,
        license_expiry: form.permitType === 'Motorcycle and Tricycle Permit' ? (vehicles[0]?.licenseExpiry || null) : null,
        
        fishing_vessels: form.permitType === 'Fishing Vessel and Gear Registration' ? fishingVessels : null,
        fishing_gears: form.permitType === 'Fishing Vessel and Gear Registration' ? fishingGears : null,
        billing_charges: (form.permitType === 'Motorcycle and Tricycle Permit' || form.permitType === 'Fishing Vessel and Gear Registration') ? modalBillingCharges : null,
        occupations_json: form.permitType === 'Occupation/Calling' ? modalOccupations : null
    };

    let success = false;
    let data = null;

    if (editing) {
      const res = await bploService.updateApplication(editing, payload);
      if (res.success) {
          success = true;
          data = res.data;
          setPermits(permits.map(p => p.id === editing ? { 
              ...p, 
              ...form, 
              ...extraData, 
              ...data, 
              grossBreakdown: form.grossBreakdown,
              occupations: modalOccupations
          } : p));
          showSnackbar('Application updated successfully', 'success');
      } else {
          showSnackbar('Failed to update application', 'error');
      }
    } else {
      const res = await bploService.createApplication(payload);
      if (res.success) {
          success = true;
          data = res.data;
          const newRecord = { 
              ...form, 
              ...extraData, 
              id: data.id, 
              grossBreakdown: form.grossBreakdown,
              occupations: modalOccupations
          };
          setPermits([newRecord, ...permits]);
          showSnackbar('Application created successfully', 'success');
      } else {
          showSnackbar('Failed to create application', 'error');
      }
    }
    
    if (success) {
        setShowModal(false);
        reset();
    }
  };

  const remove = (id) => setPermits(permits.filter(p => p.id !== id));
  const businessName = (id) => formatName(businesses.find(b => b.id === id)?.name || '');

  const openView = async (p) => {
    setViewingApplication(p);
    const applicant = businesses.find(b => b.id === p.businessId);
    setViewingApplicant(applicant || null);
    
    // Fetch payments
    const { success, data } = await bploService.getPaymentsByApplication(p.id);
    if (success) {
      setViewingPayments(data);
    } else {
      setViewingPayments([]);
    }
    setShowViewModal(true);
  };

  const openPay = async (p) => {
    setPaying(p);
    setShowTaxTable(false);
    setPayingApplication(null);
    setSelectedGrossItems([]);
    setPaymentForm({ amount: '', payment_date: new Date().toISOString().slice(0,10), or_no: '', notes: '' });

    const { success: appSuccess, data: appData } = await bploService.getApplications();
    let currentApp = null;
    if (appSuccess) {
      currentApp = (appData || []).find(a => a.id === p.id);
      setPayingApplication(currentApp || null);
    }

      // Dynamic Charge Fetching
      const typeObj = (appTypeRecords || []).find(t => t.name === p.permitType);
      
      if ((p.permitType === 'Motorcycle and Tricycle Permit' || p.permitType === 'Fishing Vessel and Gear Registration') && currentApp?.billing_charges && currentApp.billing_charges.length > 0) {
          setBillingCharges(currentApp.billing_charges);
          setDefaultCharges(currentApp.billing_charges);
          const { success: allSuccess, data: allData } = await bploService.getCharges();
          setAvailableCharges(allSuccess ? (allData || []) : []);
      } else if (typeObj) {
        let natureId = null;
        if (p.permitType === 'Business Permit' && currentApp?.nature_of_business) {
          const natureObj = (natureRecords || []).find(n => n.name === currentApp.nature_of_business);
          if (natureObj) natureId = natureObj.id;
        }
         
        (async () => {
            let defaults = [];
            if (p.permitType === 'Business Permit') {
              if (natureId) {
                const { success, data } = await bploService.getDefaultChargesByNatureId(natureId);
                defaults = success ? ((data || []).map(c => ({ item: c.item, amount: c.amount }))) : [];
              } else {
                defaults = [];
              }
            } else {
              const { success, data } = await bploService.getChargesByCriteria(typeObj.id, natureId);
              defaults = success ? (data || []) : [];
            }
            setDefaultCharges(defaults);
            setBillingCharges(defaults);
            const { success: allSuccess, data: allData } = await bploService.getCharges();
            setAvailableCharges(allSuccess ? (allData || []) : []);
        })();
      } else {
        setDefaultCharges([]);
        setBillingCharges([]);
        const { success: allSuccess, data: allData } = await bploService.getCharges();
        setAvailableCharges(allSuccess ? (allData || []) : []);
      }
    setChargesSearch('');
    setShowPaymentModal(true);
  };

  const toggleTaxTable = async () => {
    if (!showTaxTable) {
      setLoadingTax(true);
      const res = await bploService.getBusinessTaxSettings();
      if (res.success) setTaxSettings(res.data || []);
      setLoadingTax(false);
    }
    setShowTaxTable(prev => !prev);
  };

  const savePayment = async () => {
    if (!paying) return;
    const total = (billingCharges||[]).reduce((sum, c) => sum + (parseFloat(c.amount || 0) * (c.quantity || 1)), 0);
    if (total <= 0) {
      showSnackbar('Total amount must be greater than 0', 'error');
      return;
    }
    const payload = {
      application_id: paying.id,
      applicant_id: paying.businessId,
      application_no: paying.permitNo,
      amount: parseFloat(total),
      payment_date: paymentForm.payment_date,
      or_no: paymentForm.or_no || null,
      notes: paymentForm.notes || null,
      status: 'Paid',
      particulars: billingCharges
    };
    const res = await bploService.createPayment(payload);
    if (res.success) {
      const updated = permits.map(x => x.id === paying.id ? { ...x, status: 'Paid' } : x);
      setPermits(updated);
      await bploService.updateApplication(paying.id, { status: 'Paid' });
      setShowPaymentModal(false);
      setPaying(null);
      showSnackbar('Payment processed successfully', 'success');
    } else {
      showSnackbar(res.error || 'Failed to process payment', 'error');
    }
  };

  const handlePermitTypeChange = async (e) => {
    const type = e.target.value;
    setForm(prev => ({ ...prev, permitType: type }));
    if (type) {
      const { success, data } = await bploService.generateNextApplicationNo(type);
      if (success && data) {
        setForm(prev => ({ ...prev, permitNo: data }));
      }
    }
  };

  const handleNatureChange = async (e) => {
    const nature = e.target.value;
    setForm(prev => ({ ...prev, natureOfBusiness: nature }));
    
    if (nature) {
      // Find the ID of the selected nature
      const selectedNature = natureRecords.find(n => n.name === nature);
      if (selectedNature) {
          const { success, data } = await bploService.getDefaultChargesByNatureId(selectedNature.id);
          if (success && data) {
              const defaults = data.map(c => ({
                  item: c.item,
                  amount: c.amount
              }));
              setModalBillingCharges(defaults);
          }
      }
    } else {
        setModalBillingCharges([]);
    }
  };

  const handleBulkUnpay = async () => {
    const paidIds = (unpayTargets && unpayTargets.length > 0) 
      ? unpayTargets 
      : (filtered || []).filter(p => p.status === 'Paid').map(p => p.id);
    if (paidIds.length === 0) {
      setShowUnpayConfirm(false);
      return;
    }
    let successCount = 0;
    let failCount = 0;
    for (const id of paidIds) {
      const res = await bploService.unpayApplication(id);
      if (res.success) {
        successCount += 1;
      } else {
        failCount += 1;
      }
    }
    if (successCount > 0) {
      const updated = permits.map(p => paidIds.includes(p.id) ? { ...p, status: 'Unpaid' } : p);
      setPermits(updated);
    }
    setShowUnpayConfirm(false);
    setUnpayTargets([]);
    if (failCount === 0 && successCount > 0) {
      showSnackbar(
        successCount === 1 
          ? 'Marked application as Unpaid' 
          : `Marked ${successCount} applications as Unpaid`,
        'success'
      );
    } else if (successCount === 0) {
      showSnackbar('Failed to unpay applications', 'error');
    } else {
      showSnackbar(`Unpaid ${successCount}, failed ${failCount}`, 'warning');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">Application Processing</Typography>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by application no, applicant, or type"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Search /></InputAdornment>
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Filter by Type</InputLabel>
              <Select
                value={filterType}
                label="Filter by Type"
                onChange={(e) => setFilterType(e.target.value)}
              >
                <MenuItem value="All">All Types</MenuItem>
                {applicationTypes.map((type) => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Filter by Status</InputLabel>
              <Select
                value={filterStatus}
                label="Filter by Status"
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="All">All Statuses</MenuItem>
                <MenuItem value="Unpaid">Unpaid</MenuItem>
                <MenuItem value="Paid">Paid</MenuItem>
                <MenuItem value="Released">Released</MenuItem>
                <MenuItem value="Expired">Expired</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
             <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
               <Button variant="outlined" size="small" onClick={() => setShowSummaryModal(true)}>
                 Summary
               </Button>
               <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.secondary', ml: 1 }}>
                 {filtered.length}
               </Typography>
             </Box>
          </Grid>
        </Grid>
      </Paper>

      <Dialog open={showSummaryModal} onClose={() => setShowSummaryModal(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ bgcolor: 'var(--bs-primary)', color: 'white' }}>
          Application Summary
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {applicationTypes.map(type => (
              <Box key={type} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, borderBottom: '1px solid #eee' }}>
                <Typography variant="body2">{type}</Typography>
                <Typography variant="body2" fontWeight="bold">{typeSummary[type] || 0}</Typography>
              </Box>
            ))}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, bgcolor: 'grey.100', mt: 1, borderRadius: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold">Total Records</Typography>
              <Typography variant="subtitle2" fontWeight="bold">{permits.length}</Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSummaryModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showUnpayConfirm} onClose={() => setShowUnpayConfirm(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Unpay</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will mark the selected application{unpayTargets.length > 1 ? 's' : ''} as Unpaid and remove their payments. Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowUnpayConfirm(false)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleBulkUnpay}>Confirm</Button>
        </DialogActions>
      </Dialog>

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ bgcolor: 'grey.100' }}>
            <TableRow>
              <TableCell>Application No</TableCell>
              <TableCell>Applicant</TableCell>
              <TableCell>Applicant Type</TableCell>
              <TableCell>Payment Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
             {loading ? (
               <TableRow>
                 <TableCell colSpan={5} align="center">
                   <CircularProgress />
                 </TableCell>
               </TableRow>
             ) : filtered.length === 0 ? (
               <TableRow><TableCell colSpan={5} align="center">No permits found</TableCell></TableRow>
             ) : filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(p => (
              <TableRow key={p.id}>
                <TableCell><strong>{p.permitNo}</strong></TableCell>
                <TableCell>{businessName(p.businessId)}</TableCell>
                <TableCell>
                  {p.permitType}
                  {p.permitType === 'Occupation/Calling' && (p.occupations || p.occupations_json)?.length > 0 && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      {(p.occupations || p.occupations_json).length} occupation(s)
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip label={p.status === 'Paid' ? 'Paid' : 'Unpaid'} color={p.status === 'Paid' ? 'success' : 'warning'} size="small" />
                </TableCell>
                <TableCell align="center">
                  <Button 
                    size="small" 
                    variant="outlined" 
                    startIcon={<Visibility />} 
                    onClick={() => openView(p)}
                    sx={{ mr: 1 }}
                  >
                    View
                  </Button>
                  {p.permitType !== 'Sanitary Permit' && (
                    <>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditing(p.id);
                          setForm({ ...p });
                          if (p.permitType === 'Fishing Vessel and Gear Registration') {
                            setFishingVessels(p.fishingVessels || []);
                            setFishingGears(p.fishingGears || []);
                          } else {
                            setFishingVessels([]);
                            setFishingGears([]);
                          }
                          if (p.permitType === 'Motorcycle and Tricycle Permit' || p.permitType === 'Fishing Vessel and Gear Registration') {
                            setModalBillingCharges(p.billingCharges || []);
                          } else {
                            setModalBillingCharges([]);
                          }
                          if (p.permitType === 'Occupation/Calling') {
                            setModalOccupations(p.occupations || []);
                          } else {
                            setModalOccupations([]);
                          }
                          if (p.permitType === 'Motorcycle and Tricycle Permit') {
                            setVehicles(p.vehicles || []);
                            if ((!p.vehicles || p.vehicles.length === 0) && p.vehicleType) {
                              setVehicles([{
                                vehicleType: p.vehicleType,
                                serviceType: p.serviceType,
                                brandModel: p.brandModel,
                                plateNumber: p.plateNumber,
                                stickerNumber: p.stickerNumber,
                                licenseNumber: p.licenseNumber,
                                licenseExpiry: p.licenseExpiry
                              }]);
                            }
                          }
                          setShowModal(true);
                        }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      {p.status !== 'Paid' && (
                        <Button size="small" variant="contained" sx={{ ml: 1 }} onClick={() => openPay(p)}>
                          Pay
                        </Button>
                      )}
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={filtered.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      <Dialog open={showModal} onClose={() => setShowModal(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: 'var(--bs-primary)', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
          {editing ? <Edit /> : <Add />}
          {editing ? 'Edit Application' : 'Create Application'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            {/* Applicant Section */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, textTransform: 'uppercase', fontWeight: 'bold' }}>
                  Applicant Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <AccessibleSelect
                      label="Applicant"
                      value={form.businessId}
                      onChange={(e)=>setForm({ ...form, businessId: e.target.value })}
                      options={businesses.map(b => ({ value: b.id, label: b.name }))}
                      fullWidth
                    />
                  </Grid>
                  {form.businessId && (() => {
                    const selectedB = businesses.find(b => b.id === form.businessId);
                    if (selectedB) {
                      return (
                        <Grid item xs={12}>
                          <Box sx={{ bgcolor: 'grey.50', p: 1.5, borderRadius: 1, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                            <Box>
                              <Typography variant="caption" color="text.secondary">BARANGAY</Typography>
                              <Typography variant="body2" fontWeight="medium">{selectedB.barangay}</Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary">CONTACT</Typography>
                              <Typography variant="body2" fontWeight="medium">{selectedB.mobile}</Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary">EMAIL</Typography>
                              <Typography variant="body2" fontWeight="medium">{selectedB.email}</Typography>
                            </Box>
                          </Box>
                        </Grid>
                      );
                    }
                    return null;
                  })()}
                </Grid>
              </Paper>
            </Grid>

            {/* Application Details Section */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, textTransform: 'uppercase', fontWeight: 'bold' }}>
                  Application Details
                </Typography>
                <Grid container spacing={2}>
                   <Grid item xs={12}>
                     <TextField 
                       fullWidth 
                       size="small"
                       label="Application No" 
                       value={form.permitNo} 
                       required 
                       InputProps={{
                         readOnly: true,
                         startAdornment: <InputAdornment position="start"><Badge /></InputAdornment>
                       }}
                     />
                   </Grid>
                   <Grid item xs={12}>
                     <AccessibleSelect
                       label="Application Type"
                       value={form.permitType || ''}
                       onChange={handlePermitTypeChange}
                       disabled={!!editing}
                       options={(() => {
                         const opts = ['Business Permit', 'Motorcycle and Tricycle Permit', 'Occupational Permit', 'Special Permit', 'Fishing Vessel and Gear Registration'];
                         if (form.permitType && !opts.includes(form.permitType)) opts.push(form.permitType);
                         return opts;
                       })()}
                       fullWidth
                     />
                   </Grid>
                   <Grid item xs={12}>
                     <TextField 
                       fullWidth 
                       size="small"
                       type="date" 
                       label="Issue Date" 
                       InputLabelProps={{ shrink: true }} 
                       value={form.issueDate} 
                       onChange={(e)=>setForm({ ...form, issueDate: e.target.value })} 
                     />
                   </Grid>
                </Grid>

                {/* Conditional Fields */}
                {form.permitType === 'Business Permit' && (
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12}>
                      <AccessibleSelect
                        label="Type of Business"
                        value={form.typeOfBusiness || 'New'}
                        onChange={(e)=>setForm({ ...form, typeOfBusiness: e.target.value })}
                        options={['New', 'Renewal']}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField 
                        fullWidth 
                        size="small"
                        label="Name of Business" 
                        value={form.businessName || ''} 
                        onChange={(e)=>setForm({ ...form, businessName: e.target.value })} 
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField 
                        fullWidth 
                        size="small"
                        label="Business Address" 
                        value={form.businessAddress || ''} 
                        onChange={(e)=>setForm({ ...form, businessAddress: e.target.value })} 
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <AccessibleSelect
                        label="Nature of Business"
                        value={form.natureOfBusiness || ''}
                        onChange={handleNatureChange}
                        options={natureOptions}
                        fullWidth
                      />
                    </Grid>
                    {form.typeOfBusiness === 'New' && (
                      <Grid item xs={12}>
                        <TextField 
                          fullWidth 
                          size="small"
                          type="number" 
                          label="Capital" 
                          value={form.capital || ''} 
                          onChange={(e)=>setForm({ ...form, capital: e.target.value })} 
                        />
                      </Grid>
                    )}

                    {/* Gross Sales Breakdown for New and Renewal */}
                    {(form.typeOfBusiness === 'New' || form.typeOfBusiness === 'Renewal') && (
                      <Grid item xs={12}>
                        <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                           <Typography variant="subtitle2" sx={{ mb: 2 }}>Gross Sales Breakdown</Typography>
                           {(form.grossBreakdown || []).map((item) => (
                             <Box key={item.id} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                               <TextField size="small" fullWidth value={item.label} InputProps={{ readOnly: true }} />
                               <TextField size="small" sx={{ width: 150 }} value={item.amount} InputProps={{ readOnly: true }} />
                               <IconButton size="small" color="error" onClick={() => removeGrossItem(item.id)}><Delete fontSize="small" /></IconButton>
                             </Box>
                           ))}
                           <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 2 }}>
                             <TextField 
                               size="small" 
                               label="Line of Business / Item" 
                               fullWidth 
                               value={grossItem.label} 
                               onChange={(e) => setGrossItem({ ...grossItem, label: e.target.value })}
                             />
                             <TextField 
                               size="small" 
                               label="Amount" 
                               type="number" 
                               sx={{ width: 150 }} 
                               value={grossItem.amount} 
                               onChange={(e) => setGrossItem({ ...grossItem, amount: e.target.value })}
                             />
                             <Button variant="contained" size="small" onClick={addGrossItem} startIcon={<Add />}>Add</Button>
                           </Box>
                           <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                             <Typography variant="subtitle2">Total Gross: {(parseFloat(form.gross) || 0).toLocaleString()}</Typography>
                           </Box>
                        </Paper>
                      </Grid>
                    )}

                    {form.typeOfBusiness === 'Renewal' && (
                      <Grid item xs={12}>
                        <TextField 
                          fullWidth 
                          size="small"
                          type="number" 
                          label="Gross" 
                          value={form.gross || ''} 
                          onChange={(e)=>setForm({ ...form, gross: e.target.value })}
                          InputProps={{
                            readOnly: (form.grossBreakdown && form.grossBreakdown.length > 0)
                          }}
                          helperText={form.grossBreakdown && form.grossBreakdown.length > 0 ? "Calculated from breakdown" : "Enter total gross or add breakdown items"}
                        />
                      </Grid>
                    )}
                  </Grid>
                )}

                    {form.permitType === 'Motorcycle and Tricycle Permit' && (
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12}>
                      <AccessibleSelect
                        label="Type of Permit"
                        value={form.typeOfBusiness || ''}
                        onChange={(e)=>setForm({ ...form, typeOfBusiness: e.target.value })}
                        options={['New', 'Renewal']}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="subtitle2">Vehicles: {vehicles.length}</Typography>
                            {!showVehicleForm && (
                                <Button variant="outlined" size="small" startIcon={<Add />} onClick={() => {
                                    setNewVehicle({ vehicleType: 'Motorcycle', serviceType: 'Private', brandModel: '', plateNumber: '', stickerNumber: '', licenseNumber: '', licenseExpiry: '' });
                                    setShowVehicleForm(true);
                                }}>Add Vehicle</Button>
                            )}
                        </Box>
                        
                        {!showVehicleForm ? (
                            <Stack spacing={1}>
                                {vehicles.length === 0 ? <Typography variant="body2" color="text.secondary" align="center">No vehicles added.</Typography> : 
                                vehicles.map((v, idx) => (
                                    <Paper key={idx} variant="outlined" sx={{ p: 1.5 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Box>
                                                <Typography variant="subtitle2">{v.vehicleType} - {v.brandModel}</Typography>
                                                <Typography variant="caption" color="text.secondary">{v.serviceType} | Plate: {v.plateNumber}</Typography>
                                            </Box>
                                            <IconButton size="small" color="error" onClick={() => setVehicles(vehicles.filter((_, i) => i !== idx))}>
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    </Paper>
                                ))}
                            </Stack>
                        ) : (
                            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                                <Typography variant="subtitle2" sx={{ mb: 2 }}>New Vehicle Details</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <AccessibleSelect
                                            label="Vehicle Type"
                                            value={newVehicle.vehicleType}
                                            onChange={(e) => setNewVehicle({...newVehicle, vehicleType: e.target.value})}
                                            options={['Motorcycle', 'Tricycle']}
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <AccessibleSelect
                                            label="Service Type"
                                            value={newVehicle.serviceType}
                                            onChange={(e) => setNewVehicle({...newVehicle, serviceType: e.target.value})}
                                            options={['Private', 'For Hire']}
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField fullWidth size="small" label="Brand/Model" value={newVehicle.brandModel} onChange={(e) => setNewVehicle({...newVehicle, brandModel: e.target.value})} />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth size="small" label="Plate Number" value={newVehicle.plateNumber} onChange={(e) => setNewVehicle({...newVehicle, plateNumber: e.target.value})} />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth size="small" label="Sticker Number" value={newVehicle.stickerNumber} onChange={(e) => setNewVehicle({...newVehicle, stickerNumber: e.target.value})} />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth size="small" label="Driver's License No." value={newVehicle.licenseNumber} onChange={(e) => setNewVehicle({...newVehicle, licenseNumber: e.target.value})} />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth size="small" type="date" label="License Expiry" InputLabelProps={{ shrink: true }} value={newVehicle.licenseExpiry} onChange={(e) => setNewVehicle({...newVehicle, licenseExpiry: e.target.value})} />
                                    </Grid>
                                    <Grid item xs={12} sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                        <Button onClick={() => setShowVehicleForm(false)}>Cancel</Button>
                                        <Button variant="contained" onClick={() => {
                                            setVehicles([...vehicles, newVehicle]);
                                            setShowVehicleForm(false);
                                        }}>Add</Button>
                                    </Grid>
                                </Grid>
                            </Paper>
                        )}
                    </Grid>
                  </Grid>
                )}

                    {/* Billing Charges Section for MC/Fishing */}
                    {(form.permitType === 'Business Permit' || form.permitType === 'Motorcycle and Tricycle Permit' || form.permitType === 'Fishing Vessel and Gear Registration') && (
                      <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                             <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                               <Typography variant="subtitle2">Billing Charges</Typography>
                               <Chip label={`${(modalBillingCharges||[]).length} items`} size="small" />
                             </Box>
                             
                             {/* Add Charge Input */}
                             <Box sx={{ display: 'flex', gap: 1, mb: 2, position: 'relative' }}>
                               <Box sx={{ flex: 1, position: 'relative' }}>
                                 <TextField
                                   size="small"
                                   fullWidth
                                   placeholder="Search charge item..."
                                   value={modalChargesSearch}
                                   onChange={(e) => setModalChargesSearch(e.target.value)}
                                 />
                                 {modalChargesSearch.trim().length > 0 && (
                                    <Paper elevation={4} sx={{ mt: 0.5, maxHeight: 200, overflow: 'auto', position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10 }}>
                                      <List dense>
                                        {(modalAvailableCharges||[]).filter(c => String(c.item||'').toLowerCase().includes(modalChargesSearch.trim().toLowerCase())).map((c, i) => (
                                          <ListItem key={i} button onClick={() => {
                                              setModalBillingCharges([...modalBillingCharges, { item: c.item, amount: parseFloat(c.amount) }]);
                                              setModalChargesSearch('');
                                          }}>
                                            <ListItemText primary={c.item} secondary={`₱${parseFloat(c.amount).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`} />
                                          </ListItem>
                                        ))}
                                        {(modalAvailableCharges||[]).filter(c => String(c.item||'').toLowerCase().includes(modalChargesSearch.trim().toLowerCase())).length === 0 && (
                                          <ListItem><ListItemText primary="No matches found" /></ListItem>
                                        )}
                                      </List>
                                    </Paper>
                                 )}
                               </Box>
                             </Box>

                             {/* Charges List */}
                             <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                                <Table size="small">
                                  <TableHead sx={{ bgcolor: 'white' }}>
                                     <TableRow>
                                        <TableCell>Item</TableCell>
                                        <TableCell align="right">Amount</TableCell>
                                        <TableCell width={40}></TableCell>
                                     </TableRow>
                                  </TableHead>
                                  <TableBody>
                                     {(modalBillingCharges||[]).length === 0 ? (
                                       <TableRow><TableCell colSpan={3} align="center" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>No charges added</TableCell></TableRow>
                                     ) : (modalBillingCharges||[]).map((c, idx) => (
                                        <TableRow key={idx}>
                                           <TableCell>{c.item}</TableCell>
                                           <TableCell align="right">₱{parseFloat(c.amount).toLocaleString('en-PH', { minimumFractionDigits: 2 })}</TableCell>
                                           <TableCell>
                                             <IconButton size="small" color="error" onClick={() => setModalBillingCharges(modalBillingCharges.filter((_, i) => i !== idx))}>
                                               <Delete fontSize="small" />
                                             </IconButton>
                                           </TableCell>
                                        </TableRow>
                                     ))}
                                     <TableRow sx={{ bgcolor: 'grey.100' }}>
                                        <TableCell><strong>Total</strong></TableCell>
                                        <TableCell align="right"><strong>₱{(modalBillingCharges||[]).reduce((s,c)=>s+parseFloat(c.amount||0),0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}</strong></TableCell>
                                        <TableCell></TableCell>
                                     </TableRow>
                                  </TableBody>
                                </Table>
                             </TableContainer>
                          </Paper>
                        </Grid>
                      </Grid>
                    )}

                    {form.permitType === 'Motorcycle and Tricycle Permit' && (
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12}>
                      <AccessibleSelect
                        label="Type of Permit"
                        value={form.typeOfBusiness || ''}
                        onChange={(e)=>setForm({ ...form, typeOfBusiness: e.target.value })}
                        options={['New', 'Renewal']}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="subtitle2">Vehicles: {vehicles.length}</Typography>
                            {!showVehicleForm && (
                                <Button variant="outlined" size="small" startIcon={<Add />} onClick={() => {
                                    setNewVehicle({ vehicleType: 'Motorcycle', serviceType: 'Private', brandModel: '', plateNumber: '', stickerNumber: '', licenseNumber: '', licenseExpiry: '' });
                                    setShowVehicleForm(true);
                                }}>Add Vehicle</Button>
                            )}
                        </Box>
                        
                        {!showVehicleForm ? (
                            <Stack spacing={1}>
                                {vehicles.length === 0 ? <Typography variant="body2" color="text.secondary" align="center">No vehicles added.</Typography> : 
                                vehicles.map((v, idx) => (
                                    <Paper key={idx} variant="outlined" sx={{ p: 1.5 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Box>
                                                <Typography variant="subtitle2">{v.vehicleType} - {v.brandModel}</Typography>
                                                <Typography variant="caption" color="text.secondary">{v.serviceType} | Plate: {v.plateNumber}</Typography>
                                            </Box>
                                            <IconButton size="small" color="error" onClick={() => setVehicles(vehicles.filter((_, i) => i !== idx))}>
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    </Paper>
                                ))}
                            </Stack>
                        ) : (
                            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                                <Typography variant="subtitle2" sx={{ mb: 2 }}>New Vehicle Details</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <AccessibleSelect
                                            label="Vehicle Type"
                                            value={newVehicle.vehicleType}
                                            onChange={(e) => setNewVehicle({...newVehicle, vehicleType: e.target.value})}
                                            options={['Motorcycle', 'Tricycle']}
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <AccessibleSelect
                                            label="Service Type"
                                            value={newVehicle.serviceType}
                                            onChange={(e) => setNewVehicle({...newVehicle, serviceType: e.target.value})}
                                            options={['Private', 'For Hire']}
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField fullWidth size="small" label="Brand/Model" value={newVehicle.brandModel} onChange={(e) => setNewVehicle({...newVehicle, brandModel: e.target.value})} />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth size="small" label="Plate Number" value={newVehicle.plateNumber} onChange={(e) => setNewVehicle({...newVehicle, plateNumber: e.target.value})} />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth size="small" label="Sticker Number" value={newVehicle.stickerNumber} onChange={(e) => setNewVehicle({...newVehicle, stickerNumber: e.target.value})} />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth size="small" label="Driver's License No." value={newVehicle.licenseNumber} onChange={(e) => setNewVehicle({...newVehicle, licenseNumber: e.target.value})} />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth size="small" type="date" label="License Expiry" InputLabelProps={{ shrink: true }} value={newVehicle.licenseExpiry} onChange={(e) => setNewVehicle({...newVehicle, licenseExpiry: e.target.value})} />
                                    </Grid>
                                    <Grid item xs={12} sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                        <Button onClick={() => setShowVehicleForm(false)}>Cancel</Button>
                                        <Button variant="contained" onClick={() => {
                                            setVehicles([...vehicles, newVehicle]);
                                            setShowVehicleForm(false);
                                        }}>Add</Button>
                                    </Grid>
                                </Grid>
                            </Paper>
                        )}
                    </Grid>
                  </Grid>
                )}

                {form.permitType === 'Fishing Vessel and Gear Registration' && (
                  <Box sx={{ mt: 2 }}>
                    <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                      <Tab label="Fishing Vessels" />
                      <Tab label="Fishing Gears" />
                    </Tabs>

                    {tabValue === 0 && (
                      <Box>
                        {!showVesselForm ? (
                          <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                              <Typography variant="subtitle2">Registered Vessels: {fishingVessels.length}</Typography>
                              <Button variant="outlined" size="small" startIcon={<Add />} onClick={() => { 
                                setNewVessel({ registrationType: 'New', type: 'Motorized', name: '', homeport: '', placeBuilt: '', yearBuilt: '', material: 'Wood', engines: [] }); 
                                setShowVesselForm(true); 
                              }}>
                                Add Vessel
                              </Button>
                            </Box>
                            {fishingVessels.length === 0 ? (
                              <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>No vessels added.</Typography>
                            ) : (
                              <Stack spacing={1}>
                                {fishingVessels.map((v, idx) => (
                                  <Paper key={idx} variant="outlined" sx={{ p: 1.5 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                      <Box>
                                        <Typography variant="subtitle2">{v.name} ({v.type})</Typography>
                                        <Typography variant="caption" color="text.secondary">Reg: {v.registrationType} | Mat: {v.material}</Typography>
                                        {v.engines.length > 0 && (
                                          <Typography variant="caption" display="block">Engines: {v.engines.map(e => `${e.name} (${e.horsepower}HP)`).join(', ')}</Typography>
                                        )}
                                      </Box>
                                      <IconButton size="small" color="error" onClick={() => setFishingVessels(fishingVessels.filter((_, i) => i !== idx))}><Delete fontSize="small" /></IconButton>
                                    </Box>
                                  </Paper>
                                ))}
                              </Stack>
                            )}
                          </Box>
                        ) : (
                          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                            <Typography variant="subtitle2" sx={{ mb: 2 }}>New Vessel Details</Typography>
                            <Grid container spacing={2}>
                              <Grid item xs={12}>
                                <AccessibleSelect
                                  label="Registration Type"
                                  value={newVessel.registrationType}
                                  onChange={(e) => setNewVessel({...newVessel, registrationType: e.target.value})}
                                  options={['New', 'Renew']}
                                  fullWidth
                                />
                              </Grid>
                              <Grid item xs={12}>
                                <AccessibleSelect
                                  label="Type"
                                  value={newVessel.type}
                                  onChange={(e) => setNewVessel({...newVessel, type: e.target.value})}
                                  options={['Motorized', 'Non-motorized']}
                                  fullWidth
                                />
                              </Grid>
                              <Grid item xs={12}>
                                <TextField fullWidth size="small" label="Name of Fishing Vessel" value={newVessel.name} onChange={(e) => setNewVessel({...newVessel, name: e.target.value})} />
                              </Grid>
                              <Grid item xs={12}>
                                <TextField fullWidth size="small" label="Homeport (Optional)" value={newVessel.homeport} onChange={(e) => setNewVessel({...newVessel, homeport: e.target.value})} />
                              </Grid>
                              <Grid item xs={12}>
                                <TextField fullWidth size="small" label="Place Built (Optional)" value={newVessel.placeBuilt} onChange={(e) => setNewVessel({...newVessel, placeBuilt: e.target.value})} />
                              </Grid>
                              <Grid item xs={12}>
                                <TextField fullWidth size="small" label="Month & Year Built (Optional)" value={newVessel.yearBuilt} onChange={(e) => setNewVessel({...newVessel, yearBuilt: e.target.value})} placeholder="e.g. Jan 2020" />
                              </Grid>
                              <Grid item xs={12}>
                                <AccessibleSelect
                                  label="Material Used"
                                  value={newVessel.material}
                                  onChange={(e) => setNewVessel({...newVessel, material: e.target.value})}
                                  options={['Wood', 'Fiber Glass', 'Composite']}
                                  fullWidth
                                />
                              </Grid>
                              
                              <Grid item xs={12}>
                                <Divider sx={{ my: 1 }}><Typography variant="caption">ENGINE DETAILS</Typography></Divider>
                                {newVessel.engines.map((eng, idx) => (
                                  <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', bgcolor: 'white', p: 1, borderRadius: 1, mb: 1, border: '1px solid #eee' }}>
                                    <Typography variant="body2">{eng.name} - {eng.horsepower}HP {eng.serial && `(SN: ${eng.serial})`}</Typography>
                                    <IconButton size="small" color="error" onClick={() => setNewVessel({...newVessel, engines: newVessel.engines.filter((_, i) => i !== idx)})}><Delete fontSize="small" /></IconButton>
                                  </Box>
                                ))}
                                {showEngineForm ? (
                                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
                                    <TextField size="small" label="Name" value={newEngine.name} onChange={(e) => setNewEngine({...newEngine, name: e.target.value})} sx={{ flex: 2 }} />
                                    <TextField size="small" label="HP" value={newEngine.horsepower} onChange={(e) => setNewEngine({...newEngine, horsepower: e.target.value})} sx={{ flex: 1 }} />
                                    <TextField size="small" label="Serial (Opt)" value={newEngine.serial} onChange={(e) => setNewEngine({...newEngine, serial: e.target.value})} sx={{ flex: 1 }} />
                                    <Button variant="contained" size="small" onClick={() => {
                                      if (newEngine.name && newEngine.horsepower) {
                                        setNewVessel({...newVessel, engines: [...newVessel.engines, newEngine]});
                                        setNewEngine({ name: '', horsepower: '', serial: '' });
                                        setShowEngineForm(false);
                                      }
                                    }}>OK</Button>
                                    <Button size="small" onClick={() => setShowEngineForm(false)}>X</Button>
                                  </Box>
                                ) : (
                                  <Button size="small" startIcon={<Add />} onClick={() => setShowEngineForm(true)}>Add Engine</Button>
                                )}
                              </Grid>

                              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                <Button onClick={() => setShowVesselForm(false)}>Cancel</Button>
                                <Button variant="contained" onClick={() => {
                                  if (newVessel.name) {
                                    setFishingVessels([...fishingVessels, newVessel]);
                                    setShowVesselForm(false);
                                  }
                                }}>Save Vessel</Button>
                              </Grid>
                            </Grid>
                          </Paper>
                        )}
                      </Box>
                    )}

                    {tabValue === 1 && (
                      <Box>
                        <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                          {fishingGearsRef.length === 0 ? (
                            <Typography color="text.secondary" align="center" sx={{ py: 2 }}>No fishing gears available. Please manage gears in Settings.</Typography>
                          ) : (
                            Object.entries(groupedGears).map(([category, gears]) => (
                              <Box key={category} sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ bgcolor: 'grey.100', p: 1, borderRadius: 1, fontWeight: 'bold' }}>{category}</Typography>
                                <Grid container spacing={1} sx={{ mt: 0.5, px: 1 }}>
                                  {gears.map(gear => {
                                    const isSelected = fishingGears.some(g => g.gearId === gear.id);
                                    const selectedGear = fishingGears.find(g => g.gearId === gear.id);
                                    return (
                                      <Grid item xs={12} key={gear.id}>
                                         <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #eee', pb: 0.5 }}>
                                            <FormControlLabel
                                              control={
                                                <Checkbox 
                                                  checked={isSelected}
                                                  onChange={(e) => handleGearToggle(gear, e.target.checked)}
                                                  size="small"
                                                />
                                              }
                                              label={String(gear.name)}
                                              sx={{ mr: 1, flex: 1, '& .MuiTypography-root': { fontSize: '0.875rem' } }}
                                            />
                                            {isSelected && (
                                              <TextField
                                                type="number"
                                                size="small"
                                                variant="standard"
                                                placeholder="Qty"
                                                value={selectedGear?.quantity || ''}
                                                onChange={(e) => handleGearQuantityChange(gear.id, e.target.value)}
                                                sx={{ width: 50 }}
                                                InputProps={{ inputProps: { min: 1 } }}
                                              />
                                            )}
                                         </Box>
                                      </Grid>
                                    );
                                  })}
                                </Grid>
                              </Box>
                            ))
                          )}
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}

                {form.permitType === 'Occupation/Calling' && (
                  <Box sx={{ mt: 2 }}>
                    <OccupationSelector 
                      value={modalOccupations} 
                      onChange={(val) => setModalOccupations(val)}
                      error={Boolean(modalOccupationsError)}
                      helperText={modalOccupationsError}
                    />
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowModal(false)}>Cancel</Button>
          <Button onClick={save} variant="contained" startIcon={<Save />}>Save Changes</Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        classes={{ paper: 'payment-process-modal' }} 
        open={showPaymentModal} 
        onClose={(event, reason) => { 
          if (showTaxTable && reason === 'backdropClick') return; 
          setShowPaymentModal(false); 
          setShowTaxTable(false); 
        }} 
        fullWidth 
        fullScreen={fullScreen} 
        maxWidth="lg"
        hideBackdrop={showTaxTable}
        PaperProps={{
          sx: showTaxTable 
            ? { 
                position: { md: 'fixed' }, 
                right: { md: 0 }, 
                top: { md: 0 }, 
                height: { md: '100vh' }, 
                width: { md: 'calc(100% - 360px)' } 
              } 
            : {}
        }}
      >
        <DialogTitle sx={{ py: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
              <Payment fontSize="medium" />
            </Avatar>
            <Typography variant="h5" fontWeight="bold" sx={{ color: 'text.primary' }}>Process Payment</Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
            <Grid container sx={{ height: '100%', m: 0 }}>
                <Grid item xs={12} md={8} sx={{ p: 3, borderRight: { md: '1px solid' }, borderColor: 'divider', overflowY: 'auto', maxHeight: { md: 'calc(100vh - 150px)' } }}>
                    {/* Applicant & Application Details Section */}
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Info color="primary" /> Application Details
                        </Typography>
                        
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                            <Grid container spacing={2}>
                                {/* Applicant Info */}
                                <Grid item xs={12} md={6}>
                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="caption" color="text.secondary" fontWeight="bold">APPLICANT NAME</Typography>
                                        <Typography variant="body1" fontWeight="medium">
                                            {businesses.find(b => b.id === paying?.businessId)?.name || 'N/A'}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="caption" color="text.secondary" fontWeight="bold">ADDRESS</Typography>
                                        <Typography variant="body2">
                                            {businesses.find(b => b.id === paying?.businessId)?.barangay || 'N/A'}
                                        </Typography>
                                    </Box>

                                </Grid>
                                
                                {/* Application Info */}
                                <Grid item xs={12} md={6}>
                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="caption" color="text.secondary" fontWeight="bold">APPLICATION NO.</Typography>
                                        <Typography variant="h6" color="primary.main" fontWeight="bold">
                                            {paying?.permitNo || 'N/A'}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" fontWeight="bold">PERMIT TYPE</Typography>
                                        <Typography variant="body2" fontWeight="medium">
                                            {paying?.permitType || 'N/A'}
                                        </Typography>
                                    </Box>

                                </Grid>

                                {/* Specific Details based on Permit Type */}
                                <Grid item xs={12}>
                                    <Divider sx={{ my: 1 }} />
                                    {paying?.permitType === 'Business Permit' && (
                                        <Box>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} md={6}>
                                                    <Typography variant="caption" color="text.secondary" fontWeight="bold">BUSINESS NAME</Typography>
                                                    <Typography variant="body2">{paying?.businessName || '-'}</Typography>
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <Typography variant="caption" color="text.secondary" fontWeight="bold">NATURE OF BUSINESS</Typography>
                                                    <Typography variant="body2">{paying?.natureOfBusiness || '-'}</Typography>
                                                </Grid>
                                            </Grid>


                                            {/* Gross Sales Breakdown Moved to Payment Entry Column */}

                                        </Box>
                                    )}
                                    {paying?.permitType === 'Fishing Vessel and Gear Registration' && (
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" fontWeight="bold">VESSELS</Typography>
                                            <Typography variant="body2">
                                                {Array.isArray(payingApplication?.fishing_vessels) ? `${payingApplication.fishing_vessels.length} Registered` : '0 Registered'}
                                            </Typography>
                                        </Box>
                                    )}
                                </Grid>
                            </Grid>
                        </Paper>
                    </Box>

                    {/* Charges Section */}
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Receipt color="primary" /> Charges Breakdown
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button 
                                  size="small" 
                                  variant="outlined" 
                                  startIcon={<Add />} 
                                  onClick={() => {
                                      setCustomChargeForm({ item: '', amount: '', percentage: 100 });
                                      setShowCustomChargeModal(true);
                                  }}
                              >
                                  Add Charge
                              </Button>
                              {paying?.permitType === 'Business Permit' && (
                                <Button 
                                    size="small" 
                                    variant={showTaxTable ? 'contained' : 'outlined'} 
                                    onClick={toggleTaxTable}
                                >
                                    Tax Table
                                </Button>
                              )}
                            </Box>
                        </Box>

                        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                            <Box sx={{ px: 2, py: 2, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Search available charges..."
                                    value={chargesSearch}
                                    onChange={(e)=>setChargesSearch(e.target.value)}
                                    InputProps={{ 
                                        startAdornment: <InputAdornment position="start"><Search fontSize="small" color="action" /></InputAdornment>,
                                        sx: { bgcolor: 'white' }
                                    }}
                                />
                                {chargesSearch.trim().length > 0 && (
                                    <Paper elevation={3} sx={{ mt: 1, maxHeight: 200, overflow: 'auto' }}>
                                        <List dense>
                                            {(availableCharges||[]).filter(c => String(c.item||'').toLowerCase().includes(chargesSearch.trim().toLowerCase())).map(c => (
                                                <ListItem key={c.id} secondaryAction={
                                                    <Button size="small" onClick={()=>{
                                                        setBillingCharges([...billingCharges, { item: c.item, amount: parseFloat(c.amount), quantity: 1 }]);
                                                        setChargesSearch('');
                                                    }}>Add</Button>
                                                }>
                                                    <ListItemText primary={c.item} secondary={`₱${parseFloat(c.amount).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`} />
                                                </ListItem>
                                            ))}
                                            {(availableCharges||[]).filter(c => String(c.item||'').toLowerCase().includes(chargesSearch.trim().toLowerCase())).length === 0 && (
                                                <ListItem><ListItemText primary="No matches found" /></ListItem>
                                            )}
                                        </List>
                                    </Paper>
                                )}
                            </Box>

                            <Table size="small">
                                <TableHead sx={{ bgcolor: 'grey.50' }}>
                                    <TableRow>
                                        <TableCell>Charge Item</TableCell>
                                        <TableCell width={80}>Qty</TableCell>
                                        <TableCell align="right">Amount</TableCell>
                                        <TableCell width={40}></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(billingCharges||[]).length === 0 ? (
                                        <TableRow><TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>No charges added</TableCell></TableRow>
                                    ) : (billingCharges||[]).map((c, idx) => (
                                        <TableRow key={`${c.item}-${idx}`}>
                                            <TableCell>{c.item}</TableCell>
                                            <TableCell>
                                                <TextField 
                                                    type="number" 
                                                    size="small" 
                                                    value={c.quantity || 1} 
                                                    onChange={(e) => {
                                                        const val = parseInt(e.target.value) || 1;
                                                        const newCharges = [...billingCharges];
                                                        newCharges[idx] = { ...newCharges[idx], quantity: val > 0 ? val : 1 };
                                                        setBillingCharges(newCharges);
                                                    }}
                                                    inputProps={{ min: 1, style: { padding: '4px 8px' } }}
                                                />
                                            </TableCell>
                                            <TableCell align="right">₱{(parseFloat(c.amount) * (c.quantity || 1)).toLocaleString('en-PH', { minimumFractionDigits: 2 })}</TableCell>
                                            <TableCell>
                                                <IconButton size="small" color="error" onClick={()=>setBillingCharges(billingCharges.filter((_, i) => i !== idx))}>
                                                    <Delete fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    <TableRow sx={{ bgcolor: 'primary.50' }}>
                                        <TableCell colSpan={2}><strong>Total Amount</strong></TableCell>
                                        <TableCell align="right">
                                            <strong>₱{((billingCharges||[]).reduce((s,c)=>s+(parseFloat(c.amount||0) * (c.quantity||1)),0)).toLocaleString('en-PH', { minimumFractionDigits: 2 })}</strong>
                                        </TableCell>
                                        <TableCell></TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </Paper>
                    </Box>
                </Grid>

                <Grid item xs={12} md={4} sx={{ bgcolor: 'grey.50', display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="h6" fontWeight="bold">Payment Entry</Typography>
                        <Typography variant="body2" color="text.secondary">Process payment for this application</Typography>
                    </Box>

                    <Box sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                        
                        {/* Vehicles for Motorcycle Permit */}
                        {paying?.permitType === 'Motorcycle and Tricycle Permit' && (
                            <Box sx={{ mb: 4 }}>
                                {payingApplication?.vehicles && Array.isArray(payingApplication.vehicles) && payingApplication.vehicles.length > 0 ? (
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
                                            REGISTERED VEHICLES ({payingApplication.vehicles.length})
                                        </Typography>
                                        <Stack spacing={1}>
                                            {payingApplication.vehicles.map((v, i) => (
                                                <Paper key={i} variant="outlined" sx={{ p: 1, bgcolor: 'grey.50' }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <Box>
                                                            <Typography variant="subtitle2" sx={{ lineHeight: 1.2 }}>{v.vehicleType} - {v.brandModel}</Typography>
                                                            <Typography variant="caption" color="text.secondary">{v.plateNumber} | {v.serviceType}</Typography>
                                                        </Box>
                                                    </Box>
                                                </Paper>
                                            ))}
                                        </Stack>
                                    </Box>
                                ) : (
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
                                            VEHICLE DETAILS
                                        </Typography>
                                        <Paper variant="outlined" sx={{ p: 1, bgcolor: 'grey.50' }}>
                                           <Grid container spacing={1}>
                                                <Grid item xs={6}>
                                                    <Typography variant="caption" color="text.secondary">Type</Typography>
                                                    <Typography variant="body2" fontWeight="bold">{paying?.vehicleType || '-'}</Typography>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Typography variant="caption" color="text.secondary">Plate No.</Typography>
                                                    <Typography variant="body2" fontWeight="bold">{paying?.plateNumber || '-'}</Typography>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Typography variant="caption" color="text.secondary">Brand/Model</Typography>
                                                    <Typography variant="body2" fontWeight="bold">{paying?.brandModel || '-'}</Typography>
                                                </Grid>
                                           </Grid>
                                        </Paper>
                                    </Box>
                                )}
                            </Box>
                        )}

                        {/* Gross Sales Breakdown for Business Permit */}
                        {paying?.permitType === 'Business Permit' && payingApplication?.gross_breakdown && Array.isArray(payingApplication.gross_breakdown) && payingApplication.gross_breakdown.length > 0 && (
                            <Box sx={{ mb: 4 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="caption" color="text.secondary" fontWeight="bold">
                                        GROSS SALES BREAKDOWN
                                    </Typography>
                                    {selectedGrossItems.length >= 2 && (
                                        <Button 
                                            size="small" 
                                            variant="outlined" 
                                            startIcon={<Add />} 
                                            onClick={handleAddCombinedGrossTax}
                                            sx={{ fontSize: '0.75rem', py: 0.5 }}
                                        >
                                            Add Combined Tax
                                        </Button>
                                    )}
                                </Box>
                                <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                                    <Table size="small">
                                <TableHead sx={{ bgcolor: 'grey.50' }}>
                                    <TableRow>
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                size="small"
                                                indeterminate={selectedGrossItems.length > 0 && selectedGrossItems.length < payingApplication.gross_breakdown.length}
                                                checked={payingApplication.gross_breakdown.length > 0 && selectedGrossItems.length === payingApplication.gross_breakdown.length}
                                                onChange={handleSelectAllGross}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ py: 1, fontSize: '0.75rem', fontWeight: 'bold' }}>Item</TableCell>
                                        <TableCell align="right" sx={{ py: 1, fontSize: '0.75rem', fontWeight: 'bold' }}>Amount</TableCell>
                                        <TableCell align="center" sx={{ width: 80 }}></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {payingApplication.gross_breakdown.map((item, idx) => (
                                        <TableRow key={idx} selected={selectedGrossItems.includes(idx)}>
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    size="small"
                                                    checked={selectedGrossItems.includes(idx)}
                                                    onChange={() => handleSelectGross(idx)}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ py: 0.5, fontSize: '0.875rem' }}>{item.label}</TableCell>
                                            <TableCell align="right" sx={{ py: 0.5, fontSize: '0.875rem' }}>
                                                ₱{parseFloat(item.amount || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}
                                            </TableCell>
                                            <TableCell align="center">
                                                <IconButton 
                                                  size="small" 
                                                  onClick={() => { 
                                                    setGrossTaxForm({ label: item.label, grossSale: parseFloat(item.amount || 0), grossTax: '' }); 
                                                    setShowGrossTaxModal(true); 
                                                  }}
                                                >
                                                  <Add fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                                        <TableCell />
                                        <TableCell sx={{ py: 1, fontWeight: 'bold', fontSize: '0.875rem' }}>TOTAL</TableCell>
                                        <TableCell align="right" sx={{ py: 1, fontWeight: 'bold', fontSize: '0.875rem' }}>
                                            ₱{parseFloat(paying?.gross || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </Paper>
                    </Box>
                )}

                        {/* Fishing Vessel and Gear Breakdown */}
                        {paying?.permitType === 'Fishing Vessel and Gear Registration' && (
                            <Box sx={{ mb: 4 }}>
                                {/* Vessels */}
                                {payingApplication?.fishing_vessels && payingApplication.fishing_vessels.length > 0 && (
                                    <Box sx={{ mb: 3 }}>
                                        <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
                                            REGISTERED VESSELS
                                        </Typography>
                                        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                                            <Table size="small">
                                                <TableHead sx={{ bgcolor: 'grey.50' }}>
                                                    <TableRow>
                                                        <TableCell sx={{ py: 1, fontSize: '0.75rem', fontWeight: 'bold' }}>Vessel Name / Type</TableCell>
                                                        <TableCell sx={{ py: 1, fontSize: '0.75rem', fontWeight: 'bold' }}>Details</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {payingApplication.fishing_vessels.map((v, idx) => (
                                                        <TableRow key={idx}>
                                                            <TableCell sx={{ py: 0.5, fontSize: '0.875rem' }}>
                                                                <Typography variant="body2" fontWeight="medium">{v.name}</Typography>
                                                                <Typography variant="caption" color="text.secondary">{v.type}</Typography>
                                                            </TableCell>
                                                            <TableCell sx={{ py: 0.5, fontSize: '0.875rem' }}>
                                                                <Typography variant="caption" display="block">Reg: {v.registrationType}</Typography>
                                                                <Typography variant="caption" display="block">Mat: {v.material}</Typography>
                                                                {v.type === 'Motorized' && v.engines && v.engines.length > 0 && (
                                                                     <Typography variant="caption" display="block" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                                                                        Engines: {v.engines.map(e => `${e.name} (${e.horsepower}HP)${e.serial ? ` SN:${e.serial}` : ''}`).join(', ')}
                                                                     </Typography>
                                                                )}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </Paper>
                                    </Box>
                                )}

                                {/* Gears */}
                                {payingApplication?.fishing_gears && payingApplication.fishing_gears.length > 0 && (
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
                                            REGISTERED GEARS
                                        </Typography>
                                        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                                            <Table size="small">
                                                <TableHead sx={{ bgcolor: 'grey.50' }}>
                                                    <TableRow>
                                                        <TableCell sx={{ py: 1, fontSize: '0.75rem', fontWeight: 'bold' }}>Gear Name</TableCell>
                                                        <TableCell align="right" sx={{ py: 1, fontSize: '0.75rem', fontWeight: 'bold' }}>Qty</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {payingApplication.fishing_gears.map((g, idx) => (
                                                        <TableRow key={idx}>
                                                            <TableCell sx={{ py: 0.5, fontSize: '0.875rem' }}>
                                                                <Typography variant="body2">{g.name}</Typography>
                                                                <Typography variant="caption" color="text.secondary">{g.category}</Typography>
                                                            </TableCell>
                                                            <TableCell align="right" sx={{ py: 0.5, fontSize: '0.875rem' }}>
                                                                {g.quantity}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </Paper>
                                    </Box>
                                )}
                            </Box>
                        )}

                        {/* Occupation List */}
                        {paying?.permitType === 'Occupation/Calling' && (payingApplication?.occupations_json || payingApplication?.occupations) && (payingApplication.occupations_json || payingApplication.occupations).length > 0 && (
                            <Box sx={{ mb: 4 }}>
                                <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
                                    REGISTERED OCCUPATIONS
                                </Typography>
                                <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                                    <Table size="small">
                                        <TableHead sx={{ bgcolor: 'grey.50' }}>
                                            <TableRow>
                                                <TableCell sx={{ py: 1, fontSize: '0.75rem', fontWeight: 'bold' }}>Occupation Name</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {(payingApplication.occupations_json || payingApplication.occupations).map((o, idx) => (
                                                <TableRow key={idx}>
                                                    <TableCell sx={{ py: 0.5, fontSize: '0.875rem' }}>
                                                        <Typography variant="body2">{o.name}</Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </Paper>
                            </Box>
                        )}

                        <Box sx={{ textAlign: 'center', mb: 2 }}>
                            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>TOTAL PAYABLE</Typography>
                            <Typography variant="h4" color="primary.main" fontWeight="bold" sx={{ mt: 0.5 }}>
                                ₱{((billingCharges||[]).reduce((s,c)=>s+(parseFloat(c.amount||0) * (c.quantity || 1)),0)).toLocaleString('en-PH', { minimumFractionDigits: 2 })}
                            </Typography>
                        </Box>

                        <Stack spacing={2}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField 
                                        label="OR Number" 
                                        fullWidth 
                                        size="small"
                                        value={paymentForm.or_no} 
                                        onChange={(e)=>setPaymentForm({ ...paymentForm, or_no: e.target.value })} 
                                        placeholder="Enter OR No."
                                        InputProps={{ startAdornment: <InputAdornment position="start"><Tag fontSize="small" color="action" /></InputAdornment> }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField 
                                        label="Payment Date" 
                                        type="date" 
                                        fullWidth 
                                        size="small"
                                        InputLabelProps={{ shrink: true }} 
                                        value={paymentForm.payment_date} 
                                        onChange={(e)=>setPaymentForm({ ...paymentForm, payment_date: e.target.value })} 
                                    />
                                </Grid>
                            </Grid>
                            
                            <TextField 
                                label="Notes / Remarks" 
                                multiline 
                                rows={2} 
                                fullWidth 
                                size="small"
                                value={paymentForm.notes} 
                                onChange={(e)=>setPaymentForm({ ...paymentForm, notes: e.target.value })} 
                                placeholder="Optional notes..."
                            />
                        </Stack>
                    </Box>

                    <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'white' }}>
                        <Button 
                            onClick={savePayment} 
                            variant="contained" 
                            fullWidth
                            disabled={((billingCharges||[]).reduce((s,c)=>s+(parseFloat(c.amount||0) * (c.quantity || 1)),0)) <= 0}
                            sx={{ fontWeight: 'bold' }}
                        >
                            Confirm Payment
                        </Button>
                        <Button 
                            onClick={() => { setShowPaymentModal(false); setShowTaxTable(false); }} 
                            fullWidth
                            size="small"
                            sx={{ mt: 1, color: 'text.secondary' }}
                        >
                            Cancel
                        </Button>
                    </Box>
                </Grid>
            </Grid>
        </DialogContent>
        <DialogActions sx={{ display: 'none' }}>
        </DialogActions>
      </Dialog>
      {showTaxTable && paying?.permitType === 'Business Permit' && (
        <Box sx={{ position: 'fixed', left: 0, top: 0, bottom: 0, width: { xs: '100%', md: 360 }, bgcolor: 'grey.50', borderRight: '1px solid', borderColor: 'divider', zIndex: (theme) => theme.zIndex.modal + 1, display: { xs: 'none', md: 'block' }, pointerEvents: 'auto', displayPrint: 'none', boxShadow: 1, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
                <Typography variant="h6" fontWeight="bold">Business Tax Table</Typography>
                <Typography variant="body2" color="text.secondary">Reference for Gross Sale and Tax</Typography>
            </Box>
            <IconButton size="small" onClick={() => setShowTaxTable(false)}>
                <Close fontSize="small" />
            </IconButton>
          </Box>
          <Box sx={{ p: 2, overflowY: 'auto', flex: 1 }}>
            {loadingTax ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (taxSettings || []).length === 0 ? (
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'white' }}>
                <Typography variant="body2" color="text.secondary">No tax settings found</Typography>
              </Paper>
            ) : (
              <Stack spacing={2}>
                {(taxSettings || []).map((t, idx) => (
                  <Paper key={idx} variant="outlined" sx={{ overflow: 'hidden' }}>
                    <Box sx={{ p: 2, bgcolor: 'grey.100', borderBottom: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="subtitle2" fontWeight="bold">{t.category || 'Uncategorized'}</Typography>
                      {t.description && <Typography variant="caption" color="text.secondary">{t.description}</Typography>}
                    </Box>
                    <Table size="small">
                      <TableHead sx={{ bgcolor: 'grey.50' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>Gross Sale</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>Gross Tax</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(t.tax_schedule || []).map((row, i) => (
                          <TableRow key={i}>
                            <TableCell>{row.gross_sale}</TableCell>
                            <TableCell align="right">₱{parseFloat(row.gross_tax || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}</TableCell>
                          </TableRow>
                        ))}
                        {(t.tax_schedule || []).length === 0 && (
                          <TableRow>
                            <TableCell colSpan={2} align="center">No schedule</TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </Paper>
                ))}
              </Stack>
            )}
          </Box>
        </Box>
      )
      }



      {/* Custom Charge Modal */}
      <Dialog 
        open={showCustomChargeModal} 
        onClose={() => setShowCustomChargeModal(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Add Custom Charge</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 1 }}>
            <TextField
              label="Item Name"
              fullWidth
              size="small"
              value={customChargeForm.item}
              onChange={(e) => setCustomChargeForm({ ...customChargeForm, item: e.target.value })}
              placeholder="Enter custom charge name"
            />
            <TextField
              label="Amount"
              fullWidth
              size="small"
              type="number"
              value={customChargeForm.amount}
              onChange={(e) => setCustomChargeForm({ ...customChargeForm, amount: e.target.value })}
              InputProps={{
                startAdornment: <InputAdornment position="start">₱</InputAdornment>,
              }}
            />
            <TextField
              select
              label="Percentage"
              fullWidth
              size="small"
              value={customChargeForm.percentage}
              onChange={(e) => setCustomChargeForm({ ...customChargeForm, percentage: e.target.value })}
              InputProps={{
                endAdornment: <InputAdornment position="end" sx={{ mr: 2 }}>%</InputAdornment>,
              }}
            >
              {customChargePercentages.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.name} ({option.value}%)
                </MenuItem>
              ))}
              {customChargePercentages.length === 0 && (
                 <MenuItem value={100}>100%</MenuItem>
              )}
            </TextField>
            <TextField
              label="Total"
              fullWidth
              size="small"
              value={
                customChargeForm.amount 
                  ? ((parseFloat(customChargeForm.amount) * (
                      customChargePercentages.length > 0 
                        ? (customChargePercentages.find(p => p.id === customChargeForm.percentage)?.value || 0) 
                        : parseFloat(customChargeForm.percentage)
                    )) / 100).toFixed(2) 
                  : '0.00'
              }
              disabled
              InputProps={{
                startAdornment: <InputAdornment position="start">₱</InputAdornment>,
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCustomChargeModal(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={() => {
              const amount = parseFloat(customChargeForm.amount) || 0;
              const percentage = customChargePercentages.length > 0 
                  ? (customChargePercentages.find(p => p.id === customChargeForm.percentage)?.value || 0) 
                  : parseFloat(customChargeForm.percentage);
              
              const total = (amount * percentage) / 100;
              
              if (!customChargeForm.item || total <= 0) return;

              setBillingCharges([...billingCharges, { 
                item: customChargeForm.item, 
                amount: total 
              }]);
              setShowCustomChargeModal(false);
            }}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
      
      <Dialog 
        open={showGrossTaxModal} 
        onClose={() => setShowGrossTaxModal(false)} 
        maxWidth="xs" 
        fullWidth
      >
        <DialogTitle>Add Gross Tax</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 1 }}>
            <TextField
              label="Gross Name"
              fullWidth
              size="small"
              value={grossTaxForm.label}
              onChange={(e) => setGrossTaxForm({ ...grossTaxForm, label: e.target.value })}
            />
            <TextField
              label="Gross Sale Amount"
              fullWidth
              size="small"
              value={grossTaxForm.grossSale?.toLocaleString('en-PH', { minimumFractionDigits: 2 })}
              disabled
              InputProps={{ startAdornment: <InputAdornment position="start">₱</InputAdornment> }}
            />
            <TextField
              label="Gross Tax"
              fullWidth
              size="small"
              type="number"
              value={grossTaxForm.grossTax}
              onChange={(e) => setGrossTaxForm({ ...grossTaxForm, grossTax: e.target.value })}
              InputProps={{ startAdornment: <InputAdornment position="start">₱</InputAdornment> }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowGrossTaxModal(false)}>Cancel</Button>
          <Button 
            variant="contained"
            onClick={() => {
              const val = parseFloat(grossTaxForm.grossTax) || 0;
              if (!grossTaxForm.label || val <= 0) return;
              setBillingCharges([...(billingCharges || []), { item: grossTaxForm.label, amount: val }]);
              setShowGrossTaxModal(false);
              setGrossTaxForm({ label: '', grossSale: 0, grossTax: '' });
              setSelectedGrossItems([]);
            }}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Application Modal */}
      <Dialog 
        open={showViewModal} 
        onClose={() => setShowViewModal(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2, overflow: 'hidden' }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: 'primary.main', 
          color: 'white', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          py: 2
        }}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Visibility fontSize="medium" />
            <Typography variant="h6" fontWeight="bold">Application Details</Typography>
          </Box>
          <Button 
            onClick={() => setShowViewModal(false)} 
            sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
          >
            Close
          </Button>
        </DialogTitle>
        <DialogContent sx={{ bgcolor: 'grey.50', p: 3 }}>
            {viewingApplication && (
                <Grid container spacing={3}>
                    {/* Applicant Info Card */}
                    <Grid item xs={12} md={6}>
                        <Card elevation={0} variant="outlined" sx={{ height: '100%', bgcolor: 'white' }}>
                            <CardHeader 
                                avatar={<Avatar sx={{ bgcolor: 'primary.light' }}><Person /></Avatar>}
                                title={<Typography variant="subtitle1" fontWeight="bold">Applicant Information</Typography>}
                                sx={{ pb: 1 }}
                            />
                            <Divider />
                            <CardContent>
                                {viewingApplicant ? (
                                    <Stack spacing={2}>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" fontWeight="bold">FULL NAME</Typography>
                                            <Typography variant="body1">{formatName(viewingApplicant.name)}</Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" fontWeight="bold">ADDRESS</Typography>
                                            <Typography variant="body2">{viewingApplicant.barangay}</Typography>
                                        </Box>
                                        <Grid container spacing={1}>
                                            <Grid item xs={6}>
                                                <Typography variant="caption" color="text.secondary" fontWeight="bold">CONTACT NO.</Typography>
                                                <Typography variant="body2">{viewingApplicant.mobile || '-'}</Typography>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Typography variant="caption" color="text.secondary" fontWeight="bold">EMAIL</Typography>
                                                <Typography variant="body2" noWrap title={viewingApplicant.email}>{viewingApplicant.email || '-'}</Typography>
                                            </Grid>
                                        </Grid>
                                    </Stack>
                                ) : (
                                    <Typography color="error">Applicant not found</Typography>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Permit Info Card */}
                    <Grid item xs={12} md={6}>
                         <Card elevation={0} variant="outlined" sx={{ height: '100%', bgcolor: 'white' }}>
                            <CardHeader 
                                avatar={<Avatar sx={{ bgcolor: 'secondary.light' }}><BusinessCenter /></Avatar>}
                                action={
                                    <Chip 
                                        label={viewingApplication.status} 
                                        color={viewingApplication.status === 'Paid' ? 'success' : 'warning'} 
                                        size="small" 
                                        sx={{ fontWeight: 'bold' }}
                                    />
                                }
                                title={<Typography variant="subtitle1" fontWeight="bold">Permit Details</Typography>}
                                sx={{ pb: 1 }}
                            />
                            <Divider />
                            <CardContent>
                                <Stack spacing={2}>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" fontWeight="bold">PERMIT NUMBER</Typography>
                                        <Typography variant="h6" color="primary.main" fontWeight="bold">{viewingApplication.permitNo}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" fontWeight="bold">PERMIT TYPE</Typography>
                                        <Typography variant="body1">{viewingApplication.permitType}</Typography>
                                    </Box>
                                    <Grid container spacing={1}>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary" fontWeight="bold">DATE ISSUED</Typography>
                                            <Typography variant="body2">{viewingApplication.issueDate}</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary" fontWeight="bold">EXPIRY DATE</Typography>
                                            <Typography variant="body2">{viewingApplication.expiryDate || '-'}</Typography>
                                        </Grid>
                                    </Grid>
                                    {viewingApplication.businessName && (
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" fontWeight="bold">BUSINESS NAME</Typography>
                                            <Typography variant="body2">{viewingApplication.businessName}</Typography>
                                        </Box>
                                    )}
                                </Stack>
                            </CardContent>
                         </Card>
                    </Grid>

                    <Grid item xs={12}>
                        <Card elevation={0} variant="outlined" sx={{ bgcolor: 'white' }}>
                            <CardHeader 
                                avatar={<Avatar sx={{ bgcolor: 'info.light' }}><Info /></Avatar>}
                                title={<Typography variant="subtitle1" fontWeight="bold">Application Details</Typography>}
                                sx={{ pb: 1 }}
                            />
                            <Divider />
                            <CardContent>
                                {viewingApplication.permitType === 'Business Permit' && (
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}>
                                            <Typography variant="caption" color="text.secondary" fontWeight="bold">TYPE OF BUSINESS</Typography>
                                            <Typography variant="body2">{viewingApplication.typeOfBusiness || '-'}</Typography>
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <Typography variant="caption" color="text.secondary" fontWeight="bold">NATURE OF BUSINESS</Typography>
                                            <Typography variant="body2">{viewingApplication.natureOfBusiness || '-'}</Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="caption" color="text.secondary" fontWeight="bold">BUSINESS ADDRESS</Typography>
                                            <Typography variant="body2">{viewingApplication.businessAddress || '-'}</Typography>
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <Typography variant="caption" color="text.secondary" fontWeight="bold">CAPITAL</Typography>
                                            <Typography variant="body2">
                                                {viewingApplication.capital != null ? `₱${parseFloat(viewingApplication.capital).toLocaleString('en-PH', { minimumFractionDigits: 2 })}` : '-'}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <Typography variant="caption" color="text.secondary" fontWeight="bold">GROSS (Renewal)</Typography>
                                            <Typography variant="body2">
                                                {viewingApplication.gross != null ? `₱${parseFloat(viewingApplication.gross).toLocaleString('en-PH', { minimumFractionDigits: 2 })}` : '-'}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                )}
                                {viewingApplication.permitType === 'Motorcycle and Tricycle Permit' && (
                                    <Box>
                                        {viewingApplication.vehicles && Array.isArray(viewingApplication.vehicles) && viewingApplication.vehicles.length > 0 ? (
                                            <Box>
                                                <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
                                                    VEHICLES LIST ({viewingApplication.vehicles.length})
                                                </Typography>
                                                <Stack spacing={1}>
                                                    {viewingApplication.vehicles.map((v, i) => (
                                                        <Paper key={i} variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50' }}>
                                                            <Grid container spacing={1}>
                                                                <Grid item xs={12} sm={6}>
                                                                    <Typography variant="subtitle2">{v.vehicleType}</Typography>
                                                                    <Typography variant="caption" color="text.secondary">{v.serviceType}</Typography>
                                                                </Grid>
                                                                <Grid item xs={12} sm={6}>
                                                                    <Typography variant="body2"><strong>Brand:</strong> {v.brandModel}</Typography>
                                                                    <Typography variant="body2"><strong>Plate:</strong> {v.plateNumber}</Typography>
                                                                </Grid>
                                                                <Grid item xs={12}>
                                                                    <Typography variant="caption">
                                                                        <strong>License:</strong> {v.licenseNumber} (Exp: {v.licenseExpiry || '-'}) | <strong>Sticker:</strong> {v.stickerNumber || '-'}
                                                                    </Typography>
                                                                </Grid>
                                                            </Grid>
                                                        </Paper>
                                                    ))}
                                                </Stack>
                                            </Box>
                                        ) : (
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} md={4}>
                                                    <Typography variant="caption" color="text.secondary" fontWeight="bold">VEHICLE TYPE</Typography>
                                                    <Typography variant="body2">{viewingApplication.vehicleType || '-'}</Typography>
                                                </Grid>
                                                <Grid item xs={12} md={4}>
                                                    <Typography variant="caption" color="text.secondary" fontWeight="bold">SERVICE TYPE</Typography>
                                                    <Typography variant="body2">{viewingApplication.serviceType || '-'}</Typography>
                                                </Grid>
                                                <Grid item xs={12} md={4}>
                                                    <Typography variant="caption" color="text.secondary" fontWeight="bold">BRAND/MODEL</Typography>
                                                    <Typography variant="body2">{viewingApplication.brandModel || '-'}</Typography>
                                                </Grid>
                                                <Grid item xs={12} md={4}>
                                                    <Typography variant="caption" color="text.secondary" fontWeight="bold">PLATE NUMBER</Typography>
                                                    <Typography variant="body2">{viewingApplication.plateNumber || '-'}</Typography>
                                                </Grid>
                                                <Grid item xs={12} md={4}>
                                                    <Typography variant="caption" color="text.secondary" fontWeight="bold">STICKER NUMBER</Typography>
                                                    <Typography variant="body2">{viewingApplication.stickerNumber || '-'}</Typography>
                                                </Grid>
                                                <Grid item xs={12} md={4}>
                                                    <Typography variant="caption" color="text.secondary" fontWeight="bold">LICENSE NUMBER</Typography>
                                                    <Typography variant="body2">{viewingApplication.licenseNumber || '-'}</Typography>
                                                </Grid>
                                                <Grid item xs={12} md={4}>
                                                    <Typography variant="caption" color="text.secondary" fontWeight="bold">LICENSE EXPIRY</Typography>
                                                    <Typography variant="body2">{viewingApplication.licenseExpiry || '-'}</Typography>
                                                </Grid>
                                            </Grid>
                                        )}
                                    </Box>
                                )}
                                {viewingApplication.permitType === 'Occupation/Calling' && (
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
                                            REGISTERED OCCUPATIONS
                                        </Typography>
                                        {(viewingApplication.occupations || viewingApplication.occupations_json) && (viewingApplication.occupations || viewingApplication.occupations_json).length > 0 ? (
                                            <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                                                <Table size="small">
                                                    <TableHead sx={{ bgcolor: 'grey.50' }}>
                                                        <TableRow>
                                                            <TableCell sx={{ py: 1, fontSize: '0.75rem', fontWeight: 'bold' }}>Occupation Name</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {(viewingApplication.occupations || viewingApplication.occupations_json).map((o, idx) => (
                                                            <TableRow key={idx}>
                                                                <TableCell sx={{ py: 0.5, fontSize: '0.875rem' }}>
                                                                    <Typography variant="body2">{o.name}</Typography>
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </Paper>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">No occupations selected.</Typography>
                                        )}
                                    </Box>
                                )}
                                {viewingApplication.permitType === 'Fishing Vessel and Gear Registration' && (
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Registered Vessels</Typography>
                                            {Array.isArray(viewingApplication.fishingVessels) && viewingApplication.fishingVessels.length > 0 ? (
                                                <Stack spacing={1}>
                                                    {viewingApplication.fishingVessels.map((v, idx) => (
                                                        <Paper key={idx} variant="outlined" sx={{ p: 1.5 }}>
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                <Box>
                                                                    <Typography variant="body2" fontWeight="bold">{v.name || '-'}</Typography>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {`${v.registrationType || '-'} • ${v.type || '-'} • ${v.material || '-'}`}
                                                                    </Typography>
                                                                    <Typography variant="caption" display="block" color="text.secondary">
                                                                        {`${v.homeport || ''}${v.placeBuilt ? ` • ${v.placeBuilt}` : ''}${v.yearBuilt ? ` • ${v.yearBuilt}` : ''}`}
                                                                    </Typography>
                                                                    {Array.isArray(v.engines) && v.engines.length > 0 && (
                                                                        <Typography variant="caption" display="block">
                                                                            Engines: {v.engines.map(e => `${e.name} (${e.horsepower}HP)`).join(', ')}
                                                                        </Typography>
                                                                    )}
                                                                </Box>
                                                            </Box>
                                                        </Paper>
                                                    ))}
                                                </Stack>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">No vessels added.</Typography>
                                            )}
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Divider sx={{ my: 1 }} />
                                            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Fishing Gears</Typography>
                                            {Array.isArray(viewingApplication.fishingGears) && viewingApplication.fishingGears.length > 0 ? (
                                                <Stack spacing={0.5}>
                                                    {viewingApplication.fishingGears.map((g, idx) => (
                                                        <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #eee', py: 0.5 }}>
                                                            <Typography variant="body2">{g.name}</Typography>
                                                            <Typography variant="body2" color="text.secondary">Qty: {g.quantity}</Typography>
                                                        </Box>
                                                    ))}
                                                </Stack>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">No gears selected.</Typography>
                                            )}
                                        </Grid>
                                    </Grid>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Payment History Card */}
                    <Grid item xs={12}>
                        <Card elevation={0} variant="outlined" sx={{ bgcolor: 'white' }}>
                            <CardHeader 
                                avatar={<Avatar sx={{ bgcolor: 'success.light' }}><Receipt /></Avatar>}
                                title={<Typography variant="subtitle1" fontWeight="bold">Payment History</Typography>}
                                subheader="Transaction records and official receipts"
                                sx={{ pb: 1 }}
                            />
                            <Divider />
                            <CardContent sx={{ p: 0 }}>
                                {viewingPayments.length > 0 ? (
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead sx={{ bgcolor: 'grey.50' }}>
                                                <TableRow>
                                                    <TableCell width="50px" />
                                                    <TableCell>OR No.</TableCell>
                                                    <TableCell>Date</TableCell>
                                                    <TableCell align="right">Amount</TableCell>
                                                    <TableCell>Notes</TableCell>
                                                    <TableCell align="right">Actions</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {viewingPayments.map((pay) => (
                                                    <PaymentRow
                                                      key={pay.id}
                                                      pay={pay}
                                                      onUpdated={(updated) => {
                                                        setViewingPayments(prev => prev.map(p => p.id === updated.id ? updated : p));
                                                      }}
                                                      showSnackbar={showSnackbar}
                                                    />
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                ) : (
                                    <Box sx={{ p: 3, textAlign: 'center' }}>
                                        <Typography variant="body2" color="text.secondary">
                                            No payment records found for this application.
                                        </Typography>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}
        </DialogContent>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

const Permit = () => {
  const [applications, setApplications] = useState([]);
  const [applicants, setApplicants] = useState([]);
  const [applicationTypes, setApplicationTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('All');
  const [payments, setPayments] = useState([]);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedVessel, setSelectedVessel] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const [appsRes, applicantsRes, typesRes, paymentsRes] = await Promise.all([
          bploService.getApplications(),
          bploService.getApplicants(),
          bploService.getApplicationTypes(),
          bploService.getPayments()
        ]);
        if (!mounted) return;
        if (appsRes.success) setApplications(appsRes.data || []);
        if (applicantsRes.success) setApplicants(applicantsRes.data || []);
        if (typesRes.success) {
          setApplicationTypes((typesRes.data || []).map(t => t.name));
        }
        if (paymentsRes.success) setPayments(paymentsRes.data || []);
      } catch (error) {
        console.error('Failed to load permit data', error);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const businessName = (id) => {
    const biz = applicants.find(b => b.id === id);
    return formatName(biz?.name || '');
  };

  const sanitaryApplicationIds = useMemo(() => {
    if (!Array.isArray(payments) || payments.length === 0) return new Set();
    const feeLabel = 'sanitary inspection fee';
    const ids = new Set();
    payments.forEach(p => {
      const items = Array.isArray(p.particulars) ? p.particulars : [];
      if (items.some(c => String(c.item || '').toLowerCase() === feeLabel)) {
        if (p.application_id) ids.add(p.application_id);
      }
    });
    return ids;
  }, [payments]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return (applications || []).filter(app => {
      const matchesSearch =
        !s ||
        String(app.application_no || '').toLowerCase().includes(s) ||
        String(businessName(app.applicant_id)).toLowerCase().includes(s);
      let matchesType = true;
      if (filterType !== 'All') {
        if (filterType === 'Sanitary Permit') {
          matchesType = sanitaryApplicationIds.has(app.id);
        } else {
          matchesType = app.application_type === filterType;
        }
      }
      return matchesSearch && matchesType;
    });
  }, [applications, search, filterType, applicants, sanitaryApplicationIds]);

  const displayRows = useMemo(() => {
    if (filterType === 'Motorcycle and Tricycle Permit') {
      return filtered.flatMap(app => {
        const list = Array.isArray(app.vehicles) ? app.vehicles : [];
        if (!list.length) return [{ ...app, _vehicle: null }];
        return list.map(v => ({ ...app, _vehicle: v }));
      });
    }
    if (filterType === 'Fishing Vessel and Gear Registration') {
      return filtered.flatMap(app => {
        const list = Array.isArray(app.fishing_vessels) ? app.fishing_vessels : [];
        if (!list.length) return [{ ...app, _vessel: null }];
        return list.map(v => ({ ...app, _vessel: v }));
      });
    }
    return filtered;
  }, [filtered, filterType]);

  const handlePrintAll = () => {
    const source = (filterType === 'Motorcycle and Tricycle Permit' || filterType === 'Fishing Vessel and Gear Registration') ? displayRows : filtered;
    if (!source.length) return;

    const rows = source.map(app => {
      const applicant = applicants.find(b => b.id === app.applicant_id);
      const applicantName = applicant ? formatName(applicant.name) : '';
      const vehicle = app._vehicle || null;
      const vessel = app._vessel || null;
      const appPayments = payments.filter(p => p.application_id === app.id);
      const orNo = appPayments.length > 0 ? appPayments.map(p => p.or_no).filter(Boolean).join(', ') : '-';

      return {
        ...app,
        applicantName,
        businessName: app.business_name || '',
        natureOfBusiness: app.nature_of_business || '',
        barangay: applicant?.barangay || '',
        vehicleType: (vehicle && vehicle.vehicleType) || app.vehicle_type || '',
        serviceType: (vehicle && vehicle.serviceType) || app.service_type || '',
        vesselName: (vessel && vessel.name) || '',
        vesselType: (vessel && vessel.type) || '',
        status: app.status,
        orNo
      };
    });

    const columns =
      (filterType === 'Business Permit' || filterType === 'Sanitary Permit')
        ? [
            { header: 'Application No', accessor: (row) => row.application_no },
            { header: 'Applicant Name', accessor: (row) => row.applicantName },
            { header: 'Business Name', accessor: (row) => row.businessName || '' },
            { header: 'Nature of Business', accessor: (row) => row.natureOfBusiness || '' },
            { header: 'Barangay', accessor: (row) => row.barangay || '' },
            { header: 'Issue Date', accessor: (row) => row.issue_date || '' },
            { header: 'Expiry Date', accessor: (row) => row.expiry_date || '' },
            { header: 'OR No.', accessor: (row) => row.orNo || '' }
          ]
        : filterType === 'Motorcycle and Tricycle Permit'
          ? [
              { header: 'Application No', accessor: (row) => row.application_no },
              { header: 'Applicant Name', accessor: (row) => row.applicantName },
              { header: 'Barangay', accessor: (row) => row.barangay || '' },
              { header: 'Vehicle Type', accessor: (row) => row.vehicleType || '' },
              { header: 'Service Type', accessor: (row) => row.serviceType || '' },
              { header: 'Issue Date', accessor: (row) => row.issue_date || '' },
              { header: 'Expiry Date', accessor: (row) => row.expiry_date || '' },
              { header: 'OR No.', accessor: (row) => row.orNo || '' }
            ]
          : filterType === 'Fishing Vessel and Gear Registration'
            ? [
                { header: 'Application No', accessor: (row) => row.application_no },
                { header: 'Applicant Name', accessor: (row) => row.applicantName },
                { header: 'Barangay', accessor: (row) => row.barangay || '' },
                { header: 'Vessel Name', accessor: (row) => row.vesselName || '' },
                { header: 'Vessel Type', accessor: (row) => row.vesselType || '' },
                { header: 'Issue Date', accessor: (row) => row.issue_date || '' },
                { header: 'Expiry Date', accessor: (row) => row.expiry_date || '' },
                { header: 'OR No.', accessor: (row) => row.orNo || '' }
              ]
            : [
              { header: 'Application No', accessor: (row) => row.application_no },
              { header: 'Applicant Name', accessor: (row) => row.applicantName },
              { header: 'Issue Date', accessor: (row) => row.issue_date || '' },
              { header: 'Expiry Date', accessor: (row) => row.expiry_date || '' },
              { header: 'OR No.', accessor: (row) => row.orNo || '' }
            ];

    handlePrint('Permit Applications', columns, rows);
  };

  const handlePrint = (title, columns, data) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
        alert("Please allow popups to print.");
        return;
    }
    const html = `
      <html>
        <head>
          <title>${title}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h2 { text-align: center; color: #333; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; font-size: 12px; }
            th { background-color: #f2f2f2; font-weight: bold; }
            tr:nth-child(even) { background-color: #f9f9f9; }
            .footer { margin-top: 20px; text-align: right; font-size: 10px; color: #666; }
          </style>
        </head>
        <body>
          <h2>${title}</h2>
          <p>Date Generated: ${new Date().toLocaleDateString()}</p>
          <table>
            <thead>
              <tr>
                ${columns.map(c => `<th>${c.header}</th>`).join('')}
              </tr>
            </thead>
            <tbody>
              ${data.map(row => `
                <tr>
                  ${columns.map(c => `<td>${c.accessor ? c.accessor(row) : (row[c.field] || '')}</td>`).join('')}
                </tr>
              `).join('')}
            </tbody>
          </table>
          <div class="footer">Generated by BPLO System</div>
          <script>
            window.onload = function() { window.print(); window.close(); }
          </script>
        </body>
      </html>
    `;
    printWindow.document.write(html);
    printWindow.document.close();
  };

  const handleExportExcel = async (title, columns, data) => {
    const safeTitle = String(title || 'export')
      .replace(/[\\/:*?"<>|]/g, '-')
      .replace(/\s+/g, ' ')
      .trim();

    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('List');

    sheet.columns = columns.map((c, idx) => ({
      header: c.header,
      key: `c${idx}`,
      width: Math.max(12, Math.min(40, String(c.header || '').length + 8))
    }));

    (sheet.getRow(1).values || []).forEach(() => {});
    sheet.getRow(1).font = { bold: true };

    (data || []).forEach((row) => {
      const values = columns.map((c) => {
        const v = c.accessor ? c.accessor(row) : (c.field ? row[c.field] : '');
        if (v === null || v === undefined) return '';
        return String(v);
      });
      sheet.addRow(values);
    });

    sheet.views = [{ state: 'frozen', ySplit: 1 }];
    sheet.autoFilter = {
      from: { row: 1, column: 1 },
      to: { row: 1, column: Math.max(1, columns.length) }
    };

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    saveAs(blob, `${safeTitle}.xlsx`);
  };


  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">Permit</Typography>
        <Button
          variant="outlined"
          startIcon={<Print />}
          onClick={handlePrintAll}
          disabled={loading || !filtered.length}
        >
          Print
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by application no or applicant"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Application Type</InputLabel>
              <Select
                value={filterType}
                label="Application Type"
                onChange={(e) => setFilterType(e.target.value)}
              >
                <MenuItem value="All">All Types</MenuItem>
                {Array.from(new Set([...applicationTypes, 'Sanitary Permit'])).map((type) => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={5}>
            <Typography variant="body2" sx={{ textAlign: { xs: 'left', md: 'right' }, color: 'text.secondary' }}>
              {filterType === 'Motorcycle and Tricycle Permit'
                ? `Total records: ${displayRows.length}`
                : `Total applications: ${filtered.length}`}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ bgcolor: 'grey.100' }}>
            <TableRow>
              <TableCell>Application No</TableCell>
              <TableCell>Applicant Name</TableCell>
              {['Business Permit', 'Sanitary Permit'].includes(filterType) && (
                <>
                  <TableCell>Business Name</TableCell>
                  <TableCell>Nature of Business</TableCell>
                  <TableCell>Barangay</TableCell>
                </>
              )}
              {filterType === 'Motorcycle and Tricycle Permit' && (
                <>
                  <TableCell>Barangay</TableCell>
                  <TableCell>Vehicle Type</TableCell>
                  <TableCell>Service Type</TableCell>
                </>
              )}
              {filterType === 'Fishing Vessel and Gear Registration' && (
                <>
                  <TableCell>Barangay</TableCell>
                  <TableCell>Vessel Name</TableCell>
                  <TableCell>Vessel Type</TableCell>
                </>
              )}
              <TableCell>Issue Date</TableCell>
              <TableCell>Expiry Date</TableCell>
              <TableCell>OR No.</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={
                    ['Business Permit', 'Sanitary Permit', 'Motorcycle and Tricycle Permit', 'Fishing Vessel and Gear Registration'].includes(filterType)
                      ? 8
                      : 5
                  }
                  align="center"
                >
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={
                    ['Business Permit', 'Sanitary Permit', 'Motorcycle and Tricycle Permit', 'Fishing Vessel and Gear Registration'].includes(filterType)
                      ? 8
                      : 5
                  }
                  align="center"
                >
                  No applications found
                </TableCell>
              </TableRow>
            ) : (
              displayRows.map((app, index) => {
                const applicant = applicants.find(b => b.id === app.applicant_id);
                const vehicle = app._vehicle || null;
                const vessel = app._vessel || null;

                return (
                  <TableRow
                    key={`${app.id}-${index}`}
                    hover
                    onClick={() => {
                      setSelectedApp(app);
                        setSelectedVehicle(vehicle || null);
                        setSelectedVessel(vessel || null);
                      setDetailsOpen(true);
                    }}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell><strong>{app.application_no}</strong></TableCell>
                    <TableCell>{businessName(app.applicant_id)}</TableCell>

                    {['Business Permit', 'Sanitary Permit'].includes(filterType) && (
                      <>
                        <TableCell>{app.business_name || '-'}</TableCell>
                        <TableCell>{app.nature_of_business || '-'}</TableCell>
                        <TableCell>{applicant?.barangay || '-'}</TableCell>
                      </>
                    )}

                    {filterType === 'Motorcycle and Tricycle Permit' && (
                      <>
                        <TableCell>{applicant?.barangay || '-'}</TableCell>
                        <TableCell>{(vehicle && vehicle.vehicleType) || app.vehicle_type || '-'}</TableCell>
                        <TableCell>{(vehicle && vehicle.serviceType) || app.service_type || '-'}</TableCell>
                      </>
                    )}

                    {filterType === 'Fishing Vessel and Gear Registration' && (
                      <>
                        <TableCell>{applicant?.barangay || '-'}</TableCell>
                        <TableCell>{(vessel && vessel.name) || '-'}</TableCell>
                        <TableCell>{(vessel && vessel.type) || '-'}</TableCell>
                      </>
                    )}

                    <TableCell>{app.issue_date}</TableCell>
                    <TableCell>{app.expiry_date || '-'}</TableCell>
                    <TableCell>{(() => {
                      const appPayments = payments.filter(p => p.application_id === app.id);
                      return appPayments.length > 0 ? appPayments.map(p => p.or_no).filter(Boolean).join(', ') : '-';
                    })()}</TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={detailsOpen && !!selectedApp}
        onClose={() => setDetailsOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Application Details</DialogTitle>
        <DialogContent dividers>
          {selectedApp && (() => {
            const applicant = applicants.find(b => b.id === selectedApp.applicant_id);
            const appPayments = payments.filter(p => p.application_id === selectedApp.id);
            const orNo = appPayments.length > 0 ? appPayments.map(p => p.or_no).filter(Boolean).join(', ') : '-';
            const vehicle = selectedVehicle || null;
            const vessel = selectedVessel || null;
            
            return (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2">Applicant</Typography>
                  <Typography variant="body2">
                    {applicant ? formatName(applicant.name) : 'Unknown'}
                  </Typography>
                  <Typography variant="body2">
                    {applicant?.barangay || ''}{applicant?.address ? `, ${applicant.address}` : ''}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2">Application</Typography>
                  <Typography variant="body2">
                    No: {selectedApp.application_no}
                  </Typography>
                  <Typography variant="body2">
                    Type: {selectedApp.application_type}
                  </Typography>
                  <Typography variant="body2">
                    Status: {selectedApp.status || 'Unknown'}
                  </Typography>
                  <Typography variant="body2">
                    Issue Date: {selectedApp.issue_date || '-'}
                  </Typography>
                  <Typography variant="body2">
                    Expiry Date: {selectedApp.expiry_date || '-'}
                  </Typography>
                  <Typography variant="body2">
                    OR No.: {orNo}
                  </Typography>
                </Grid>
                {selectedApp.business_name && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">Business</Typography>
                    <Typography variant="body2">
                      Name: {selectedApp.business_name}
                    </Typography>
                    <Typography variant="body2">
                      Nature of Business: {selectedApp.nature_of_business || '-'}
                    </Typography>
                    <Typography variant="body2">
                      Address: {selectedApp.business_address || '-'}
                    </Typography>
                  </Grid>
                )}
                {(selectedApp.vehicle_type || selectedApp.service_type) && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">Vehicle</Typography>
                    <Typography variant="body2">
                      Vehicle Type: {(vehicle && vehicle.vehicleType) || selectedApp.vehicle_type || '-'}
                    </Typography>
                    <Typography variant="body2">
                      Service Type: {(vehicle && vehicle.serviceType) || selectedApp.service_type || '-'}
                    </Typography>
                    <Typography variant="body2">
                      Plate No.: {(vehicle && vehicle.plateNumber) || selectedApp.plate_number || '-'}
                    </Typography>
                  </Grid>
                )}
                {selectedApp.application_type === 'Fishing Vessel and Gear Registration' && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">Fishing Vessel</Typography>
                    <Typography variant="body2">
                      Vessel Name: {(vessel && vessel.name) || '-'}
                    </Typography>
                    <Typography variant="body2">
                      Vessel Type: {(vessel && vessel.type) || '-'}
                    </Typography>
                    <Typography variant="body2">
                      Registration Type: {(vessel && vessel.registrationType) || '-'}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const Payments = () => {
  const [charges, setCharges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ item: '', amount: '' });

  // Alert & Confirm states
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const showConfirm = (title, message, onConfirm) => {
    setConfirmDialog({ open: true, title, message, onConfirm });
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const chargesRes = await bploService.getCharges();
        if (chargesRes.success) setCharges(chargesRes.data);
      } catch (error) {
        console.error("Failed to load charges:", error);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return (charges || []).filter(c => {
      if (s && !String(c.item || '').toLowerCase().includes(s)) return false;
      return true;
    }).sort((a, b) => String(a.item || '').localeCompare(String(b.item || ''), undefined, { sensitivity: 'base' }));
  }, [charges, search]);

  const reset = () => { setForm({ item: '', amount: '' }); setEditing(null); };

  const save = async () => {
    if (!form.item || !form.amount) return;
    if (editing) {
      const { success, error } = await bploService.updateCharge(editing, form);
      if (success) {
        const { success: s2, data } = await bploService.getCharges();
        if (s2) setCharges(data);
        showSnackbar('Charge updated successfully', 'success');
      } else {
        showSnackbar(error || 'Failed to update charge', 'error');
        return;
      }
    } else {
      const { success, error } = await bploService.createCharge(form);
      if (success) {
        const { success: s2, data } = await bploService.getCharges();
        if (s2) setCharges(data);
        showSnackbar('Charge added successfully', 'success');
      } else {
        showSnackbar(error || 'Failed to add charge', 'error');
        return;
      }
    }
    setShowModal(false);
    reset();
  };

  const remove = async (id) => {
    showConfirm(
      'Delete Charge',
      'Are you sure you want to delete this charge?',
      async () => {
        setConfirmDialog(prev => ({ ...prev, open: false }));
        const { success, error } = await bploService.deleteCharge(id);
        if (success) {
          const { success: s2, data } = await bploService.getCharges();
          if (s2) setCharges(data);
          showSnackbar('Charge deleted successfully', 'success');
        } else {
          showSnackbar(error || 'Failed to delete charge', 'error');
        }
      }
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">Charges</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => { reset(); setShowModal(true); }}>
          Add Charge
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: 'center' }}>
          <Box sx={{ flexGrow: 1, width: { xs: '100%', md: 'auto' } }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by item"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Search /></InputAdornment>
              }}
            />
          </Box>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ bgcolor: 'grey.100' }}>
            <TableRow>
              <TableCell>Item</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={3} align="center">No charges found</TableCell></TableRow>
            ) : filtered.map(c => (
              <TableRow key={c.id}>
                <TableCell><strong>{c.item}</strong></TableCell>
                <TableCell>₱{parseFloat(c.amount).toLocaleString('en-PH', { minimumFractionDigits: 2 })}</TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => { setEditing(c.id); setForm({ item: c.item, amount: c.amount }); setShowModal(true); }}>
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => remove(c.id)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={showModal} onClose={() => setShowModal(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? 'Edit Charge' : 'Add Charge'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Item" value={form.item} onChange={(e)=>setForm({ ...form, item: e.target.value })} required />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" type="number" label="Amount" value={form.amount} onChange={(e)=>setForm({ ...form, amount: e.target.value })} required />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowModal(false)}>Cancel</Button>
          <Button onClick={save} variant="contained" disabled={!form.item || !form.amount}>Save</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}>
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ whiteSpace: 'pre-line' }}>{confirmDialog.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}>Cancel</Button>
          <Button onClick={confirmDialog.onConfirm} variant="contained" sx={{ bgcolor: 'var(--bs-primary)', color: '#fff', '&:hover': { bgcolor: 'var(--bs-primary)' } }}>Confirm</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const Reports = () => {
  const [loading, setLoading] = useState(false);
  const [businesses, setBusinesses] = useLocalStorage('bplo_businesses', []);
  const [permits, setPermits] = useLocalStorage('bplo_permits', []);
  const [payments, setPayments] = useLocalStorage('bplo_payments', []);
  const [range, setRange] = useState({ from: '', to: '' });

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [applicantsRes, applicationsRes, paymentsRes] = await Promise.all([
          bploService.getApplicants(),
          bploService.getApplications(),
          bploService.getPayments()
        ]);

        if (applicantsRes.success) setBusinesses(applicantsRes.data || []);
        
        if (applicationsRes.success) {
            const mapped = (applicationsRes.data || []).map(a => ({
                id: a.id,
                businessId: a.applicant_id,
                permitNo: a.application_no,
                permitType: a.application_type,
                issueDate: a.issue_date,
                expiryDate: a.expiry_date,
                status: a.status,
                typeOfBusiness: a.type_of_business,
                businessName: a.business_name,
                businessAddress: a.business_address,
                natureOfBusiness: a.nature_of_business,
                capital: a.capital,
                gross: a.gross,
                grossBreakdown: a.gross_breakdown,
                vehicleType: a.vehicle_type,
                serviceType: a.service_type,
                brandModel: a.brand_model,
                plateNumber: a.plate_number,
                stickerNumber: a.sticker_number,
                licenseNumber: a.license_number,
                licenseExpiry: a.license_expiry,
                fishingVessels: a.fishing_vessels,
                fishingGears: a.fishing_gears,
                billingCharges: a.billing_charges,
                vehicles: a.vehicles
            }));
            setPermits(mapped);
        }

        if (paymentsRes.success) setPayments(paymentsRes.data || []);

      } catch (error) {
        console.error("Failed to load report data", error);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const inRange = (d) => {
    if (!range.from && !range.to) return true;
    const x = new Date(d);
    const f = range.from ? new Date(range.from) : null;
    const t = range.to ? new Date(range.to) : null;
    if (f && x < f) return false;
    if (t && x > t) return false;
    return true;
  };

  const activePermits = useMemo(() => (permits || []).filter(p => p.status === 'Active' && inRange(p.issueDate)).length, [permits, range]);
  const collections = useMemo(() => (payments || []).filter(p => inRange(p.payment_date)).reduce((sum, p) => sum + parseFloat(p.amount || p.amount_paid || 0), 0), [payments, range]);
  const totalBusinesses = businesses.length;

  const [detailedSummary, setDetailedSummary] = useState({ type: null });
  const [reportsVehicles, setReportsVehicles] = useState([]);
  const [reportsNewVehicle, setReportsNewVehicle] = useState({ vehicleType: 'Motorcycle', serviceType: 'Private', brandModel: '', plateNumber: '', stickerNumber: '', licenseNumber: '', licenseExpiry: '' });
  const [reportsShowVehicleForm, setReportsShowVehicleForm] = useState(false);
  const [showDetailedSummaryModal, setShowDetailedSummaryModal] = useState(false);
  const [summaryFilters, setSummaryFilters] = useState({
      permitStatus: 'All',
      vehicleType: 'All',
      serviceType: 'All'
  });

  const handleSummaryClick = (type) => {
    setDetailedSummary({ type });
    setSummaryFilters({ permitStatus: 'All', vehicleType: 'All', serviceType: 'All' });
    setShowDetailedSummaryModal(true);
  };

  const [drillDownList, setDrillDownList] = useState([]);
  const [showDrillDownModal, setShowDrillDownModal] = useState(false);
  const [drillDownTitle, setDrillDownTitle] = useState('');

  const handleCountClick = (label, permitsList) => {
    if (!permitsList || permitsList.length === 0) return;
    setDrillDownTitle(label);
    setDrillDownList(permitsList);
    setShowDrillDownModal(true);
  };

  useEffect(() => {
    if (showDrillDownModal && drillDownList.length > 0) {
      const neededIds = drillDownList
        .map(p => p.businessId || p.applicant_id || p.applicantId)
        .filter(id => id);
      
      const uniqueIds = [...new Set(neededIds)];
      const missingIds = uniqueIds.filter(id => !businesses.find(b => String(b.id) === String(id)));
      
      if (missingIds.length > 0) {
        bploService.getApplicantsByIds(missingIds).then(res => {
          if (res.success && res.data.length > 0) {
             const merged = [...businesses];
             res.data.forEach(newItem => {
                if (!merged.find(existing => String(existing.id) === String(newItem.id))) {
                    merged.push(newItem);
                }
             });
             setBusinesses(merged);
          }
        });
      }
    }
  }, [showDrillDownModal, drillDownList, businesses]);

  const detailedSummaryData = useMemo(() => {
    if (!detailedSummary.type || !showDetailedSummaryModal) return [];

    let data = [];
    const relevantPermits = (permits || []).filter(p => inRange(p.issueDate));
    const type = detailedSummary.type;

    if (type === 'Business Permit') {
        const groups = {};
        relevantPermits.filter(p => p.permitType === type).forEach(p => {
            const nature = p.natureOfBusiness || 'Unspecified';
            if (!groups[nature]) groups[nature] = [];
            groups[nature].push(p);
        });
        data = Object.entries(groups).map(([label, list]) => ({ label, count: list.length, permits: list })).sort((a, b) => b.count - a.count);
    } else if (type === 'Motorcycle and Tricycle Permit') {
        const groups = {};
        
        relevantPermits.filter(p => p.permitType === type).forEach(p => {
            const permitStatus = p.typeOfBusiness || 'Unspecified';
            
            const processVehicle = (vType, sType, vehicleObj = null) => {
                 const vehicle = vType || 'Unspecified';
                 const service = sType || 'Unspecified';

                 // Apply filters
                 if (summaryFilters.permitStatus !== 'All' && permitStatus !== summaryFilters.permitStatus) return;
                 if (summaryFilters.vehicleType !== 'All' && vehicle !== summaryFilters.vehicleType) return;
                 if (summaryFilters.serviceType !== 'All' && service !== summaryFilters.serviceType) return;

                 const key = `${vehicle} - ${permitStatus} - ${service}`;
                 if (!groups[key]) groups[key] = [];
                 
                 // Attach vehicle details for display
                 const displayItem = { ...p };
                 if (vehicleObj) {
                     displayItem.displayVehicle = vehicleObj;
                 } else {
                     displayItem.displayVehicle = {
                         plateNumber: p.plateNumber,
                         brandModel: p.brandModel,
                         vehicleType: p.vehicleType,
                         serviceType: p.serviceType
                     };
                 }
                 groups[key].push(displayItem);
            };

            if (p.vehicles && Array.isArray(p.vehicles) && p.vehicles.length > 0) {
                 p.vehicles.forEach(v => processVehicle(v.vehicleType, v.serviceType, v));
            } else {
                 processVehicle(p.vehicleType, p.serviceType);
            }
        });
        
        data = Object.entries(groups).map(([label, list]) => ({ label, count: list.length, permits: list }));
             
    } else if (type === 'Fishing Vessel and Gear Registration') {
        const groups = {
            'Motorized Boats': [],
            'Non-Motorized Boats': [],
            'Fishing Gears': []
        };
        
        relevantPermits.filter(p => p.permitType === type).forEach(p => {
            if (Array.isArray(p.fishingVessels)) {
                p.fishingVessels.forEach(v => {
                    const item = { ...p, displayFishingVessel: v };
                    if (v.type === 'Motorized') groups['Motorized Boats'].push(item);
                    else groups['Non-Motorized Boats'].push(item); 
                });
            }
            if (Array.isArray(p.fishingGears)) {
                p.fishingGears.forEach(g => {
                    const qty = parseInt(g.quantity) || 0;
                    const item = { ...p, displayFishingGear: g };
                    for(let i=0; i<qty; i++) groups['Fishing Gears'].push(item);
                });
            }
        });
        data = Object.entries(groups).map(([label, list]) => ({ label, count: list.length, permits: list }));
    } else {
        const list = relevantPermits.filter(p => p.permitType === type);
        data = [{ label: 'Total Applications', count: list.length, permits: list }];
    }
    return data;
  }, [detailedSummary.type, showDetailedSummaryModal, permits, range, summaryFilters]);

  const typeSummary = useMemo(() => {
    const summary = {};
    (permits || []).filter(p => inRange(p.issueDate)).forEach(p => {
      const type = p.permitType || 'Unknown';
      let count = 1;

      if (type === 'Motorcycle and Tricycle Permit') {
        if (p.vehicles && Array.isArray(p.vehicles) && p.vehicles.length > 0) {
          count = p.vehicles.length;
        }
      } else if (type === 'Fishing Vessel and Gear Registration') {
        let vesselCount = 0;
        if (Array.isArray(p.fishingVessels)) {
          vesselCount += p.fishingVessels.length;
        }
        if (Array.isArray(p.fishingGears)) {
          p.fishingGears.forEach(g => {
            vesselCount += parseInt(g.quantity) || 0;
          });
        }
        count = vesselCount;
      }

      summary[type] = (summary[type] || 0) + count;
    });
    return Object.entries(summary).sort((a, b) => b[1] - a[1]);
  }, [permits, range]);

  const sanitarySummary = useMemo(() => {
    if (!Array.isArray(payments) || payments.length === 0) {
      return { count: 0, totalAmount: 0, permits: [] };
    }

    const feeLabel = 'sanitary inspection fee';

    const relevantPayments = payments.filter(p => {
      if (!inRange(p.payment_date)) return false;
      if (!Array.isArray(p.particulars) || p.particulars.length === 0) return false;
      return p.particulars.some(c => String(c.item || '').toLowerCase() === feeLabel);
    });

    const appMap = new Map((permits || []).map(app => [app.id, app]));
    const seenApplications = new Set();
    const relevantPermits = [];

    let totalAmount = 0;

    relevantPayments.forEach(pay => {
      const items = Array.isArray(pay.particulars) ? pay.particulars : [];

      items.forEach(c => {
        if (String(c.item || '').toLowerCase() === feeLabel) {
          totalAmount += parseFloat(c.amount || 0);
        }
      });

      const appId = pay.application_id;
      if (appId && !seenApplications.has(appId)) {
        seenApplications.add(appId);
        const app = appMap.get(appId);
        if (app) {
          relevantPermits.push(app);
        }
      }
    });

    return {
      count: relevantPermits.length,
      totalAmount,
      permits: relevantPermits
    };
  }, [payments, permits, range]);

  const getApplicantName = (p) => {
    const applicant = businesses.find(b => String(b.id) === String(p.businessId) || String(b.id) === String(p.applicant_id) || String(b.id) === String(p.applicantId));
    const debugName = p.businessId ? `Unknown (ID: ${p.businessId})` : 'Unknown (No ID)';
    return applicant ? formatName(applicant.name) : (p.businessName || debugName);
  };

  const handlePrint = (title, columns, data) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
        alert("Please allow popups to print.");
        return;
    }
    const html = `
      <html>
        <head>
          <title>${title}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h2 { text-align: center; color: #333; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; font-size: 12px; }
            th { background-color: #f2f2f2; font-weight: bold; }
            tr:nth-child(even) { background-color: #f9f9f9; }
            .footer { margin-top: 20px; text-align: right; font-size: 10px; color: #666; }
          </style>
        </head>
        <body>
          <h2>${title}</h2>
          <p>Date Generated: ${new Date().toLocaleDateString()}</p>
          <table>
            <thead>
              <tr>
                ${columns.map(c => `<th>${c.header}</th>`).join('')}
              </tr>
            </thead>
            <tbody>
              ${data.map(row => `
                <tr>
                  ${columns.map(c => `<td>${c.accessor ? c.accessor(row) : (row[c.field] || '')}</td>`).join('')}
                </tr>
              `).join('')}
            </tbody>
          </table>
          <div class="footer">Generated by BPLO System</div>
          <script>
            window.onload = function() { window.print(); window.close(); }
          </script>
        </body>
      </html>
    `;
    printWindow.document.write(html);
    printWindow.document.close();
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" gutterBottom>Reports & Analytics</Typography>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="date" label="From" InputLabelProps={{ shrink: true }} value={range.from} onChange={(e)=>setRange({ ...range, from: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="date" label="To" InputLabelProps={{ shrink: true }} value={range.to} onChange={(e)=>setRange({ ...range, to: e.target.value })} />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={4}>
          <StatCard title="Applicants" value={totalBusinesses} icon={<Store />} color="#b45309" bgcolor="#fef3c7" />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard title="Active Applications" value={activePermits} icon={<Badge />} color="#16a34a" bgcolor="#dcfce7" />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard title="Collections" value={`₱${parseFloat(collections).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`} icon={<Payment />} color="#2563eb" bgcolor="#dbeafe" />
        </Grid>
      </Grid>

      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom>Summary by Application Type</Typography>
        <Grid container spacing={3}>
            {typeSummary.map(([type, count]) => (
                <Grid item xs={12} sm={6} md={4} key={type}>
                    <Card variant="outlined" sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'grey.50' } }} onClick={() => handleSummaryClick(type)}>
                        <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.75rem' }}>APPLICATION TYPE</Typography>
                                <Typography variant="subtitle1" fontWeight="bold">{type}</Typography>
                            </Box>
                            <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
                                <Typography variant="h6" fontWeight="bold" color="white">{count}</Typography>
                            </Avatar>
                        </CardContent>
                    </Card>
                </Grid>
            ))}
            {typeSummary.length === 0 && (
                <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                        <Typography color="text.secondary">No applications found in the selected range.</Typography>
                    </Paper>
                </Grid>
            )}
        </Grid>
      </Box>

      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom>Sanitary Permit Summary</Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={4}>
            <Card
              variant="outlined"
              sx={{ cursor: sanitarySummary.count > 0 ? 'pointer' : 'default', '&:hover': sanitarySummary.count > 0 ? { bgcolor: 'grey.50' } : {} }}
              onClick={() => sanitarySummary.count > 0 && handleCountClick('Sanitary Inspection Fee', sanitarySummary.permits)}
            >
              <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.75rem' }}>
                    SANITARY INSPECTION FEE
                  </Typography>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {sanitarySummary.count} applications
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    ₱{sanitarySummary.totalAmount.toLocaleString('en-PH', { minimumFractionDigits: 2 })}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      <Dialog open={showDetailedSummaryModal} onClose={() => setShowDetailedSummaryModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white' }}>
            {detailedSummary.type} Summary
        </DialogTitle>
        <DialogContent dividers>
            {detailedSummary.type === 'Motorcycle and Tricycle Permit' && (
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={4}>
                        <AccessibleSelect
                            label="Type of Permit"
                            value={summaryFilters.permitStatus}
                            onChange={(e) => setSummaryFilters({ ...summaryFilters, permitStatus: e.target.value })}
                            options={['All', 'New', 'Renewal']}
                            fullWidth
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <AccessibleSelect
                            label="Vehicle Type"
                            value={summaryFilters.vehicleType}
                            onChange={(e) => setSummaryFilters({ ...summaryFilters, vehicleType: e.target.value })}
                            options={['All', 'Motorcycle', 'Tricycle']}
                            fullWidth
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <AccessibleSelect
                            label="Service Type"
                            value={summaryFilters.serviceType}
                            onChange={(e) => setSummaryFilters({ ...summaryFilters, serviceType: e.target.value })}
                            options={['All', 'Private', 'For Hire']}
                            fullWidth
                            size="small"
                        />
                    </Grid>
                </Grid>
            )}
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Category</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Count</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {detailedSummaryData.map((row, idx) => (
                            <TableRow key={idx}>
                                <TableCell>{row.label}</TableCell>
                                <TableCell 
                                  align="right" 
                                  sx={{ fontWeight: 'bold', color: 'primary.main', cursor: 'pointer', textDecoration: 'underline' }}
                                  onClick={() => handleCountClick(row.label, row.permits)}
                                >
                                  {row.count}
                                </TableCell>
                            </TableRow>
                        ))}
                        {detailedSummaryData.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={2} align="center">No data available</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </DialogContent>
        <DialogActions>
            <Button onClick={() => handlePrint(
                `${detailedSummary.type} Summary`, 
                [{ header: 'Category', field: 'label' }, { header: 'Count', field: 'count' }], 
                detailedSummaryData
            )}>Print</Button>
            <Button onClick={() => setShowDetailedSummaryModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showDrillDownModal} onClose={() => setShowDrillDownModal(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white' }}>
            List: {drillDownTitle}
        </DialogTitle>
        <DialogContent dividers>
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Application No.</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Applicant Name</TableCell>
                            {detailedSummary.type === 'Business Permit' && (
                                <>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Business Name</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Nature of Business</TableCell>
                                </>
                            )}
                            {detailedSummary.type === 'Motorcycle and Tricycle Permit' && (
                                <>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Plate No.</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Brand/Model</TableCell>
                                </>
                            )}
                            {detailedSummary.type === 'Fishing Vessel and Gear Registration' && drillDownTitle !== 'Fishing Gears' && (
                                <>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Vessel Name</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                                </>
                            )}
                            {detailedSummary.type === 'Fishing Vessel and Gear Registration' && drillDownTitle === 'Fishing Gears' && (
                                <TableCell sx={{ fontWeight: 'bold' }}>Gear Type</TableCell>
                            )}
                            <TableCell sx={{ fontWeight: 'bold' }}>Issue Date</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {drillDownList.map((p, idx) => {
                            const displayName = getApplicantName(p);
                            return (
                                <TableRow key={`${p.id}-${idx}`}>
                                    <TableCell>{p.permitNo}</TableCell>
                                    <TableCell>{displayName}</TableCell>
                                    {detailedSummary.type === 'Business Permit' && (
                                        <>
                                            <TableCell>{p.businessName || '-'}</TableCell>
                                            <TableCell>{p.natureOfBusiness || '-'}</TableCell>
                                        </>
                                    )}
                                    {detailedSummary.type === 'Motorcycle and Tricycle Permit' && (
                                        <>
                                            <TableCell>{p.displayVehicle?.plateNumber || '-'}</TableCell>
                                            <TableCell>{p.displayVehicle?.brandModel || '-'}</TableCell>
                                        </>
                                    )}
                                    {detailedSummary.type === 'Fishing Vessel and Gear Registration' && drillDownTitle !== 'Fishing Gears' && (
                                        <>
                                            <TableCell>{p.displayFishingVessel?.name || '-'}</TableCell>
                                            <TableCell>{p.displayFishingVessel?.type || '-'}</TableCell>
                                        </>
                                    )}
                                    {detailedSummary.type === 'Fishing Vessel and Gear Registration' && drillDownTitle === 'Fishing Gears' && (
                                        <TableCell>{p.displayFishingGear?.type || p.displayFishingGear?.name || '-'}</TableCell>
                                    )}
                                    <TableCell>{p.issueDate}</TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={p.status} 
                                            size="small" 
                                            color={p.status === 'Paid' ? 'success' : 'warning'} 
                                            variant="outlined"
                                        />
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                        {drillDownList.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={detailedSummary.type === 'Business Permit' ? 6 : (detailedSummary.type === 'Motorcycle and Tricycle Permit' || (detailedSummary.type === 'Fishing Vessel and Gear Registration' && drillDownTitle !== 'Fishing Gears')) ? 6 : (detailedSummary.type === 'Fishing Vessel and Gear Registration' && drillDownTitle === 'Fishing Gears') ? 5 : 4} align="center">No records found</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </DialogContent>
        <DialogActions>
            <Button
              startIcon={<Download />}
              onClick={() => {
                const columns = [
                    { header: 'Application No.', field: 'permitNo' },
                    { header: 'Applicant Name', accessor: (row) => getApplicantName(row) }
                ];

                if (detailedSummary.type === 'Business Permit') {
                    columns.push(
                      { header: 'Business Name', accessor: (row) => row.businessName || '-' },
                      { header: 'Nature of Business', accessor: (row) => row.natureOfBusiness || '-' }
                    );
                }
                
                if (detailedSummary.type === 'Motorcycle and Tricycle Permit') {
                    columns.push(
                        { header: 'Plate No.', accessor: (row) => row.displayVehicle?.plateNumber || '-' },
                        { header: 'Brand/Model', accessor: (row) => row.displayVehicle?.brandModel || '-' }
                    );
                }

                if (detailedSummary.type === 'Fishing Vessel and Gear Registration') {
                    if (drillDownTitle === 'Fishing Gears') {
                        columns.push(
                            { header: 'Gear Type', accessor: (row) => row.displayFishingGear?.type || row.displayFishingGear?.name || '-' }
                        );
                    } else {
                        columns.push(
                            { header: 'Vessel Name', accessor: (row) => row.displayFishingVessel?.name || '-' },
                            { header: 'Type', accessor: (row) => row.displayFishingVessel?.type || '-' }
                        );
                    }
                }
                
                columns.push(
                    { header: 'Issue Date', field: 'issueDate' },
                    { header: 'Status', field: 'status' }
                );

                exportToExcel(`List - ${drillDownTitle}`, columns, drillDownList);
              }}
            >
              Export to Excel
            </Button>
            <Button onClick={() => {
                const columns = [
                    { header: 'Application No.', field: 'permitNo' },
                    { header: 'Applicant Name', accessor: (row) => getApplicantName(row) }
                ];

                if (detailedSummary.type === 'Business Permit') {
                    columns.push(
                      { header: 'Business Name', accessor: (row) => row.businessName || '-' },
                      { header: 'Nature of Business', accessor: (row) => row.natureOfBusiness || '-' }
                    );
                }
                
                if (detailedSummary.type === 'Motorcycle and Tricycle Permit') {
                    columns.push(
                        { header: 'Plate No.', accessor: (row) => row.displayVehicle?.plateNumber || '-' },
                        { header: 'Brand/Model', accessor: (row) => row.displayVehicle?.brandModel || '-' }
                    );
                }

                if (detailedSummary.type === 'Fishing Vessel and Gear Registration') {
                    if (drillDownTitle === 'Fishing Gears') {
                        columns.push(
                            { header: 'Gear Type', accessor: (row) => row.displayFishingGear?.type || row.displayFishingGear?.name || '-' }
                        );
                    } else {
                        columns.push(
                            { header: 'Vessel Name', accessor: (row) => row.displayFishingVessel?.name || '-' },
                            { header: 'Type', accessor: (row) => row.displayFishingVessel?.type || '-' }
                        );
                    }
                }
                
                columns.push(
                    { header: 'Issue Date', field: 'issueDate' },
                    { header: 'Status', field: 'status' }
                );

                handlePrint(`List: ${drillDownTitle}`, columns, drillDownList);
            }}>Print</Button>
            <Button onClick={() => setShowDrillDownModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const Settings = () => {
  const [appTypes, setAppTypes] = useState([]);
  const [natureList, setNatureList] = useState([]);
  const [typeInput, setTypeInput] = useState('');
  const [natureInput, setNatureInput] = useState('');
  const [editingType, setEditingType] = useState(null);
  const [editingNature, setEditingNature] = useState(null);
  const [chargesNatureId, setChargesNatureId] = useState('');
  const [charges, setCharges] = useState([]);
  const [chargesLoading, setChargesLoading] = useState(false);
  const [chargeForm, setChargeForm] = useState({ item: '', amount: '' });
  const [editingCharge, setEditingCharge] = useState(null);
  const [showDefaultModal, setShowDefaultModal] = useState(false);
  const [chargesSearch, setChargesSearch] = useState('');
  const [availableCharges, setAvailableCharges] = useState([]);
  const [availableChargesLoading, setAvailableChargesLoading] = useState(false);
  const [defaultError, setDefaultError] = useState('');
  const [addingId, setAddingId] = useState(null);
  const [showManageTypesModal, setShowManageTypesModal] = useState(false);
  const [showManageNatureModal, setShowManageNatureModal] = useState(false);
  const [natureError, setNatureError] = useState('');
  const [loading, setLoading] = useState(true);
  const [typeLoading, setTypeLoading] = useState(false);
  const [natureLoading, setNatureLoading] = useState(false);
  const [deletingNatureId, setDeletingNatureId] = useState(null);
  const [typeError, setTypeError] = useState('');
  const [deletingTypeId, setDeletingTypeId] = useState(null);
  const [deletingChargeId, setDeletingChargeId] = useState(null);
  
  // Fishing Gears Management
  const [fishingGearsRef, setFishingGearsRef] = useState([]);
  const [showManageGearsModal, setShowManageGearsModal] = useState(false);
  const [gearForm, setGearForm] = useState({ name: '', category: '' });
  const [editingGearRef, setEditingGearRef] = useState(null);
  const [gearError, setGearError] = useState('');

  // Fishing Gear Categories Management
  const [fishingGearCategories, setFishingGearCategories] = useState([]);
  const [showManageGearCategoriesModal, setShowManageGearCategoriesModal] = useState(false);
  const [categoryForm, setCategoryForm] = useState({ name: '' });
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryError, setCategoryError] = useState('');

  // Custom Charge Items Management
  const [customChargeItems, setCustomChargeItems] = useState([]);
  const [showManageCustomItemsModal, setShowManageCustomItemsModal] = useState(false);
  const [customItemForm, setCustomItemForm] = useState({ name: '' });
  const [customItemError, setCustomItemError] = useState('');

  // Custom Charge Percentages Management
  const [customChargePercentages, setCustomChargePercentages] = useState([]);
  const [showManageCustomPercentagesModal, setShowManageCustomPercentagesModal] = useState(false);
  const [customPercentageForm, setCustomPercentageForm] = useState({ name: '', value: '' });
  const [customPercentageError, setCustomPercentageError] = useState('');

  // Business Tax Settings Management
  const [businessTaxSettings, setBusinessTaxSettings] = useState([]);
  const [showManageBusinessTaxModal, setShowManageBusinessTaxModal] = useState(false);
  const [businessTaxForm, setBusinessTaxForm] = useState({ category: '', description: '', tax_schedule: [] });
  const [editingBusinessTax, setEditingBusinessTax] = useState(null);
  const [businessTaxError, setBusinessTaxError] = useState('');

  // Occupations Management
  const [occupations, setOccupations] = useState([]);
  const [showManageOccupationsModal, setShowManageOccupationsModal] = useState(false);
  const [occupationForm, setOccupationForm] = useState({ name: '' });
  const [editingOccupation, setEditingOccupation] = useState(null);
  const [occupationError, setOccupationError] = useState('');
  const [occupationLoading, setOccupationLoading] = useState(false);
  const [deletingOccupationId, setDeletingOccupationId] = useState(null);

  // Alert & Confirm states
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const showConfirm = (title, message, onConfirm) => {
    setConfirmDialog({ open: true, title, message, onConfirm });
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [
          { success: s1, data: t }, 
          { success: s2, data: n },
          { success: s3, data: c },
          { success: s4, data: g },
          { success: s5, data: ci },
          { success: s6, data: cp },
          { success: s7, data: bt },
          { success: s8, data: occ }
        ] = await Promise.all([
          bploService.getApplicationTypes(),
          bploService.getNatureOfBusiness(),
          bploService.getFishingGearCategories(),
          bploService.getFishingGears(),
          bploService.getCustomChargeItems(),
          bploService.getCustomChargePercentages(),
          bploService.getBusinessTaxSettings(),
          bploService.getOccupations()
        ]);
        if (s1) setAppTypes(t || []);
        if (s2) setNatureList(n || []);
        if (s3) setFishingGearCategories(c || []);
        if (s4) setFishingGearsRef(g || []);
        if (s5) setCustomChargeItems(ci || []);
        if (s6) setCustomChargePercentages(cp || []);
        if (s7) setBusinessTaxSettings(bt || []);
        if (s8) setOccupations(occ || []);
      } catch (error) {
        console.error("Failed to load settings data", error);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const refreshTypes = async () => {
    const { success, data } = await bploService.getApplicationTypes();
    if (success) setAppTypes(data || []);
  };
  const refreshOccupations = async () => {
    const { success, data } = await bploService.getOccupations();
    if (success) setOccupations(data || []);
  };
  const refreshNature = async () => {
    const { success, data } = await bploService.getNatureOfBusiness();
    if (success) setNatureList(data || []);
  };
  const refreshGearCategories = async () => {
    const { success, data } = await bploService.getFishingGearCategories();
    if (success) setFishingGearCategories(data || []);
  };
  const refreshGears = async () => {
    const { success, data } = await bploService.getFishingGears();
    if (success) setFishingGearsRef(data || []);
  };
  const refreshCustomItems = async () => {
    const { success, data } = await bploService.getCustomChargeItems();
    if (success) setCustomChargeItems(data || []);
  };
  const refreshCustomPercentages = async () => {
    const { success, data } = await bploService.getCustomChargePercentages();
    if (success) setCustomChargePercentages(data || []);
  };
  const refreshBusinessTax = async () => {
    const { success, data } = await bploService.getBusinessTaxSettings();
    if (success) setBusinessTaxSettings(data || []);
  };
  const refreshCharges = async (natureId) => {
    if (!natureId) { setCharges([]); return; }
    setChargesLoading(true);
    const { success, data } = await bploService.getDefaultChargesByNatureId(natureId);
    if (success) setCharges(data || []);
    setChargesLoading(false);
  };

  const saveType = async () => {
    const name = typeInput.trim();
    if (!name) return;
    setTypeLoading(true);
    setTypeError('');
    let res;
    if (editingType) {
      res = await bploService.updateApplicationType(editingType.id, { name });
      if (res && res.success) setEditingType(null);
    } else {
      res = await bploService.createApplicationType(name);
    }
    setTypeLoading(false);
    if (!res || !res.success) {
      console.error('Save failed:', res?.error);
      const errorMessage = res?.error && res.error.includes('duplicate key value')
        ? 'This application type already exists.'
        : (res?.error || 'Failed to save application type');
      setTypeError(errorMessage);
      showSnackbar(errorMessage, 'error');
      return;
    }
    setTypeInput('');
    await refreshTypes();
    showSnackbar('Application type saved successfully', 'success');
  };
  const removeType = async (id) => {
    if (!id) return;
    setTypeError('');

    // Check usage before confirm
    const typeObj = appTypes.find(t => t.id === id);
    if (typeObj) {
        const usage = await bploService.checkAppTypeUsage(id, typeObj.name);
        if (usage.inUse) {
            const msg = `Cannot delete: ${usage.reason}`;
            setTypeError(msg);
            showSnackbar(msg, 'error');
            return;
        }
    }
    
    showConfirm(
      'Delete Application Type',
      'Delete this application type?',
      async () => {
        setConfirmDialog(prev => ({ ...prev, open: false }));
        setDeletingTypeId(id);
        const res = await bploService.deleteApplicationType(id);
        setDeletingTypeId(null);
        if (!res || !res.success) {
          console.error('Delete failed:', res?.error);
          const errorMessage = res?.error && res.error.includes('violates foreign key constraint')
            ? 'Cannot delete: This application type is currently in use.'
            : (res?.error || 'Failed to delete application type');
          setTypeError(errorMessage);
          showSnackbar(errorMessage, 'error');
          return;
        }
        await refreshTypes();
        showSnackbar('Application type deleted successfully', 'success');
      }
    );
  };

  const saveOccupation = async () => {
    const name = occupationForm.name.trim();
    if (!name) return;
    setOccupationLoading(true);
    setOccupationError('');
    let res;
    if (editingOccupation) {
      res = await bploService.updateOccupation(editingOccupation.id, { name });
      if (res && res.success) setEditingOccupation(null);
    } else {
      res = await bploService.createOccupation(name);
    }
    setOccupationLoading(false);
    if (!res || !res.success) {
      console.error('Save failed:', res?.error);
      const errorMessage = res?.error && res.error.includes('duplicate key value')
        ? 'This occupation already exists.'
        : (res?.error || 'Failed to save occupation');
      setOccupationError(errorMessage);
      showSnackbar(errorMessage, 'error');
      return;
    }
    setOccupationForm({ name: '' });
    await refreshOccupations();
    showSnackbar('Occupation saved successfully', 'success');
  };

  const removeOccupation = async (id) => {
    if (!id) return;
    setOccupationError('');

    // Check usage before confirm
    const usage = await bploService.checkOccupationUsage(id);
    if (usage.inUse) {
        const msg = `Cannot delete: ${usage.reason}`;
        setOccupationError(msg);
        showSnackbar(msg, 'error');
        return;
    }
    
    showConfirm(
      'Delete Occupation',
      'Delete this occupation?',
      async () => {
        setConfirmDialog(prev => ({ ...prev, open: false }));
        setDeletingOccupationId(id);
        const res = await bploService.deleteOccupation(id);
        setDeletingOccupationId(null);
        if (!res || !res.success) {
          console.error('Delete failed:', res?.error);
          const errorMessage = res?.error || 'Failed to delete occupation';
          setOccupationError(errorMessage);
          showSnackbar(errorMessage, 'error');
          return;
        }
        await refreshOccupations();
        showSnackbar('Occupation deleted successfully', 'success');
      }
    );
  };

  const saveNature = async () => {
    const name = natureInput.trim();
    if (!name) return;
    setNatureError('');
    setNatureLoading(true);
    let res;
    if (editingNature) {
      res = await bploService.updateNatureOfBusiness(editingNature.id, { name });
      if (res && res.success) setEditingNature(null);
    } else {
      res = await bploService.createNatureOfBusiness(name);
    }
    setNatureLoading(false);
    if (!res || !res.success) {
      console.error('Save failed:', res?.error);
      const errorMessage = res?.error && res.error.includes('duplicate key value')
        ? 'This nature of business already exists.'
        : (res?.error || 'Failed to save nature of business');
      setNatureError(errorMessage);
      showSnackbar(errorMessage, 'error');
      return;
    }
    setNatureInput('');
    await refreshNature();
    showSnackbar('Nature of business saved successfully', 'success');
  };
  const removeNature = async (id) => {
    if (!id) return;
    setNatureError('');

    // Check usage before confirm
    const natureObj = natureList.find(n => n.id === id);
    if (natureObj) {
        const usage = await bploService.checkNatureUsage(id, natureObj.name);
        if (usage.inUse) {
            const msg = `Cannot delete: ${usage.reason}`;
            setNatureError(msg);
            showSnackbar(msg, 'error');
            return;
        }
    }
    
    showConfirm(
      'Delete Nature of Business',
      'Delete this nature of business?',
      async () => {
        setConfirmDialog(prev => ({ ...prev, open: false }));
        setDeletingNatureId(id);
        const res = await bploService.deleteNatureOfBusiness(id);
        setDeletingNatureId(null);
        if (!res || !res.success) {
          console.error('Delete failed:', res?.error);
          const errorMessage = res?.error && res.error.includes('violates foreign key constraint')
            ? 'Cannot delete: This nature of business is currently in use by one or more applications.'
            : res?.error && res.error.includes('Permission denied')
            ? 'Cannot delete: Your current role is not allowed to delete this table. Update Supabase RLS policy to permit delete.'
            : (res?.error || 'Failed to delete nature of business');
          setNatureError(errorMessage);
          showSnackbar(errorMessage, 'error');
          return;
        }
        await refreshNature();
        showSnackbar('Nature of business deleted successfully', 'success');
      }
    );
  };

  const onSelectNatureForCharges = async (id) => { setChargesNatureId(id); await refreshCharges(id); setChargeForm({ item: '', amount: '' }); setEditingCharge(null); };
  const openDefaultModal = async () => { 
    setShowDefaultModal(true); 
    setAvailableChargesLoading(true);
    const { success, data } = await bploService.getCharges(); 
    if (success) setAvailableCharges(data || []); 
    setAvailableChargesLoading(false);
  };
  const saveCharge = async () => {
    if (!chargesNatureId || !chargeForm.item || !chargeForm.amount) return;
    let res;
    if (editingCharge) {
      res = await bploService.updateDefaultCharge(editingCharge.id, { item: chargeForm.item, amount: parseFloat(chargeForm.amount) });
      setEditingCharge(null);
    } else {
      res = await bploService.createDefaultCharge(null, chargeForm.item, parseFloat(chargeForm.amount), chargesNatureId);
    }
    
    if (res && res.success) {
      setChargeForm({ item: '', amount: '' });
      await refreshCharges(chargesNatureId);
      showSnackbar('Charge saved successfully', 'success');
    } else {
      const err = res?.error || 'Failed to save charge';
      setDefaultError(err);
      showSnackbar(err, 'error');
    }
  };
  const removeCharge = async (id) => {
    if (!id) return;
    setDefaultError('');
    
    showConfirm(
      'Remove Charge',
      'Remove this default charge?',
      async () => {
        setConfirmDialog(prev => ({ ...prev, open: false }));
        setDeletingChargeId(id);
        const res = await bploService.deleteDefaultCharge(id);
        setDeletingChargeId(null);
        if (!res || !res.success) {
          const err = res?.error || 'Failed to remove charge';
          setDefaultError(err);
          showSnackbar(err, 'error');
          return;
        }
        await refreshCharges(chargesNatureId);
        showSnackbar('Charge removed successfully', 'success');
      }
    );
  };
  const addDefaultFromCharge = async (c) => {
    if (!chargesNatureId) return;
    setAddingId(c.id);
    const res = await bploService.createDefaultCharge(null, c.item, parseFloat(c.amount), chargesNatureId);
    if (res && res.success) {
      setDefaultError('');
      await refreshCharges(chargesNatureId);
      showSnackbar('Charge added successfully', 'success');
    } else {
      const err = res?.error || 'Failed to add charge';
      setDefaultError(err);
      showSnackbar(err, 'error');
    }
    setAddingId(null);
  };

  const saveCategory = async () => {
    const name = categoryForm.name.trim();
    if (!name) return;
    
    // Check duplicates locally (optional, but good for UX)
    // However, DB has unique constraint, so we can also just rely on error return
    const exists = fishingGearCategories.some(c => c.name.toLowerCase() === name.toLowerCase() && (!editingCategory || c.id !== editingCategory.id));
    if (exists) {
      setCategoryError('This category already exists.');
      return;
    }

    let res;
    if (editingCategory) {
      res = await bploService.updateFishingGearCategory(editingCategory.id, name);
    } else {
      res = await bploService.createFishingGearCategory(name);
    }

    if (res && res.success) {
      setEditingCategory(null);
      setCategoryForm({ name: '' });
      setCategoryError('');
      await refreshGearCategories();
      // If renamed, refresh gears too to update category names
      if (editingCategory) await refreshGears(); 
      showSnackbar(editingCategory ? 'Category updated successfully' : 'Category added successfully', 'success');
    } else {
       const err = res?.error || 'Failed to save category';
       setCategoryError(err);
       showSnackbar(err, 'error');
    }
  };

  const removeCategory = (id) => {
    const cat = fishingGearCategories.find(c => c.id === id);
    if (!cat) return;
    
    // Check if used
    // fishingGearsRef has category name in .category property
    const isUsed = fishingGearsRef.some(g => g.category === cat.name);
    if (isUsed) {
        showConfirm(
            'Cannot Delete Category',
            `The category "${cat.name}" is currently used by one or more fishing gears.\nPlease remove or reassign the gears before deleting this category.`,
            () => setConfirmDialog(prev => ({ ...prev, open: false }))
        );
        return;
    }

    showConfirm(
      'Delete Category',
      'Are you sure you want to delete this category?',
      async () => {
        setConfirmDialog(prev => ({ ...prev, open: false }));
        const res = await bploService.deleteFishingGearCategory(id);
        if (res && res.success) {
          await refreshGearCategories();
          showSnackbar('Category deleted successfully', 'success');
        } else {
          showSnackbar(res?.error || 'Failed to delete category', 'error');
        }
      }
    );
  };

  const saveGearRef = async () => {
    const name = gearForm.name.trim();
    const categoryId = gearForm.category; // This is now ID
    if (!name || !categoryId) return;
    
    // Local duplicate check
    const exists = fishingGearsRef.some(g => g.name.toLowerCase() === name.toLowerCase() && (!editingGearRef || g.id !== editingGearRef.id));
    if (exists) {
        setGearError('This gear name already exists.');
        return;
    }

    let res;
    if (editingGearRef) {
      res = await bploService.updateFishingGear(editingGearRef.id, name, categoryId);
    } else {
      res = await bploService.createFishingGear(name, categoryId);
    }

    if (res && res.success) {
      setEditingGearRef(null);
      setGearForm({ name: '', category: '' });
      setGearError('');
      await refreshGears();
      showSnackbar(editingGearRef ? 'Fishing gear updated successfully' : 'Fishing gear added successfully', 'success');
    } else {
      setGearError(res?.error || 'Failed to save fishing gear');
    }
  };

  const removeGearRef = (id) => {
    showConfirm(
      'Delete Fishing Gear',
      'Are you sure you want to delete this fishing gear reference?',
      async () => {
        setConfirmDialog(prev => ({ ...prev, open: false }));
        const res = await bploService.deleteFishingGear(id);
        if (res && res.success) {
          await refreshGears();
          showSnackbar('Fishing gear deleted successfully', 'success');
        } else {
          showSnackbar(res?.error || 'Failed to delete fishing gear', 'error');
        }
      }
    );
  };

  const saveCustomItem = async () => {
    const name = customItemForm.name.trim();
    if (!name) return;
    
    // Check duplicates
    const exists = customChargeItems.some(c => c.name.toLowerCase() === name.toLowerCase());
    if (exists) {
      setCustomItemError('This item already exists.');
      return;
    }

    const res = await bploService.createCustomChargeItem(name);
    if (res && res.success) {
      setCustomItemForm({ name: '' });
      setCustomItemError('');
      await refreshCustomItems();
      showSnackbar('Item added successfully', 'success');
    } else {
      setCustomItemError(res?.error || 'Failed to add item');
      showSnackbar(res?.error || 'Failed to add item', 'error');
    }
  };

  const removeCustomItem = (id) => {
    showConfirm(
      'Delete Item',
      'Are you sure you want to delete this item?',
      async () => {
        setConfirmDialog(prev => ({ ...prev, open: false }));
        const res = await bploService.deleteCustomChargeItem(id);
        if (res && res.success) {
          await refreshCustomItems();
          showSnackbar('Item deleted successfully', 'success');
        } else {
          showSnackbar(res?.error || 'Failed to delete item', 'error');
        }
      }
    );
  };

  const saveCustomPercentage = async () => {
    const value = parseFloat(customPercentageForm.value);
    const name = customPercentageForm.name ? customPercentageForm.name.trim() : '';
    
    if (isNaN(value)) return;
    if (!name) {
        setCustomPercentageError('Name is required.');
        return;
    }
    
    // Check duplicates
    const exists = customChargePercentages.some(c => c.name && c.name.toLowerCase() === name.toLowerCase());
    if (exists) {
      setCustomPercentageError('This name already exists.');
      return;
    }

    const res = await bploService.createCustomChargePercentage(name, value);
    if (res && res.success) {
      setCustomPercentageForm({ name: '', value: '' });
      setCustomPercentageError('');
      await refreshCustomPercentages();
      showSnackbar('Percentage added successfully', 'success');
    } else {
      setCustomPercentageError(res?.error || 'Failed to add percentage');
      showSnackbar(res?.error || 'Failed to add percentage', 'error');
    }
  };

  const removeCustomPercentage = (id) => {
    showConfirm(
      'Delete Percentage',
      'Are you sure you want to delete this percentage?',
      async () => {
        setConfirmDialog(prev => ({ ...prev, open: false }));
        const res = await bploService.deleteCustomChargePercentage(id);
        if (res && res.success) {
          await refreshCustomPercentages();
          showSnackbar('Percentage deleted successfully', 'success');
        } else {
          showSnackbar(res?.error || 'Failed to delete percentage', 'error');
        }
      }
    );
  };

  const saveBusinessTax = async () => {
    const category = businessTaxForm.category.trim();
    if (!category) {
        setBusinessTaxError('Category is required.');
        return;
    }
    
    // Validate tax schedule
    const schedule = businessTaxForm.tax_schedule || [];
    // Ensure all rows have valid data if needed, but we can allow empty schedule
    
    const payload = {
        category,
        description: businessTaxForm.description || '',
        tax_schedule: schedule
    };

    let res;
    if (editingBusinessTax) {
        res = await bploService.updateBusinessTaxSetting(editingBusinessTax.id, payload);
    } else {
        res = await bploService.createBusinessTaxSetting(payload);
    }

    if (res && res.success) {
        setBusinessTaxForm({ category: '', description: '', tax_schedule: [] });
        setEditingBusinessTax(null);
        setBusinessTaxError('');
        await refreshBusinessTax();
        showSnackbar(editingBusinessTax ? 'Business Tax updated successfully' : 'Business Tax added successfully', 'success');
        setShowManageBusinessTaxModal(false);
    } else {
        setBusinessTaxError(res?.error || 'Failed to save business tax');
        showSnackbar(res?.error || 'Failed to save business tax', 'error');
    }
  };

  const removeBusinessTax = (id) => {
    showConfirm(
        'Delete Business Tax',
        'Are you sure you want to delete this business tax setting?',
        async () => {
            setConfirmDialog(prev => ({ ...prev, open: false }));
            const res = await bploService.deleteBusinessTaxSetting(id);
            if (res && res.success) {
                await refreshBusinessTax();
                showSnackbar('Business Tax deleted successfully', 'success');
            } else {
                showSnackbar(res?.error || 'Failed to delete business tax', 'error');
            }
        }
    );
  };

  const addTaxScheduleRow = () => {
    setBusinessTaxForm(prev => ({
        ...prev,
        tax_schedule: [...(prev.tax_schedule || []), { gross_sale: '', gross_tax: '' }]
    }));
  };

  const removeTaxScheduleRow = (index) => {
    setBusinessTaxForm(prev => ({
        ...prev,
        tax_schedule: (prev.tax_schedule || []).filter((_, i) => i !== index)
    }));
  };

  const updateTaxScheduleRow = (index, field, value) => {
    setBusinessTaxForm(prev => ({
        ...prev,
        tax_schedule: (prev.tax_schedule || []).map((row, i) => i === index ? { ...row, [field]: value } : row)
    }));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" gutterBottom>Settings</Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Occupations</Typography>
                <Button variant="contained" onClick={async ()=>{ await refreshOccupations(); setOccupationForm({ name: '' }); setEditingOccupation(null); setShowManageOccupationsModal(true); }}>Manage</Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Application Types</Typography>
                <Button variant="contained" onClick={async ()=>{ await refreshTypes(); setTypeInput(''); setEditingType(null); setShowManageTypesModal(true); }}>Manage</Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Custom Items</Typography>
                <Button variant="contained" onClick={() => { setCustomItemForm({ name: '' }); setCustomItemError(''); setShowManageCustomItemsModal(true); }}>Manage</Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Custom Percentages</Typography>
                <Button variant="contained" onClick={() => { setCustomPercentageForm({ name: '', value: '' }); setCustomPercentageError(''); setShowManageCustomPercentagesModal(true); }}>Manage</Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Nature of Business</Typography>
                <Button variant="contained" onClick={async ()=>{ await refreshNature(); setNatureInput(''); setEditingNature(null); setShowManageNatureModal(true); }}>Manage</Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Business Tax</Typography>
                <Button variant="contained" onClick={async ()=>{ await refreshBusinessTax(); setBusinessTaxForm({ category: '', description: '', tax_schedule: [] }); setEditingBusinessTax(null); setShowManageBusinessTaxModal(true); }}>Manage</Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Fishing Gears</Typography>
                <Button variant="contained" onClick={() => { setGearForm({ name: '', category: fishingGearCategories.length > 0 ? fishingGearCategories[0].id : '' }); setEditingGearRef(null); setShowManageGearsModal(true); }}>Manage</Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Charges related to {natureList.find(n => n.id === chargesNatureId)?.name || 'Nature of Business'}
              </Typography>
              <AccessibleSelect
                label="Nature of Business"
                value={chargesNatureId}
                onChange={(e)=>onSelectNatureForCharges(e.target.value)}
                options={(natureList||[]).map(n => ({ value: n.id, label: n.name }))}
                fullWidth
                sx={{ mb: 2 }}
              />
              {chargesNatureId ? (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">Manage charges for the selected nature</Typography>
                  <Button variant="contained" onClick={openDefaultModal}>Manage Charges</Button>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">Select a nature of business to manage its charges.</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <Dialog open={showDefaultModal} onClose={() => setShowDefaultModal(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ bgcolor: 'var(--bs-primary)', color: 'white' }}>
            Manage Charges related to {natureList.find(n => n.id === chargesNatureId)?.name || 'Nature of Business'}
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          {defaultError && (
            <Alert severity="error" sx={{ mb: 2 }}>{defaultError}</Alert>
          )}
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>SELECT NATURE OF BUSINESS</Typography>
             <AccessibleSelect
                label="Nature of Business"
                value={chargesNatureId}
                onChange={(e)=>onSelectNatureForCharges(e.target.value)}
                options={(natureList||[]).map(n => ({ value: n.id, label: n.name }))}
                fullWidth
             />
          </Box>

          {chargesNatureId ? (
            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
                             <Typography variant="subtitle1" fontWeight="bold">Available Charges</Typography>
                             <TextField
                                fullWidth
                                size="small"
                                placeholder="Search charges..."
                                value={chargesSearch}
                                onChange={(e) => setChargesSearch(e.target.value)}
                                InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
                                sx={{ mt: 1, bgcolor: 'white' }}
                              />
                        </Box>
                        <Box sx={{ flexGrow: 1, overflow: 'auto', maxHeight: 400, minHeight: 300 }}>
                             <Table size="small" stickyHeader>
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Item</TableCell>
                                    <TableCell align="right">Amount</TableCell>
                                    <TableCell align="center" width={80}>Action</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {availableChargesLoading ? (
                                    <TableRow><TableCell colSpan={3} align="center"><CircularProgress size={24} sx={{ my: 2 }} /></TableCell></TableRow>
                                  ) : availableCharges.filter(c => !chargesSearch.trim() || String(c.item||'').toLowerCase().includes(chargesSearch.trim().toLowerCase())).map(c => (
                                    <TableRow key={c.id} hover>
                                      <TableCell>{c.item}</TableCell>
                                      <TableCell align="right">₱{parseFloat(c.amount).toLocaleString('en-PH', { minimumFractionDigits: 2 })}</TableCell>
                                      <TableCell align="center">
                                        <Button size="small" variant="contained" color="primary" onClick={() => addDefaultFromCharge(c)} disabled={addingId===c.id} sx={{ minWidth: 60 }}>
                                            Add
                                        </Button>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                  {availableCharges.length === 0 && !availableChargesLoading && (
                                     <TableRow><TableCell colSpan={3} align="center" sx={{ py: 4, color: 'text.secondary' }}>No charges available.</TableCell></TableRow>
                                  )}
                                </TableBody>
                              </Table>
                        </Box>
                    </Paper>
                </Grid>
                
                <Grid item xs={12} md={6}>
                     <Paper variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="subtitle1" fontWeight="bold">Current Charges for {natureList.find(n => n.id === chargesNatureId)?.name}</Typography>
                             <Chip label={`${(charges||[]).length} items`} size="small" color="primary" />
                        </Box>
                        <Box sx={{ flexGrow: 1, overflow: 'auto', maxHeight: 400, minHeight: 300 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Item</TableCell>
                                    <TableCell align="right">Amount</TableCell>
                                    <TableCell align="center" width={80}>Action</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {chargesLoading ? (
                                    <TableRow><TableCell colSpan={3} align="center"><CircularProgress size={24} sx={{ my: 2 }} /></TableCell></TableRow>
                                  ) : (charges||[]).length === 0 ? (
                                    <TableRow><TableCell colSpan={3} align="center" sx={{ py: 4, color: 'text.secondary' }}>No charges added yet.</TableCell></TableRow>
                                  ) : (charges||[]).map(c => (
                                    <TableRow key={c.id} hover>
                                      <TableCell>{c.item}</TableCell>
                                      <TableCell align="right">₱{parseFloat(c.amount).toLocaleString('en-PH', { minimumFractionDigits: 2 })}</TableCell>
                                      <TableCell align="center">
                                        <IconButton size="small" color="error" onClick={() => removeCharge(c.id)} disabled={deletingChargeId===c.id}>
                                            {deletingChargeId===c.id ? <CircularProgress size={16} /> : <Delete fontSize="small" />}
                                        </IconButton>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                        </Box>
                        <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'grey.50', textAlign: 'right' }}>
                             <Typography variant="subtitle1" fontWeight="bold">
                                Total: ₱{(charges||[]).reduce((sum, c) => sum + parseFloat(c.amount||0), 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}
                             </Typography>
                        </Box>
                     </Paper>
                </Grid>
            </Grid>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyItems: 'center', py: 8, opacity: 0.5 }}>
                <SettingsIcon sx={{ fontSize: 64, mb: 2, color: 'text.disabled' }} />
                <Typography variant="h6" color="text.secondary">No Nature of Business Selected</Typography>
                <Typography variant="body2">Please select a nature of business to manage charges.</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setShowDefaultModal(false)} variant="contained">Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={showManageTypesModal} onClose={() => { setShowManageTypesModal(false); setTypeError(''); }} maxWidth="sm" fullWidth>
        <DialogTitle>Manage Application Types</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField fullWidth size="small" placeholder="Type name" value={typeInput} onChange={(e)=>setTypeInput(e.target.value)} />
            <Button variant="contained" onClick={saveType} disabled={typeLoading || !typeInput.trim()}>{typeLoading ? <CircularProgress size={24} /> : (editingType ? 'Save' : 'Add')}</Button>
          </Box>
          {typeError && (
            <Alert severity="error" sx={{ mb: 2 }}>{typeError}</Alert>
          )}
          <Table size="small">
            <TableBody>
              {(appTypes||[]).map(t => (
                <TableRow key={t.id}>
                  <TableCell>{t.name}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => { setTypeError(''); setEditingType(t); setTypeInput(t.name); }}><Edit fontSize="small" /></IconButton>
                    <IconButton size="small" color="error" onClick={() => removeType(t.id)} disabled={deletingTypeId===t.id}><Delete fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowManageTypesModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={showManageOccupationsModal} onClose={() => { setShowManageOccupationsModal(false); setOccupationError(''); }} maxWidth="sm" fullWidth>
        <DialogTitle>Manage Occupations</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField fullWidth size="small" placeholder="Occupation name" value={occupationForm.name} onChange={(e)=>setOccupationForm({...occupationForm, name: e.target.value})} />
            <Button variant="contained" onClick={saveOccupation} disabled={occupationLoading || !occupationForm.name.trim()}>{occupationLoading ? <CircularProgress size={24} /> : (editingOccupation ? 'Save' : 'Add')}</Button>
          </Box>
          {occupationError && (
            <Alert severity="error" sx={{ mb: 2 }}>{occupationError}</Alert>
          )}
          <Table size="small">
            <TableBody>
              {(occupations||[]).map(o => (
                <TableRow key={o.id}>
                  <TableCell>{o.name}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => { setOccupationError(''); setEditingOccupation(o); setOccupationForm({ name: o.name }); }}><Edit fontSize="small" /></IconButton>
                    <IconButton size="small" color="error" onClick={() => removeOccupation(o.id)} disabled={deletingOccupationId===o.id}><Delete fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowManageOccupationsModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={showManageNatureModal} onClose={() => { setShowManageNatureModal(false); setNatureError(''); }} maxWidth="sm" fullWidth>
        <DialogTitle>Manage Nature of Business</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField fullWidth size="small" placeholder="Nature name" value={natureInput} onChange={(e)=>setNatureInput(e.target.value)} />
            <Button variant="contained" onClick={saveNature} disabled={natureLoading || !natureInput.trim()}>{natureLoading ? <CircularProgress size={24} /> : (editingNature ? 'Save' : 'Add')}</Button>
          </Box>
          {natureError && (
            <Alert severity="error" sx={{ mb: 2 }}>{natureError}</Alert>
          )}
          <Table size="small">
            <TableBody>
              {(natureList||[]).map(n => (
                <TableRow key={n.id}>
                  <TableCell>{n.name}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => { setNatureError(''); setEditingNature(n); setNatureInput(n.name); }}><Edit fontSize="small" /></IconButton>
                    <IconButton size="small" color="error" onClick={() => removeNature(n.id)} disabled={deletingNatureId===n.id}><Delete fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowManageNatureModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showManageGearsModal} onClose={() => { setShowManageGearsModal(false); setGearError(''); }} maxWidth="md" fullWidth>
        <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                Manage Fishing Gears
                <Button variant="outlined" size="small" onClick={() => setShowManageGearCategoriesModal(true)}>Manage Categories</Button>
            </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField fullWidth size="small" placeholder="Gear Name" value={gearForm.name} onChange={(e)=>setGearForm({...gearForm, name: e.target.value})} />
                <AccessibleSelect
                    label="Category"
                    value={gearForm.category}
                    onChange={(e)=>setGearForm({...gearForm, category: e.target.value})}
                    options={fishingGearCategories.map(c => ({ value: c.id, label: c.name }))}
                    fullWidth
                />
                <Button variant="contained" onClick={saveGearRef} disabled={!gearForm.name.trim() || !gearForm.category}>{editingGearRef ? 'Save' : 'Add'}</Button>
            </Box>
            {fishingGearCategories.length === 0 && (
                <Alert severity="warning">You need to create categories before adding gears.</Alert>
            )}
          </Box>
          {gearError && (
            <Alert severity="error" sx={{ mb: 2 }}>{gearError}</Alert>
          )}
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(fishingGearsRef||[]).map(g => (
                  <TableRow key={g.id}>
                    <TableCell>{g.name}</TableCell>
                    <TableCell>{g.category}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => { setGearError(''); setEditingGearRef(g); setGearForm({ name: g.name, category: g.category_id }); }}><Edit fontSize="small" /></IconButton>
                      <IconButton size="small" color="error" onClick={() => removeGearRef(g.id)}><Delete fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowManageGearsModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showManageGearCategoriesModal} onClose={() => { setShowManageGearCategoriesModal(false); setCategoryError(''); }} maxWidth="sm" fullWidth>
        <DialogTitle>Manage Fishing Gear Categories</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField fullWidth size="small" placeholder="Category name" value={categoryForm.name} onChange={(e)=>setCategoryForm({...categoryForm, name: e.target.value})} />
            <Button variant="contained" onClick={saveCategory} disabled={!categoryForm.name.trim()}>{editingCategory ? 'Save' : 'Add'}</Button>
          </Box>
          {categoryError && (
            <Alert severity="error" sx={{ mb: 2 }}>{categoryError}</Alert>
          )}
          <Table size="small">
            <TableBody>
              {(fishingGearCategories||[]).map(c => (
                <TableRow key={c.id}>
                  <TableCell>{c.name}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => { setCategoryError(''); setEditingCategory(c); setCategoryForm({ name: c.name }); }}><Edit fontSize="small" /></IconButton>
                    <IconButton size="small" color="error" onClick={() => removeCategory(c.id)}><Delete fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowManageGearCategoriesModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showManageCustomItemsModal} onClose={() => { setShowManageCustomItemsModal(false); setCustomItemError(''); }} maxWidth="sm" fullWidth>
        <DialogTitle>Manage Custom Items</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField fullWidth size="small" placeholder="Item Name" value={customItemForm.name} onChange={(e)=>setCustomItemForm({...customItemForm, name: e.target.value})} />
            <Button variant="contained" onClick={saveCustomItem} disabled={!customItemForm.name.trim()}>Add</Button>
          </Box>
          {customItemError && (
            <Alert severity="error" sx={{ mb: 2 }}>{customItemError}</Alert>
          )}
          <Table size="small">
            <TableBody>
              {(customChargeItems||[]).map(c => (
                <TableRow key={c.id}>
                  <TableCell>{c.name}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" color="error" onClick={() => removeCustomItem(c.id)}><Delete fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowManageCustomItemsModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showManageCustomPercentagesModal} onClose={() => { setShowManageCustomPercentagesModal(false); setCustomPercentageError(''); }} maxWidth="sm" fullWidth>
        <DialogTitle>Manage Custom Percentages</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField 
                fullWidth 
                size="small" 
                placeholder="Name (e.g. Surcharge)" 
                value={customPercentageForm.name} 
                onChange={(e)=>setCustomPercentageForm({...customPercentageForm, name: e.target.value})} 
            />
            <TextField 
              fullWidth 
              size="small" 
              placeholder="Value" 
              type="number"
              value={customPercentageForm.value} 
              onChange={(e)=>setCustomPercentageForm({...customPercentageForm, value: e.target.value})} 
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
            />
            <Button variant="contained" onClick={saveCustomPercentage} disabled={!customPercentageForm.value || !customPercentageForm.name}>Add</Button>
          </Box>
          {customPercentageError && (
            <Alert severity="error" sx={{ mb: 2 }}>{customPercentageError}</Alert>
          )}
          <Table size="small">
            <TableBody>
              {(customChargePercentages||[]).map(c => (
                <TableRow key={c.id}>
                  <TableCell>{c.name}</TableCell>
                  <TableCell align="right">{c.value}%</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" color="error" onClick={() => removeCustomPercentage(c.id)}><Delete fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowManageCustomPercentagesModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showManageBusinessTaxModal} onClose={() => { setShowManageBusinessTaxModal(false); setBusinessTaxError(''); }} maxWidth="md" fullWidth>
        <DialogTitle>Manage Business Tax Tables</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2, p: 2, border: '1px solid #eee', borderRadius: 1 }}>
            <Typography variant="subtitle2" color="primary">Add/Edit Business Tax</Typography>
            <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    <TextField 
                        fullWidth size="small" 
                        label="Category" 
                        placeholder="e.g. Manufacturers"
                        value={businessTaxForm.category} 
                        onChange={(e)=>setBusinessTaxForm({...businessTaxForm, category: e.target.value})} 
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField 
                        fullWidth size="small" 
                        label="Description" 
                        placeholder="Optional description"
                        value={businessTaxForm.description} 
                        onChange={(e)=>setBusinessTaxForm({...businessTaxForm, description: e.target.value})} 
                    />
                </Grid>
            </Grid>
            
            <Box sx={{ mt: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="caption" fontWeight="bold">GROSS TAX SCHEDULE</Typography>
                    <Button size="small" variant="outlined" startIcon={<Add />} onClick={addTaxScheduleRow}>Add Row</Button>
                </Box>
                {(!businessTaxForm.tax_schedule || businessTaxForm.tax_schedule.length === 0) && (
                    <Typography variant="caption" color="text.secondary" fontStyle="italic">No tax schedule defined. Click "Add Row" to add tax brackets.</Typography>
                )}
                {(businessTaxForm.tax_schedule || []).map((row, idx) => (
                    <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                        <TextField 
                            size="small" label="Gross Sale" 
                            value={row.gross_sale} 
                            onChange={(e)=>updateTaxScheduleRow(idx, 'gross_sale', e.target.value)}
                            sx={{ width: 200 }}
                        />
                        <TextField 
                            size="small" label="Gross Tax" type="number" 
                            value={row.gross_tax} 
                            onChange={(e)=>updateTaxScheduleRow(idx, 'gross_tax', e.target.value)}
                            sx={{ width: 150 }}
                        />
                         <IconButton size="small" color="error" onClick={() => removeTaxScheduleRow(idx)}><Delete fontSize="small" /></IconButton>
                    </Box>
                ))}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <Button variant="contained" onClick={saveBusinessTax} disabled={!businessTaxForm.category.trim()}>{editingBusinessTax ? 'Update Business Tax' : 'Add Business Tax'}</Button>
            </Box>
          </Box>

          {businessTaxError && (
            <Alert severity="error" sx={{ mb: 2 }}>{businessTaxError}</Alert>
          )}

          <Table size="small">
            <TableHead>
                <TableRow>
                    <TableCell>Category</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Tax Brackets</TableCell>
                    <TableCell align="right">Action</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
              {(businessTaxSettings||[]).map(t => (
                <TableRow key={t.id}>
                  <TableCell>{t.category}</TableCell>
                  <TableCell>{t.description}</TableCell>
                  <TableCell>{(t.tax_schedule||[]).length} brackets</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => { setBusinessTaxError(''); setEditingBusinessTax(t); setBusinessTaxForm({ category: t.category, description: t.description, tax_schedule: t.tax_schedule || [] }); }}><Edit fontSize="small" /></IconButton>
                    <IconButton size="small" color="error" onClick={() => removeBusinessTax(t.id)}><Delete fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {(businessTaxSettings||[]).length === 0 && (
                  <TableRow><TableCell colSpan={4} align="center" sx={{ color: 'text.secondary', py: 3 }}>No business tax settings found.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowManageBusinessTaxModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}>
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ whiteSpace: 'pre-line' }}>{confirmDialog.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}>Cancel</Button>
          <Button onClick={confirmDialog.onConfirm} variant="contained" sx={{ bgcolor: 'var(--bs-primary)', color: '#fff', '&:hover': { bgcolor: 'var(--bs-primary)' } }}>Confirm</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BPLODashboard;
